import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { assetFoldersCopy } from '../features/assets/asset-folders-copy';
import { assetUploadCopy } from '../features/assets/asset-upload-copy';
import { aiProviderCopy } from '../features/manage/ai-provider-copy';
import { companyOverviewCopy } from '../features/manage/company-overview-copy';
import { copyReviewCopy } from '../features/presentations/copy-proposal-review-copy';
import { shareLinkProposalReviewCopy } from '../features/sharing/share-link-proposal-review-copy';
import { copyToProjectCopy } from '../features/presentations/copy-to-project-copy';
import { duplicateCopy } from '../features/presentations/duplicate-copy';
import { presentationsGalleryCopy } from '../features/presentations/presentations-gallery-copy';
import { guidedCreateCopy } from '../features/presentations/guided-create/guided-create-copy';
import { templateForkRejectionCopy } from '../features/templates/template-fork-rejection-copy';
import { templateUseCopy } from '../features/templates/template-use-copy';
import { bulkReviewCopy } from '../features/shared/bulk-review-copy';
import { projectSettingsCopy } from '../features/projects/project-settings-copy';
import { auditLogCopy } from '../features/manage/audit-log-copy';
import { narrationFeedbackCopy } from '../features/presentations/narration-feedback-copy';
import { viewerPlaybackCopy } from '../features/viewer/viewer-playback-copy';
import { styleGalleryCopy } from '../features/styles/style-gallery-copy';

const LOCALE_STORAGE_KEY = 'slider:locale:v1';

function getInitialLanguage(): 'en' | 'fa' {
  const stored = globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY);
  if (stored === 'fa' || stored === 'en') return stored;
  return globalThis.navigator?.language?.startsWith('fa') ? 'fa' : 'en';
}

const resources = {
  en: {
    translation: {
      brand: { name: 'Slider', tagline: 'Present ideas beautifully.' },
      styleGallery: styleGalleryCopy.en,
      bulkReview: bulkReviewCopy.en,
      common: {
        cancel: 'Cancel',
        close: 'Close',
        openMenu: 'Open navigation menu',
        theme: 'Appearance',
      },
      marketing: {
        nav: {
          primary: 'Primary navigation',
          features: 'Features',
          templates: 'Templates',
          security: 'Security',
          about: 'About',
        },
        requestAccess: 'Request Early Access',
        logIn: 'Log in',
        signedInAs: 'Signed in:',
        workspace: 'Workspace',
        language: 'Language',
        languageAction: 'Switch language',
        appearanceLabel: 'Appearance',
        appearanceAction: 'Appearance: {{mode}}. Activate to change.',
        appearance: { system: 'System', light: 'Light', dark: 'Dark' },
        footer: {
          product: 'Product',
          company: 'Company',
          legal: 'Legal',
          privacy: 'Privacy',
          terms: 'Terms',
          copyright: '© {{year}} Slider. All rights reserved.',
        },
        cta: {
          eyebrow: 'Ready when you are',
          title: 'Build your next presentation as a web experience.',
          description:
            'Join Early Access and help shape a more structured way to create, share and learn from presentations.',
        },
        home: {
          metaTitle: 'Interactive web-native presentations',
          metaDescription:
            'Create responsive, interactive presentations with professional templates, secure sharing, analytics and AI-assisted workflows.',
          eyebrow: 'Web-native presentations',
          title: 'Create interactive presentations that feel like the web.',
          description:
            'Build from professional templates, create with AI assistance, add meaningful interaction, share securely and understand what your audience engaged with.',
          seeHow: 'See how it works',
          previewLabel: 'Interactive Slider presentation preview',
          thumbnails: 'Open slide thumbnails',
          mockEyebrow: 'PRODUCT LAUNCH',
          mockTitle: 'Move from signal to story.',
          mockDescription: 'A responsive narrative for every screen.',
          capabilityLabel: 'Product capabilities',
          capability1: 'Responsive by design',
          capability2: 'Interactive',
          capability3: 'Measurable',
          capability4: 'AI-assisted',
          featuresEyebrow: 'One connected workflow',
          featuresTitle: 'From first idea to audience insight.',
          featuresDescription:
            'Slider keeps structure, design quality, publishing and measurement in one focused product.',
          feature1Title: 'Designer-built templates',
          feature1Description:
            'Start with responsive structures created for real communication goals.',
          feature2Title: 'Interactive web experiences',
          feature2Description:
            'Turn passive slides into focused, accessible actions and exploration.',
          feature3Title: 'Responsive everywhere',
          feature3Description:
            'Presentations adapt thoughtfully across desktop, tablet and mobile.',
          feature4Title: 'Narrated presentations',
          feature4Description:
            'Pair clear scripts with accessible playback and transcript-aware experiences.',
          feature5Title: 'Viewer analytics',
          feature5Description:
            'Understand completion, active time, drop-off and interaction patterns.',
          feature6Title: 'AI that uses your content',
          feature6Description:
            'Generate structured drafts from approved project knowledge, then review every change.',
          templatesEyebrow: 'Template system',
          templatesTitle: 'Design quality without rebuilding the system.',
          exploreTemplates: 'Explore templates',
          template1Art: 'Launch',
          template1Title: 'Product launch',
          template1Type: 'Narrative · Product',
          template2Art: 'Report',
          template2Title: 'Performance report',
          template2Type: 'Analytics · Leadership',
          template3Art: 'Story',
          template3Title: 'Customer story',
          template3Type: 'Case study · Sales',
          template4Art: 'Brief',
          template4Title: 'Creative brief',
          template4Type: 'Strategy · Design',
          responsiveBadge: 'Responsive',
          interactiveBadge: 'Interactive',
          viewerEyebrow: 'A viewer built for the web',
          viewerTitle: 'Share one link, not a stack of files.',
          viewerDescription:
            'Your audience gets a polished presentation experience with purposeful controls and no management UI.',
          viewerPoint1: 'Keyboard, touch and mobile-friendly navigation',
          viewerPoint2: 'Optional narration and accessible playback',
          viewerPoint3: 'The latest successful release stays live',
          analyticsEyebrow: 'Actionable analytics',
          analyticsTitle: 'Learn where attention turns into action.',
          analyticsDescription:
            'Use realistic audience signals to refine the story instead of guessing from downloads.',
          analytics: {
            views: 'Unique views',
            completion: 'Completion',
            activeTime: 'Average active time',
            chartLabel: 'Sample slide completion chart',
          },
          aiEyebrow: 'Responsible AI workflow',
          aiTitle: 'Assistance that stays reviewable.',
          aiStep1Title: 'Prompt',
          aiStep1Description: 'Describe the goal and select approved project knowledge.',
          aiStep2Title: 'Structured draft',
          aiStep2Description: 'Receive content shaped to a known responsive template.',
          aiStep3Title: 'Review and publish',
          aiStep3Description: 'Edit, validate and deliberately publish the result.',
          securityEyebrow: 'Access with intent',
          securityTitle: 'Share openly or restrict precisely.',
          securityDescription:
            'Company, Project and Presentation access, strong MFA, protected ShareLinks and audited publishing are designed into the workflow.',
          learnSecurity: 'Explore security',
        },
        features: {
          metaTitle: 'Features',
          title: 'Everything you need to build, share and measure interactive presentations.',
          description:
            'A structured creation workflow, high-quality templates and a purpose-built web viewer work together from first draft to audience insight.',
          group1Title: 'Creation',
          group1Description: 'Build a clear story in a focused, schema-driven editor.',
          group1Point1: 'Structured slide content and live preview',
          group1Point2: 'Revision-safe collaborative editing',
          group2Title: 'Templates & Themes',
          group2Description:
            'Use developer-built responsive templates and controlled brand themes.',
          group2Point1: 'Immutable template and theme versions',
          group2Point2: 'Consistent design across a company',
          group3Title: 'Interactions & Media',
          group3Description: 'Add purposeful hotspots, media and embedded experiences.',
          group3Point1: 'Accessible interaction patterns',
          group3Point2: 'Versioned private asset delivery',
          group4Title: 'Narration',
          group4Description: 'Create presentation audio from reviewed scripts.',
          group4Point1: 'Per-slide narration lifecycle',
          group4Point2: 'Playback controls and transcripts',
          group5Title: 'Sharing & Access',
          group5Description: 'Publish immutable releases and choose who can view them.',
          group5Point1: 'Public, password or invite access',
          group5Point2: 'One link resolves the latest good release',
          group6Title: 'Analytics',
          group6Description: 'Measure real presentation engagement.',
          group6Point1: 'Views, completion and active time',
          group6Point2: 'Slide drop-off and interactions',
          group7Title: 'AI & Knowledge',
          group7Description: 'Ground generation in approved project content.',
          group7Point1: 'Structured, validated output',
          group7Point2: 'Provider policy by data classification',
          group8Title: 'Collaboration',
          group8Description: 'Review, comment and publish with explicit access.',
          group8Point1: 'Scoped Company and Project roles',
          group8Point2: 'Review status and notifications',
        },
        templates: {
          metaTitle: 'Presentation templates',
          title: 'Responsive templates built for communication, not decoration.',
          description:
            'Explore representative systems for launches, reports, proposals and stories. These examples illustrate Slider’s template-driven quality.',
          galleryLabel: 'Public template examples',
          responsive: 'Responsive',
          interactive: 'Interactive',
          chart: 'Chart',
          item1Art: 'Launch',
          item1Title: 'Product launch',
          item1Description: 'Frame the problem, solution and path to adoption.',
          item2Art: 'Metrics',
          item2Title: 'Performance review',
          item2Description: 'Turn key metrics into a clear leadership narrative.',
          item3Art: 'Proposal',
          item3Title: 'Sales proposal',
          item3Description: 'Connect customer context to a focused recommendation.',
          item4Art: 'Research',
          item4Title: 'Research readout',
          item4Description: 'Explain evidence, patterns and next decisions.',
          item5Art: 'Story',
          item5Title: 'Customer story',
          item5Description: 'Present an outcome with credible supporting detail.',
          item6Art: 'Brief',
          item6Title: 'Strategy brief',
          item6Description: 'Align a team around priorities, ownership and timing.',
        },
        security: {
          metaTitle: 'Security',
          title: 'Security and access designed around how teams actually publish.',
          description:
            'Slider separates internal collaboration from public viewing and enforces access on the server at every resource boundary.',
          item1Title: 'Authentication and MFA',
          item1Description:
            'Secure HttpOnly sessions with passkeys, TOTP and deliberate recovery options.',
          item2Title: 'Tenant isolation',
          item2Description:
            'Company-owned data is protected by application authorization and PostgreSQL row-level security.',
          item3Title: 'Scoped access',
          item3Description:
            'Company, Project and Presentation permissions are explicit; viewer access stays read-only.',
          item4Title: 'Secure ShareLinks',
          item4Description:
            'Public, password and invite access are distinct from internal membership.',
          item5Title: 'Private object storage',
          item5Description:
            'Private source objects and immutable released derivatives follow separate delivery policies.',
          item6Title: 'Audit and security events',
          item6Description:
            'Important access and publishing changes create durable records with request correlation.',
          item7Title: 'AI provider policy',
          item7Description:
            'Data classification and provider eligibility are checked before approved content leaves the application boundary.',
          noCertificationClaim:
            'This page describes current product architecture and policy. Slider does not claim certifications it has not obtained.',
        },
        about: {
          metaTitle: 'About',
          title: 'Presentations should work like thoughtful web products.',
          description:
            'Slider is a structured platform for creating, publishing and measuring interactive presentations without recreating a design tool.',
          whyTitle: 'Why Slider exists',
          whyDescription:
            'Traditional presentation workflows split content, design, sharing and feedback across disconnected files and tools.',
          whyDescription2:
            'Slider brings those steps into one web-native lifecycle while preserving professional design through deliberate templates.',
          principle1Title: 'Web-native',
          principle1Description: 'Responsive output and link-based delivery are the default.',
          principle2Title: 'Structured',
          principle2Description: 'Clear schemas make quality repeatable and collaboration safer.',
          principle3Title: 'Design-quality',
          principle3Description:
            'Templates encode intentional systems instead of improvising every slide.',
        },
        earlyAccess: {
          metaTitle: 'Request Early Access',
          eyebrow: 'Early Access',
          title: 'Help shape a better presentation workflow.',
          description:
            'Tell us how your team creates and shares presentations. We review every request and will contact you if access becomes available.',
          back: 'Back to home',
          benefit1: 'Explore a structured web-native editor',
          benefit2: 'Publish responsive interactive experiences',
          benefit3: 'Share feedback directly with the product team',
          name: 'Name',
          email: 'Work email',
          company: 'Company',
          role: 'Role',
          useCase: 'What would you use Slider for?',
          useCasePlaceholder:
            'For example: interactive product launches for distributed sales teams.',
          useCaseHint: 'Tell us a little more (at least 10 characters).',
          required: 'This field is required.',
          invalidEmail: 'Enter a valid work email.',
          consent: 'Product updates and Early Access news',
          consentDescription:
            'Optional. Slider may email me about product updates. This is separate from the service acknowledgement.',
          privacyNote:
            'Submitting this form does not create an account. We use these details only to review and respond to your request.',
          submitError: 'Your request could not be submitted. Please try again.',
          rateLimited: 'Too many requests were received. Please wait before trying again.',
          successEyebrow: 'Early Access',
          successTitle: 'Request received',
          successBody:
            'Thanks. We’ll review your request and contact you at {{email}} if access becomes available.',
          backHome: 'Back to home',
          roles: {
            MARKETING: 'Marketing',
            DESIGN: 'Design',
            PRODUCT: 'Product',
            SALES: 'Sales',
            LEADERSHIP: 'Leadership',
            ENGINEERING: 'Engineering',
            OTHER: 'Other',
          },
        },
        privacy: {
          eyebrow: 'Legal',
          title: 'Privacy',
          description:
            'How Slider handles information submitted through the public site and product.',
          updated: 'Last updated: August 28, 2026',
          section1Title: 'Information we collect',
          section1Body:
            'We collect information you choose to submit, such as Early Access contact details, and limited technical information required to operate and protect the service.',
          section2Title: 'How we use information',
          section2Body:
            'We use information to respond to requests, operate Slider, secure accounts, improve reliability and meet legal obligations.',
          section3Title: 'Service providers',
          section3Body:
            'Approved infrastructure, email and AI providers process only the information needed for their documented purpose and policy.',
          section4Title: 'Retention and security',
          section4Body:
            'We retain information according to product and legal needs and use access controls, encryption and audit records appropriate to the data.',
          section5Title: 'Choices and contact',
          section5Body:
            'Marketing follow-up is optional and separate from service messages. Contact the Slider operator for access, correction or deletion requests.',
        },
        terms: {
          eyebrow: 'Legal',
          title: 'Terms',
          description:
            'The basic terms for using the Slider public site and invitation-only product.',
          updated: 'Last updated: August 28, 2026',
          section1Title: 'Using Slider',
          section1Body:
            'Slider is currently invitation-only. You must use the service lawfully and follow the access policies of the Company that invited you.',
          section2Title: 'Accounts and access',
          section2Body:
            'Keep authentication methods secure and do not share access beyond the scope granted to you.',
          section3Title: 'Your content',
          section3Body:
            'You remain responsible for content you submit and for ensuring you have the rights and permissions needed to use it.',
          section4Title: 'Availability and changes',
          section4Body:
            'Early Access features may change as the product develops. We work to keep published experiences reliable but do not promise uninterrupted availability.',
          section5Title: 'Responsible use',
          section5Body:
            'Do not attempt to bypass access controls, disrupt the service or use Slider to distribute unlawful or harmful content.',
        },
      },
      invitation: {
        eyebrow: 'Company invitation',
        loadingTitle: 'Checking your invitation',
        loadingDescription: 'One moment while we confirm this invitation.',
        invalidTitle: 'Invitation unavailable',
        invalidDescription:
          'This invitation link is not valid. Ask your administrator to send a new one.',
        expiredTitle: 'Invitation expired',
        expiredDescription:
          'This invitation has expired. Ask your administrator to send a new one.',
        revokedTitle: 'Invitation no longer valid',
        revokedDescription:
          'This invitation was withdrawn. Contact your administrator if you think this is a mistake.',
        alreadyAcceptedTitle: 'Invitation already used',
        alreadyAcceptedDescription:
          'This invitation has already been accepted. Sign in to continue.',
        errorTitle: 'We could not check this invitation',
        errorDescription: 'Something went wrong on our side. Try again in a moment.',
        retry: 'Try again',
        summary:
          'You have been invited to {{company}} as {{role}}. This invitation is for {{email}}.',
        role: { OWNER: 'Owner', ADMIN: 'Admin', EDITOR: 'Editor', VIEWER: 'Viewer' },
        signInTitle: 'Sign in to join {{company}}',
        signInToAccept: 'Sign in to accept invitation',
        acceptTitle: 'Join {{company}}',
        accept: 'Accept invitation',
        setupTitle: 'Set up your account to join {{company}}',
        displayName: 'Your name',
        displayNamePlaceholder: 'Enter your full name',
        createAccount: 'Create account and join',
        emailFixed: 'Your account will use the email address this invitation was sent to.',
        mismatchTitle: 'This invitation is for a different account',
        mismatchDescription:
          'You are signed in with another account. This invitation was sent to {{email}}. Sign out to continue with the invited account.',
        switchAccount: 'Sign out and switch account',
        errorRateLimited: 'Too many attempts. Wait a little, then try again.',
        errorValidation: 'Check your name and password, then try again.',
        errorGeneric: 'We could not accept this invitation. Try again.',
      },
      auth: {
        eyebrow: 'Welcome back',
        title: 'Sign in to Slider',
        description: 'Use your work email and password to continue to your company workspace.',
        email: 'Work email',
        emailPlaceholder: 'name@company.com',
        password: 'Password',
        passwordPlaceholder: 'Enter your password',
        showPassword: 'Show password',
        hidePassword: 'Hide password',
        forgotPassword: 'Forgot password?',
        signIn: 'Sign in',
        needAccess: 'Need access?',
        requestEarlyAccess: 'Request early access',
        verifyEyebrow: 'One more step',
        verifyTitle: 'Verify it’s you',
        verifyDescription: 'Use one of your available verification methods to finish signing in.',
        usePasskey: 'Continue with passkey',
        verificationCode: 'Verification code',
        recoveryCode: 'Recovery code',
        recoveryCodePlaceholder: 'Enter your recovery code',
        verify: 'Verify and sign in',
        otherMethods: 'Other verification methods',
        backToSignIn: 'Back to sign in',
        errorInvalidCredentials: 'The email or password is incorrect.',
        errorInvalidCode: 'That verification code is incorrect or has already been used.',
        errorCodeRequired: 'Enter the verification or recovery code to continue.',
        errorCodeIncomplete: 'Enter all {{digits}} digits of the verification code.',
        errorRateLimited: 'Too many attempts. Wait a little, then try again.',
        errorChallengeExpired: 'This sign-in attempt expired. Start again.',
        errorPasskey: 'The passkey could not be verified. Try again or use another method.',
        errorGeneric: 'Something went wrong. Try again.',
        method: {
          PASSKEY: 'Use a passkey',
          TOTP: 'Use authenticator app',
          EMAIL_OTP: 'Send email code',
          RECOVERY_CODE: 'Use recovery code',
        },
        resetEyebrow: 'Account recovery',
        forgotTitle: 'Reset your password',
        forgotDescription: 'Enter your work email and we’ll send you a secure reset link.',
        sendResetLink: 'Send reset link',
        resetRequested: 'If an account exists for that email, we sent password reset instructions.',
        resetTitle: 'Choose a new password',
        resetDescription: 'Create a new password for your Slider account.',
        newPassword: 'New password',
        newPasswordPlaceholder: 'Create a password with at least 15 characters',
        confirmPassword: 'Confirm new password',
        confirmPasswordPlaceholder: 'Re-enter your new password',
        passwordHint: 'Use at least 15 characters.',
        updatePassword: 'Update password',
        errorPasswordMismatch: 'The passwords do not match.',
        errorResetLink: 'This reset link is invalid, expired, or has already been used.',
        requestNewLink: 'Request a new link',
        passwordChangedTitle: 'Password updated',
        passwordChangedDescription: 'Your password has been changed. You can now sign in.',
        enrollmentEyebrow: 'Protect your account',
        enrollmentTitle: 'Set up strong authentication',
        enrollmentDescription:
          'Your role requires a passkey or authenticator app before you can enter Slider.',
        enrollPasskey: 'Set up a passkey',
        enrollAuthenticator: 'Set up authenticator app',
        passkeyDeviceName: 'This device',
        scanQrCode: 'Scan this QR code with your authenticator app, then enter its 6-digit code.',
        qrCodeAlt: 'Authenticator setup QR code',
        manualSecret: 'Manual setup key',
        finishEnrollment: 'Verify and finish setup',
        strongMfaHint:
          'Strong MFA means a passkey or authenticator app. Email codes are a fallback method.',
        recoveryCodesTitle: 'Save your recovery codes',
        recoveryCodesDescription:
          'Each code can be used once if you lose access to your authenticator. Store them somewhere safe; they will not be shown again.',
        copyRecoveryCodes: 'Copy recovery codes',
        savedRecoveryCodes: 'I saved these codes',
        errorSessionExpired: 'Your setup session expired. Sign in again to continue.',
      },
      manage: {
        workspace: 'Workspace',
        overview: 'Overview',
        projects: 'Projects',
        presentations: 'Presentations',
        templates: 'Templates',
        templateCategories: 'Categories',
        designLibrary: 'Design Library',
        aiActions: 'AI Actions',
        assets: 'Assets',
        analytics: 'Analytics',
        settings: 'Settings',
        pageTitle: 'Company overview',
        pageDescription: 'Your workspace foundation is ready for product modules.',
        recentWork: 'Recent work',
        emptyTitle: 'No presentations yet',
        emptyDescription: 'Create a project first, then your team can start a presentation.',
        createProject: 'Create project',
        profile: 'Open account menu',
        navigationLabel: 'Management',
        secondaryNavigationLabel: 'Company',
        collapseSidebar: 'Collapse sidebar',
        expandSidebar: 'Expand sidebar',
        switchCompany: 'Switch company',
        companies: 'Companies',
        companySettings: 'Company settings',
        search: 'Search',
        commandPlaceholder: 'Search pages and actions…',
        commandTitle: 'Search Slider',
        commandDescription: 'Navigate to a page or run an available action.',
        commandEmpty: 'No matching page or action.',
        notifications: 'Notifications',
        noNotifications: 'You’re all caught up. New notifications will appear here.',
        account: 'Account',
        security: 'Security',
        sessions: 'Sessions',
        appearance: 'Appearance',
        systemTheme: 'System',
        lightTheme: 'Light',
        darkTheme: 'Dark',
        language: 'Language',
        signOut: 'Sign out',
        signingOut: 'Signing out…',
      },
      companyOverview: {
        eyebrow: 'Company overview',
        description: 'Your team’s projects, presentations and recent activity.',
        summary: 'Company summary',
        projects: 'Projects',
        presentations: 'Presentations',
        published: 'Published',
        views: 'Views · 30 days',
        members: 'Members',
        newProject: 'New project',
        newPresentation: 'New presentation',
        recentPresentations: 'Recent presentations',
        viewAll: 'View all',
        noPresentations: 'No presentations yet.',
        recentActivity: 'Recent activity',
        activityLine: '{{actor}} · {{action}}',
        system: 'System',
        firstProjectTitle: 'Create your first project',
        firstProjectDescription:
          'Projects group themes, knowledge, assets and presentations for an initiative.',
        errorTitle: 'Company overview could not be loaded',
        errorDescription: 'Try again or return to this workspace later.',
        retry: 'Try again',
        ...companyOverviewCopy.en,
      },
      presentations: {
        shareLinkProposalReview: shareLinkProposalReviewCopy.en,
        copyReview: copyReviewCopy.en,
        copyToProject: copyToProjectCopy.en,
        duplicate: duplicateCopy.en,
        gallery: presentationsGalleryCopy.en,
        guided: guidedCreateCopy.en,
        eyebrow: 'Presentations',
        title: 'Presentations',
        description: 'Create, review and publish interactive stories.',
        new: 'New presentation',
        search: 'Search presentations',
        searchPlaceholder: 'Search presentations…',
        empty: 'No presentations yet',
        filteredEmpty: 'No presentations match your search',
        emptyDescription: 'Create a presentation inside a writable project when you are ready.',
        errorTitle: 'Presentations could not be loaded',
        retry: 'Try again',
        results_one: '{{count}} presentation',
        results_other: '{{count}} presentations',
        pages: 'Presentation pages',
        previous: 'Previous',
        next: 'Next',
        page: 'Page {{page}}',
        back: 'Back to presentations',
        open: 'Open {{name}}',
        cardActions: 'Actions for {{name}}',
        moreActions: 'More actions',
        delete: {
          action: 'Delete presentation',
          title: 'Delete presentation?',
          description:
            'This removes “{{name}}” from active views. Published history and analytics may be retained according to policy.',
          publishedWarning:
            'This presentation is published. Its share links stop working immediately.',
          typeToConfirm: 'Type “{{name}}” to confirm',
          confirm: 'Delete presentation',
          deleting: 'Deleting…',
          success: 'Presentation deleted.',
          error: 'We couldn’t delete this presentation. Try again.',
          errorConflict:
            'This presentation changed or was already deleted. Review it, then try again.',
          errorDenied: 'You don’t have permission to delete this presentation.',
        },
        newTitle: 'Create presentation',
        newDescription: 'Start a structured presentation in this project.',
        fields: {
          name: 'Presentation name',
          description: 'Description',
          descriptionPlaceholder: 'What is this presentation about?',
        },
        create: 'Create presentation',
        createError: 'The presentation could not be created.',
        createSuccess: 'Presentation created.',
        status: {
          DRAFT: 'Draft',
          IN_REVIEW: 'In review',
          APPROVED: 'Approved',
          PUBLISHED: 'Published',
        },
      },
      presentationAuthoring: {
        unavailable: 'This presentation is unavailable or you do not have access.',
        appearance: 'Appearance',
        customizeAppearance: 'Customize appearance',
        customizeAppearanceHint:
          'Adjust colors, fonts and surfaces for this presentation. Slides and published releases keep their own content.',
        noDescription: 'A structured web-native presentation.',
        editSlides: 'Edit slides',
        addSlide: 'Add slide',
        open: 'Open presentation',
        publish: 'Publish',
        preview: 'Preview',
        previewPresentation: 'Preview presentation',
        share: 'Share',
        notAvailableYet: 'Not available yet',
        draftPreview: 'Draft preview',
        themeFallback: {
          title: 'Preview is using fallback theme values. Publishing is blocked.',
        },
        closePreview: 'Close preview',
        previewViewport: 'Preview viewport',
        viewport: { wide: 'Wide preview', compact: 'Compact preview', mobile: 'Mobile preview' },
        navigation: 'Presentation navigation',
        panels: 'Slide panels',
        visibilityOptions: { auto: 'Auto', shown: 'Shown', hidden: 'Hidden' },
        overview: 'Overview',
        slides: 'Slides',
        selectSlideHint: 'Select a slide to preview it above.',
        searchSlides: 'Search slides',
        noMatchingSlides: 'No slides match this search.',
        selectedSlide: 'Currently previewing',
        review: 'Review',
        analytics: 'Analytics',
        shares: 'Shares',
        access: 'Access',
        settings: 'Settings',
        readiness: 'Publication readiness',
        workflow: 'Workflow',
        live: 'Live release',
        liveRelease: 'Release {{number}}',
        notPublished: 'Not published',
        slideCount: 'Slide count',
        updated: 'Updated',
        chooseTemplate: 'Choose a template',
        chooseTemplateDescription: 'Add a structured slide using a trusted responsive template.',
        changeTemplate: 'Change template',
        chooseAsset: 'Choose asset',
        chooseAssetDescription: 'Select a ready Project or Company asset.',
        replaceImage: 'Replace image',
        removeImage: 'Remove image',
        noImageSelected: 'No image selected',
        link: {
          missingSlide: 'The linked slide no longer exists. Choose another destination.',
          nextSlide: 'Next slide',
          none: 'No link',
          slideOption: 'Slide {{number}}: {{title}}',
          urlInvalid: 'Use a full https:// address.',
          urlLabel: 'Website address',
          website: 'Website (https)',
        },
        backOverview: 'Back to presentation overview',
        history: 'Edit history',
        undo: 'Undo',
        redo: 'Redo',
        undoShortcut: 'Undo (Ctrl/Cmd+Z)',
        redoShortcut: 'Redo (Ctrl+Y or Ctrl/Cmd+Shift+Z)',
        viewOnly: 'View only',
        slideNavigation: 'Slide navigation',
        inspector: 'Inspector',
        assetField: 'Asset',
        content: 'Content',
        design: 'Design',
        narration: 'Notes & narration',
        presenterNotes: 'Presenter notes',
        presenterScript: 'Presenter read-aloud script',
        presenterScriptCopy: {
          fromNarration: 'Copy from narration script',
          fromNotes: 'Copy from presenter notes',
          confirmTitle: 'Replace read-aloud script',
          confirmBody:
            'The read-aloud script already has text. Replace it with the copied text? This is not linked afterwards.',
          replace: 'Replace',
          cancel: 'Cancel',
        },
        narrationScript: 'Narration script',
        narrationControls: {
          ...narrationFeedbackCopy.en,
          title: 'Generated narration',
          description:
            'Choose a trusted voice profile, delivery style, and speed. Generation uses the saved script.',
          voice: 'Voice profile',
          style: 'Delivery style',
          speed: 'Speed',
          generate: 'Generate audio',
          regenerate: 'Regenerate audio',
          processing: 'Generating narration in the background…',
          scriptRequired: 'Add and save a narration script before generating audio.',
          transcript: 'Transcript',
          audioUnsupported: 'Your browser cannot play this narration.',
          staleTitle: 'Narration is out of date',
          staleDescription: 'The script or voice settings changed. Regenerate before publishing.',
          failedTitle: 'Generation failed',
          failedDescription:
            'The provider could not generate this narration. You can retry safely.',
          styles: { NEUTRAL: 'Neutral', WARM: 'Warm', ENERGETIC: 'Energetic', CALM: 'Calm' },
          status: {
            NONE: 'Not started',
            DRAFT_SCRIPT: 'Draft script',
            PROCESSING: 'Generating',
            READY: 'Ready',
            STALE: 'Out of date',
            FAILED: 'Failed',
          },
          errors: {
            NARRATION_SCRIPT_EMPTY: 'Add and save a narration script first.',
            VOICE_PROFILE_UNAVAILABLE: 'This voice profile is unavailable.',
            TTS_PROVIDER_UNAVAILABLE:
              'Narration is not configured or is disabled for this company. Ask a company Owner or Admin to connect Gemini in AI Providers & Voice.',
            NARRATION_DAILY_LIMIT_REACHED:
              'This company has reached its daily narration limit. Try again tomorrow or ask a company Owner or Admin to raise the limit.',
            NARRATION_GENERATION_FAILED: 'Narration generation failed. Try again.',
            default: 'Narration could not be generated.',
          },
        },
        batchNarration: {
          open: 'Presentation-wide narration',
          singleSlide: 'Back to this slide',
          title: 'Presentation narration preview',
          description:
            'Review narration status across slides and preview a selection. Script edits stay temporary until you preview.',
          loadError: 'Narration status could not be loaded',
          empty: 'This presentation has no slides to review.',
          pageBoundary:
            'Showing page {{page}} of {{totalPages}} ({{count}} slides total). Selection is kept as you move between pages.',
          selectPage: 'Select all {{count}} loaded slides',
          selectSlide: 'Select slide {{number}}',
          selectedCount: '{{count}} selected',
          selectionLimit:
            'Preview supports up to {{count}} selected slides. Deselect one or more slides to continue.',
          scriptLimit:
            'Preview supports up to {{count}} script edits and {{bytes}} KiB of serialized script overrides. Clear edits or deselect edited slides to continue.',
          baseVoice: 'Base voice',
          slideVoice: 'Voice for this slide',
          useBaseVoice: 'Use base voice',
          scriptOverride: 'Temporary script override',
          slide: 'Slide {{number}}',
          blocked: 'Previous issue: {{reason}}',
          previous: 'Previous page',
          next: 'Next page',
          page: 'Page {{page}} of {{totalPages}}',
          preview: 'Preview selected slides',
          previewError: 'Preview could not be created',
          previewErrorDescription: 'Check the selection and voice settings, then try again.',
          previewResult: 'Preview summary',
          expectedJobs: '{{count}} jobs would be needed',
          approximateDuration: 'Approximate narration duration: {{duration}}',
          durationMinutesSeconds: '{{minutes}} min {{seconds}} sec',
          durationSeconds: '{{seconds}} sec',
          costUnavailable: 'Estimated cost: unavailable',
          dispatchDisabled: 'Generation is unavailable',
          dispatchExplanation:
            'Human approval for paid narration operations has not been implemented. This preview does not generate audio or queue jobs.',
          generateDisabled: 'Generate narration (unavailable)',
          actions: {
            ALREADY_PROCESSING: 'In progress',
            GENERATE_REQUIRED: 'Generation needed',
            REUSE_READY: 'Ready audio can be reused',
            SKIP_NO_SCRIPT: 'No script',
          },
          reasons: {
            CLASSIFICATION_POLICY_BLOCKED: 'Blocked by content policy',
            PROVIDER_NOT_CONFIGURED: 'Voice provider is not configured',
            SCRIPT_LIMIT_EXCEEDED: 'Script exceeds the limit',
            VOICE_PROFILE_UNAVAILABLE: 'Voice is unavailable',
          },
        },
        fieldLabels: {
          title: 'Title', subtitle: 'Subtitle', logo: 'Logo',
          description: 'Description', headline: 'Headline', eyebrow: 'Eyebrow',
          caption: 'Caption', body: 'Body', image: 'Image',
          text: 'Text', name: 'Name', label: 'Label', value: 'Value', icon: 'Icon',
        },
        noFields: 'This template has no editable fields.',
        textScale: {
          heading: 'Text size',
          option: '{{percent}}%',
          hint: 'Larger or smaller text for this slide. Text is still reduced automatically if it would not fit.',
          dynamicHtml: 'Dynamic HTML slides control their own text size, so this may not apply.',
          scaled: 'Reduced to {{percent}}% to fit the slide.',
          overflow:
            'This text does not fit the slide even at the smallest size. Shorten it or split it across two slides.',
        },
        theme: {
          heading: 'Theme',
          usePresentationTheme: 'Use presentation theme',
          useCustomTheme: 'Custom for this slide',
          resetToPresentationTheme: 'Reset to presentation theme',
          colors: {
            primary: 'Primary',
            background: 'Background',
            surface: 'Surface',
            text: 'Text',
            textMuted: 'Muted',
            border: 'Border',
          },
          invalidColor: 'Enter a valid CSS color (e.g. #6933ff).',
        },
        repeaterItemOf: '{{label}} {{index}} of {{count}}',
        dragItem: 'Drag {{label}} {{index}} to reorder',
        addRepeaterItem: 'Add {{label}}',
        duplicateItem: 'Duplicate',
        deleteItem: 'Delete',
        moveItemUp: 'Move {{label}} {{index}} up',
        moveItemDown: 'Move {{label}} {{index}} down',
        repeaterMinReached: 'At least {{count}} {{label}} required',
        repeaterMaxReached: 'At most {{count}} {{label}} allowed',
        listField: {
          unsupported: '{{label}} cannot be edited here.',
          fields: {
            title: 'Title',
            body: 'Body',
            description: 'Description',
            label: 'Label',
            text: 'Text',
            name: 'Name',
            value: 'Value',
            subtitle: 'Subtitle',
          },
        },
        deleteSlide: 'Delete slide',
        dragSlideNamed: 'Drag slide {{title}} to reorder',
        moveSlideUpNamed: 'Move slide {{title}} up',
        moveSlideDownNamed: 'Move slide {{title}} down',
        deleteSlideNamed: 'Delete slide: {{title}}',
        deleteSlideTitle: 'Delete this slide?',
        deleteSlideDescription: 'Remove “{{title}}” from this presentation? This cannot be undone. Other slides are not affected.',
        deletingSlide: 'Deleting…',
        deleteSlideSaveError: 'The current slide has unsaved changes. Save or resolve them before deleting it.',
        deleteSlideUnavailable: 'This slide cannot be deleted. Check your permissions or refresh the presentation.',
        deleteSlideConflict: 'This slide or presentation changed elsewhere. Refresh before trying again.',
        deleteSlideError: 'Could not delete this slide. No further changes were made.',
        conflict: 'This slide changed elsewhere. Reload it before continuing.',
        saveFailureTitle: 'Changes are not saved',
        saveFailureDescription: 'Automatic saving is paused. Your changes remain in this editor. Review the error code and retry once the issue is resolved.',
        saveFailureCode: 'Error code',
        saveFailureRequest: 'Request ID',
        retrySave: 'Retry save',
        save: {
          idle: 'Ready',
          saving: 'Saving…',
          saved: 'Saved',
          conflict: 'Conflict',
          error: 'Save failed',
        },
      },
      publishQa: {
        title: 'Publish presentation',
        description: 'Review the immutable release checks before making this draft live.',
        checking: 'Checking presentation…',
        passedChecks: 'Passed checks ({{count}})',
        publishedToast: 'Presentation published.',
        technicalDetails: 'Technical details',
        requestId: 'Request ID',
        operationId: 'Operation ID',
        viewErrors: 'View errors',
        slides: 'Slides',
        errors: 'Errors',
        warnings: 'Warnings',
        loadError: 'Publish checks could not be loaded',
        loadErrorDescription: 'Try the checks again. Nothing has been published.',
        retry: 'Try again',
        cancel: 'Cancel',
        publish: 'Publish release',
        starting: 'Starting publish…',
        done: 'Done',
        openSlide: 'Open slide {{number}}',
        acceptWarnings: 'Publish with these warnings',
        acceptWarningsDescription: 'I reviewed the warnings and want to continue.',
        submitError: 'Publish could not start',
        failedTitle: 'Publish failed',
        successTitle: 'Presentation published',
        successDescription:
          'Release {{number}} is now live. Draft changes made later will not alter it.',
        severity: { ERROR: 'Blocking', WARNING: 'Warning', PASSED: 'Passed' },
        operation: {
          loading: 'Preparing publish',
          loadingDescription: 'Creating a durable publish operation…',
          QUEUED: {
            title: 'Publish queued',
            description: 'The immutable release is waiting for a render worker.',
          },
          VALIDATING: {
            title: 'Validating release',
            description: 'Checking exact templates, theme, variables and assets.',
          },
          RENDERING: {
            title: 'Rendering release',
            description: 'Generating the social preview and slide thumbnails.',
          },
          FINALIZING: {
            title: 'Activating release',
            description: 'Completing quality gates and switching the live release atomically.',
          },
        },
        failure: {
          PRESENTATION_CONFLICT:
            'The draft changed. Review the refreshed checks before publishing.',
          PUBLISH_VALIDATION_FAILED: 'One or more publish checks must be resolved.',
          PUBLISH_ASSET_NOT_READY: 'A referenced asset is not ready.',
          PUBLISH_SOCIAL_PREVIEW_FAILED: 'The required social preview could not be generated.',
          PUBLISH_RENDER_FAILED: 'A release image could not be generated.',
          PUBLISH_RELEASE_PREPARATION_FAILED:
            'Release preparation failed after automatic retries. The previous live release is unchanged.',
          PRESENTATION_PUBLISH_IN_PROGRESS: 'Another publish is already in progress.',
          default: 'The release could not be prepared. The previous live release is unchanged.',
        },
      },
      presentationVariables: {
        title: 'Presentation variables',
        description: 'Define reusable values for slides and optional ShareLink personalization.',
        add: 'Add variable',
        empty: 'No presentation variables yet.',
        key: 'Name / key',
        type: 'Type',
        defaultValue: 'Default value',
        shareOverride: 'Allow ShareLink override',
        remove: 'Remove',
        save: 'Save variables',
        saved: 'Variables saved.',
        conflict: 'The presentation changed elsewhere. Reload before saving variables.',
        error: 'Variables could not be saved.',
        types: {
          STRING: 'Text',
          NUMBER: 'Number',
          BOOLEAN: 'Boolean',
          DATE: 'Date',
          URL: 'URL',
          ASSET: 'Asset version',
        },
      },
      collaboration: {
        comments: 'Comments',
        presence: 'People here',
        viewing: 'Viewing',
        editing: 'Editing',
        empty: 'No comments on this slide',
        emptyDescription: 'Start a focused discussion without changing slide content.',
        loadError: 'Comments could not be loaded.',
        addComment: 'Add comment',
        placeholder: 'Write a comment…',
        send: 'Send',
        reply: 'Reply',
        resolve: 'Resolve',
        reopen: 'Reopen',
        replying: 'Replying to a thread',
        cancel: 'Cancel',
        createError: 'Comment could not be sent.',
        readAll: 'Mark all read',
        new: 'New',
        notification: {
          COMMENT_CREATED: 'New presentation comment',
          COMMENT_MENTION: 'You were mentioned',
          COMMENT_REPLY: 'New reply',
          REVIEW_UPDATE: 'Review updated',
        },
      },
      projectSettings: projectSettingsCopy.en,
      project: {
        navigation: {
          label: 'Project',
          project: 'Project',
          overview: 'Overview',
          presentations: 'Presentations',
          assets: 'Assets',
          knowledge: 'Knowledge',
          aiProposals: 'AI proposals',
          theme: 'Theme',
          access: 'Access',
          settings: 'Settings',
        },
        new: {
          back: 'Back to projects',
          eyebrow: 'Projects',
          title: 'Create project',
          description: 'Set up a focused space for presentations, themes, assets and knowledge.',
          detailsHeading: 'Details',
          submit: 'Create project',
          successToast: 'Project created.',
        },
        fields: {
          name: 'Project name',
          namePlaceholder: 'e.g. Website redesign',
          description: 'Description',
          descriptionPlaceholder: 'What is this project for?',
          descriptionTooLong: 'Keep the description under 1,000 characters.',
          classification: 'Data classification',
          classificationHelp:
            'Classification controls which AI and external providers may process this project.',
          required: 'Project name is required.',
        },
        classification: {
          NON_SENSITIVE: 'Non-sensitive',
          INTERNAL: 'Internal',
          CONFIDENTIAL: 'Confidential',
        },
        status: { ACTIVE: 'Active', ARCHIVED: 'Archived' },
        actions: { cancel: 'Cancel', retry: 'Try again', save: 'Save changes' },
        errors: {
          denied: 'You cannot create projects in this company.',
          generic: 'Something went wrong. Try again.',
          validation: 'Check the highlighted fields and try again.',
          unavailableTitle: 'Project unavailable',
          unavailable: 'This project is unavailable or you do not have access.',
          conflict: 'This project changed elsewhere. Reload before saving again.',
        },
        overview: {
          eyebrow: 'Project overview',
          title: 'Overview',
          description: 'A focused summary of this project.',
          presentations: 'Presentations',
          classification: 'Classification',
          status: 'Status',
          emptyTitle: 'No presentations yet',
          emptyDescription: 'Create the first presentation for this project.',
          recent: 'Recent presentations',
          themeTitle: 'Project theme',
          themeDescription: 'Keep brand and presentation tokens consistent.',
          openTheme: 'Open theme',
          knowledgeTitle: 'Project knowledge',
          knowledgeDescription: 'Ground future AI workflows in approved source material.',
          openKnowledge: 'Open knowledge',
        },
        settings: {
          title: 'Project settings',
          description: 'Manage project identity, classification and lifecycle.',
          saved: 'Project settings saved.',
        },
        assets: {
          title: 'Assets',
          description: 'Project images, video and audio.',
          emptyTitle: 'No project assets yet',
          emptyDescription: 'Uploads and the asset pipeline are introduced in the Asset stage.',
        },
        knowledge: {
          title: 'Knowledge',
          description: 'Approved source material for this project.',
          emptyTitle: 'No knowledge sources yet',
          emptyDescription: 'Document and URL ingestion is introduced in the Knowledge stage.',
        },
        theme: {
          title: 'Theme',
          description: 'Project presentation colors, typography, shape and motion.',
          emptyTitle: 'No theme version yet',
          emptyDescription: 'Create the first immutable Theme Version in the Theme stage.',
        },
        access: {
          title: 'Project access',
          description: 'Review inherited Company access and direct project grants.',
          emptyTitle: 'No direct project grants',
          emptyDescription: 'Company-wide members may already have inherited access.',
        },
      },
      projectAccess: {
        title: 'Project access',
        description:
          'People with Project access can reach every presentation in this Project within their Company role.',
        errorTitle: 'Project access could not be loaded',
        empty: 'No company members',
        emptyDescription: 'Invite a company member before assigning project access.',
        member: 'Member',
        companyRole: 'Company role',
        source: 'Access source',
        level: 'Project level',
        levelFor: 'Project access level for {{name}}',
        inherited: 'Inherited from Company',
        none: 'No access',
        viewer: 'Viewer',
        editor: 'Editor',
        results_one: '{{count}} member',
        results_other: '{{count}} members',
        pages: 'Project access pages',
        remove: 'Remove project access for {{name}}',
        sources: { COMPANY: 'Company', PROJECT: 'Project', NONE: 'None' },
        search: 'Search members',
        searchPlaceholder: 'Search by name or email',
        presentationOnly_one: '{{count}} direct presentation grant',
        presentationOnly_other: '{{count}} direct presentation grants',
      },
      presentationAccess: {
        title: 'Presentation access',
        description:
          'Manage direct access to this presentation. Project and Company access are inherited automatically.',
        search: 'Search members',
        searchPlaceholder: 'Search by name or email',
        errorTitle: 'Presentation access could not be loaded',
        saveError: 'The access change could not be saved.',
        conflictTitle: 'Access changed elsewhere',
        conflictDescription:
          'Another administrator changed this access after the list loaded. Nothing was overwritten; the list now shows the current state.',
        empty: 'No members found',
        emptyDescription: 'Invite a company member before assigning presentation access.',
        inheritedAccess: 'Inherited access',
        directGrant: 'Direct grant',
        noInherited: 'None',
        notNeeded: 'Not needed (Company-wide)',
        redundant: 'Already covered by inherited access',
        none: 'No direct grant',
        inherited: { COMPANY: 'Company · {{level}}', PROJECT: 'Project · {{level}}' },
        levels: { VIEWER: 'Viewer', EDITOR: 'Editor' },
        directLevelFor: 'Direct access level for {{name}}',
        remove: 'Remove direct presentation access for {{name}}',
        pages: 'Presentation access pages',
      },
      companySettings: {
        retry: 'Try again',
        system: 'System',
        errors: {
          title: 'Company settings could not be loaded',
          description: 'Try again or verify your access.',
          conflict: 'Company settings changed elsewhere. Reload before saving.',
          save: 'Company settings could not be saved.',
        },
        identity: {
          title: 'Company',
          description: 'Manage your Company identity and workspace settings.',
          name: 'Company name',
          slug: 'Company slug',
          slugHelp: 'Used in management URLs. Slug changes are not supported in V1.',
          workspaceDescription: 'Description',
          save: 'Save changes',
          saved: 'Company settings saved.',
        },
        audit: {
          ...auditLogCopy.en,
          title: 'Audit log',
          description: 'Review durable administrative and resource activity.',
          time: 'Time',
          actor: 'Actor',
          action: 'Action',
          resource: 'Resource',
          request: 'Request ID',
          results_one: '{{count}} event',
          results_other: '{{count}} events',
          pages: 'Audit event pages',
        },
        security: {
          title: 'Security',
          description: 'Review enforced security policy and recent security events.',
          strongMfa: 'Strong MFA for privileged members',
          strongMfaHelp:
            'Owners and Admins must use a passkey or authenticator app for privileged operations. Email codes remain fallback only.',
          required: 'Required',
          recent: 'Recent security events',
          event: 'Event',
          severity: 'Severity',
          severityLevels: {
            INFO: 'Info', WARNING: 'Warning', HIGH: 'High', CRITICAL: 'Critical',
          },
          pages: 'Security event pages',
        },
      },
      appearanceStudio: {
        styleDirection: {
          heading: 'Style direction',
          intro:
            'Explore the Styles this Project is allowed to use, with their compatibility. This is guidance only.',
          open: 'Explore Styles',
          close: 'Hide Styles',
          note:
            'Choosing a Style here does not change this presentation. Its appearance changes only when you Apply the controls above. Slide layouts and content are never changed by a Style.',
          compatibility: {
            heading: 'Compatibility with this Presentation',
            noSelection:
              'Select a Style to see how it fits this Presentation. Nothing changes until you Apply the controls above.',
            checking: 'Checking compatibility…',
            status: {
              'eligible-recommendation': 'Structurally compatible',
              'preview-only': 'Concept only',
              incompatible: 'Not compatible',
              'missing-or-stale-style': 'Style no longer current',
            },
            statusDetail: {
              'eligible-recommendation':
                'Every Slide uses a template version this Style recommends. This is a structural check only.',
              'preview-only': 'No template recommendation is evidenced for this Style yet.',
              incompatible:
                'This Style has no available recommendation, or some Slides use a different template version.',
              'missing-or-stale-style':
                'This exact version and fingerprint is not in the current Project catalog. Select the Style again.',
            },
            notApplied: 'Not applied. Not ready to publish.',
            visualReadiness: 'Visual readiness: unknown, not verified',
            capability: 'Capability requirements: not declared by this Style',
            slidesPinned: 'Slides on another template version: {{count}} of {{total}}',
            designKitRestricted:
              'Some templates for this Style are restricted by the Project Design Kit.',
            recommended: 'Recommended template versions',
            slidesUsing: 'Slides using this version: {{count}}',
            alternatives: 'Alternative Styles',
            altStatus: {
              'eligible-recommendation': 'Structurally compatible',
              'preview-only': 'Concept only',
              incompatible: 'Not compatible',
            },
            reasonsHeading: 'Why',
            reasons: {
              STYLE_IDENTITY_MISSING_OR_STALE:
                'This exact version and fingerprint is not in the current Project catalog.',
              STYLE_HAS_NO_EVIDENCED_RECOMMENDATION:
                'No template recommendation is evidenced for this Style.',
              NO_ELIGIBLE_RECOMMENDED_TEMPLATE:
                'No recommended template is currently eligible for this Style.',
              RECOMMENDED_TEMPLATE_UNAVAILABLE_OR_RESTRICTED:
                'The recommended template is unavailable or restricted by current policy.',
              EXISTING_SLIDES_USE_OTHER_TEMPLATE_VERSIONS:
                'Some Slides use a different template version than the recommendation.',
              VISUAL_QA_NOT_VERIFIED: 'Visual quality has not been verified for this Style.',
              STYLE_CAPABILITY_REQUIREMENTS_NOT_DECLARED:
                'This Style does not declare capability requirements.',
            },
            viewOnly:
              'View only: you can check compatibility but cannot change this Presentation’s appearance.',
            unavailable:
              'Compatibility is unavailable: you cannot access this Project’s Style catalog or this Presentation. This is not a verdict on the Style.',
            forbidden: 'You do not have permission to check compatibility for this Presentation.',
            session: 'Sign in again to check compatibility.',
            stale: 'The selected Style changed or is no longer current. Select it again.',
            failed: 'Compatibility could not be checked. Try again.',
            retry: 'Retry',
          },
        },
        title: 'Appearance',
        description:
          'Choose how this presentation looks. Applying updates the draft and the next publish; releases that are already published never change.',
        errorTitle: 'Appearance could not be loaded',
        retry: 'Try again',
        saveError: 'We couldn’t save the appearance. Try again.',
        applied: 'Appearance applied.',
        status: { saved: 'Saved', unsaved: 'Unsaved changes' },
        source: {
          heading: 'Theme source',
          inherit: 'Use the Project theme',
          inheritHint: 'Follows the Project theme, including later changes to it.',
          custom: 'Customize for this presentation',
          customHint:
            'Only the options you change are overridden; the rest keep following the Project theme.',
          inheritedSwatches: 'Colors inherited from the Project theme',
        },
        presets: {
          heading: 'Presets',
          names: {
            'studio-light': 'Studio light',
            midnight: 'Midnight',
            editorial: 'Editorial',
            forest: 'Forest',
            ocean: 'Ocean',
            sunset: 'Sunset',
            mono: 'Mono',
            'persian-calm': 'Persian calm',
          },
        },
        colors: {
          heading: 'Colors',
          primary: 'Primary',
          primaryContrast: 'Text on primary',
          secondary: 'Secondary',
          accent: 'Accent',
          background: 'Background',
          surface: 'Surface',
          text: 'Text',
          textMuted: 'Muted text',
          hexFor: '{{label}} hex value',
          hexInvalid: 'Enter a color like #1A2B3C.',
          useInherited: 'Use the inherited {{label}}',
          inherited: 'Inherited',
          customized: 'Customized',
        },
        fonts: {
          heading: 'Heading font',
          body: 'Body font',
          search: 'Search fonts',
          noResults: 'No matching fonts',
          groups: {
            latinSans: 'Latin Sans',
            latinSerif: 'Latin Serif',
            latinDisplay: 'Latin Display',
            latinMono: 'Latin Monospace',
            persianSans: 'Persian / Arabic Sans',
            persianSerif: 'Persian / Arabic Serif',
            persianDisplay: 'Persian Display / Calligraphy',
          },
          inherited: 'Inherited ({{family}})',
          names: {
            inter: 'Inter',
            'ibm-plex-sans': 'IBM Plex Sans',
            'space-grotesk': 'Space Grotesk',
            'source-serif-4': 'Source Serif 4',
            'playfair-display': 'Playfair Display',
            vazirmatn: 'Vazirmatn',
            'noto-sans-arabic': 'Noto Sans Arabic',
            'noto-naskh-arabic': 'Noto Naskh Arabic',
            roboto: 'Roboto',
            'open-sans': 'Open Sans',
            montserrat: 'Montserrat',
            'dm-sans': 'DM Sans',
            manrope: 'Manrope',
            'plus-jakarta-sans': 'Plus Jakarta Sans',
            'nunito-sans': 'Nunito Sans',
            raleway: 'Raleway',
            figtree: 'Figtree',
            lora: 'Lora',
            merriweather: 'Merriweather',
            oswald: 'Oswald',
            fraunces: 'Fraunces',
            'jetbrains-mono': 'JetBrains Mono',
            estedad: 'Estedad',
            cairo: 'Cairo',
            changa: 'Changa',
            'el-messiri': 'El Messiri',
            mada: 'Mada',
            'reem-kufi': 'Reem Kufi',
            amiri: 'Amiri',
            'markazi-text': 'Markazi Text',
            'noto-nastaliq-urdu': 'Noto Nastaliq Urdu',
            lalezar: 'Lalezar',
            katibeh: 'Katibeh',
            gulzar: 'Gulzar',
          },
        },
        style: {
          heading: 'Shape and style',
          inherited: 'Inherited',
          radius: 'Corner radius',
          neutral: 'Neutral tone',
          surface: 'Surface style',
          radii: { sharp: 'Sharp', soft: 'Soft', round: 'Round' },
          neutrals: { slate: 'Slate', zinc: 'Zinc', stone: 'Stone', warm: 'Warm', cool: 'Cool' },
          surfaces: { flat: 'Flat', raised: 'Raised', outlined: 'Outlined' },
        },
        preview: {
          heading: 'Live preview',
          slide: 'Previewed slide',
          slideOption: '{{number}}. {{title}}',
          noSlides: 'Add a slide to preview this appearance.',
          note: 'The preview shows your unsaved draft. It is not affected by the app’s light or dark mode.',
        },
        actions: {
          group: 'Appearance actions',
          shuffle: 'Shuffle',
          reset: 'Reset to inherited',
          discard: 'Discard',
          apply: 'Apply',
        },
        readOnly: {
          title: 'View only',
          description: 'You can see this appearance but need Editor access to change it.',
        },
        conflict: {
          title: 'Appearance changed elsewhere',
          description: 'This appearance was updated by someone else while you were editing.',
          useLatest: 'Use latest version',
          keepMine: 'Keep my changes',
        },
        support: {
          title: 'Appearance may not fully apply',
          unknown:
            'Some templates in this presentation do not say which appearance options they support, so the result may differ from the preview.',
          unsupported: 'The previewed slide’s template does not apply: {{options}}.',
          options: {
            colors: 'colors',
            fonts: 'fonts',
            radius: 'corner radius',
            surface: 'surface style',
          },
        },
        contrast: {
          title: 'Low contrast',
          text: 'Text on the background is {{ratio}}:1. WCAG AA recommends at least 4.5:1.',
          textMuted:
            'Muted text on the background is {{ratio}}:1. WCAG AA recommends at least 4.5:1.',
          primary: 'Text on the primary color is {{ratio}}:1. WCAG AA recommends at least 4.5:1.',
        },
        leave: {
          title: 'Discard unsaved appearance changes?',
          description: 'You have changes that have not been applied. Leaving now discards them.',
          stay: 'Keep editing',
          discard: 'Discard and leave',
        },
      },
      themeEditor: {
        title: 'Project theme',
        description:
          'Create immutable theme versions for presentation colors, typography and motion.',
        errorTitle: 'Project theme could not be loaded',
        conflict: 'The Project changed elsewhere. Reload before creating a new Theme Version.',
        saveError: 'The Theme Version could not be created.',
        createVersion: 'Create Theme Version',
        previewEyebrow: 'Live preview',
        previewTitle: 'A clear story, consistently branded',
        previewBody:
          'Templates consume semantic tokens and reflow for every presentation viewport.',
        previewAction: 'Explore the story',
        history: 'Version history',
        noVersions: 'No Theme Versions yet. The preview uses Slider’s safe default theme.',
        version: 'Version {{version}}',
        createdBy: 'Created by {{name}}',
        createdByLabel: 'Created by',
        swatchFor: '{{label}} swatch',
        current: 'Project default',
        colors: {
          primary: 'Primary',
          secondary: 'Secondary',
          accent: 'Accent',
          background: 'Background',
          surface: 'Surface',
          text: 'Text',
          textMuted: 'Muted text',
        },
      },
      templateUse: templateUseCopy.en,
      templateCatalog: {
        eyebrow: 'CREATE FASTER',
        title: 'Templates',
        description: 'Browse trusted, responsive presentation patterns and their capabilities.',
        heroBadge: 'New templates weekly',
        heroCard: 'Turn ideas into impact',
        taxonomy: {
          categories: {
            story: 'Story', metrics: 'Metrics', media: 'Media',
            marketing: 'Marketing', general: 'General', custom: 'Custom',
            comparison: 'Comparison', timeline: 'Timeline',
          },
          tags: {
            cover: 'Cover', intro: 'Intro', story: 'Story',
            media: 'Media', narrative: 'Narrative', split: 'Split',
            'executive-summary': 'Executive summary', kpi: 'KPI', metrics: 'Metrics',
            brand: 'Brand', hero: 'Hero', data: 'Data',
            comparison: 'Comparison', timeline: 'Timeline',
            chart: 'Chart', visual: 'Visual', product: 'Product',
          },
        },
        highlights: {
          customizable: 'Fully customizable',
          performance: 'Designed for performance',
          time: 'Save hours of work',
        },
        search: 'Search templates',
        searchPlaceholder: 'Search templates…',
        categoryLabel: 'Filter by category',
        allCategories: 'All categories',
        clearFilters: 'Clear filters',
        useTemplate: 'Use template',
        preview: 'Preview {{name}}',
        previewUnavailable: 'Preview unavailable',
        version: 'Version {{version}}',
        cardActions: 'Template actions for {{name}}',
        statuses: {
          ACTIVE: 'Active', DRAFT: 'Draft', RETIRED: 'Retired',
          REJECTED: 'Rejected', PUBLISHED: 'Published',
        },
        duplicate: 'Duplicate',
        delete: 'Delete',
        moreTags: '{{count}} more tag(s)',
        errorTitle: 'Templates could not be loaded',
        empty: 'No templates found',
        emptyDescription: 'Try another search or category.',
        results_one: '{{count}} template',
        results_other: '{{count}} templates',
        showing: 'Showing {{count}} of {{total}} templates',
        pages: 'Template pages',
        back: 'Back to templates',
        edit: 'Edit template',
        editAria: 'Edit {{name}}',
        bestFor: 'Best for',
        capabilities: 'Capabilities',
        fields: 'Editor fields',
        fieldNameHeader: 'Field',
        fieldKeyHeader: 'Key',
        fieldTypeHeader: 'Type',
        required: 'required',
        createDynamicHtml: 'Dynamic HTML Template',
        addTemplate: 'Add new template',
        addTemplateManual: 'Create manually',
        addTemplateFromFile: 'Import from a file',
        capabilityNames: {
          actions: 'Actions',
          chart: 'Chart',
          'interactive-detail': 'Interactive',
          media: 'Media',
          narration: 'Narration',
          'semantic-visibility': 'Responsive',
        },
        fieldTypeNames: {
          asset: 'Image or media',
          boolean: 'Toggle',
          currency: 'Currency',
          date: 'Date',
          enum: 'Choice',
          link: 'Link',
          list: 'List',
          number: 'Number',
          percentage: 'Percentage',
          text: 'Text',
          textarea: 'Long text',
        },
        previewViewport: 'Preview viewport',
        parametersHeading: 'Page parameters (JSON)',
      },
      templateCategories: {
        actions: 'Actions',
        allTypes: 'All types',
        byName: 'Name (A–Z)',
        clearFilters: 'Clear filters',
        confirmDeleteDescription:
          'Delete the empty category “{{name}}”? This action cannot be undone.',
        confirmDeleteTitle: 'Delete category?',
        deleteCategory: 'Delete category {{name}}',
        editCategory: 'Edit category {{name}}',
        filterType: 'Filter category type',
        mostTemplates: 'Most templates',
        newest: 'Updated (newest)',
        noMatches: 'No matching categories',
        noMatchesDescription: 'Try a different search or clear the filters.',
        oldest: 'Updated (oldest)',
        searchLabel: 'Search categories',
        searchPlaceholder: 'Search categories…',
        sortLabel: 'Sort categories',
        summaryLabel: 'Category summary',
        cancel: 'Cancel',
        create: 'Create category',
        createDescription:
          'Keep template filters tidy with intentional names, order and descriptions.',
        createTitle: 'New category',
        creating: 'Creating…',
        delete: 'Delete',
        deleteBlocked: 'Only empty categories can be deleted.',
        description: 'Organize Template Registry categories used by authors and template cards.',
        descriptionField: 'Description',
        descriptionPlaceholder: 'Short internal description',
        edit: 'Edit',
        empty: 'No categories yet',
        emptyDescription: 'Create the first category to organize templates.',
        errorDescription:
          'The category name may already exist, or the category is still used by templates.',
        errorTitle: 'Category change could not be saved',
        eyebrow: 'Template system',
        loadError: 'Categories could not be loaded',
        name: 'Name',
        namePlaceholder: 'e.g. Narrative',
        noDescription: 'No description',
        save: 'Save',
        custom: 'Custom',
        systemReadOnly: 'Built in, read only',
        system: 'System',
        sortOrder: 'Order',
        templateCount_one: '{{count}} template',
        templateCount_other: '{{count}} templates',
        templates: 'Templates',
        title: 'Categories',
        totalCategories: 'Categories',
        totalTemplates: 'Assigned templates',
        updated: 'Updated',
      },
      templateImport: {
        title: 'Import template from a file',
        description:
          'Upload a .zip containing one HTML file and the images it references. We’ll import it and open the template preview.',
        errors: {
          notAZip: 'Please choose a .zip file.',
          TEMPLATE_IMPORT_INVALID_ARCHIVE: 'This file could not be read as a template archive.',
          TEMPLATE_IMPORT_NO_HTML: 'No HTML file was found in the archive.',
          TEMPLATE_IMPORT_TOO_LARGE: 'This archive is larger than the supported limit.',
          default: 'The file could not be imported. Please try again.',
        },
        status: {
          processing: 'Reading archive…',
          uploading: 'Uploading… {{progress}}%',
        },
      },
      templateFork: {
        createAction: 'Create fork',
        createTitle: 'Create a fork',
        createDescription:
          'Make an independent, editable draft of {{name}} (version {{version}}). The source template is never changed.',
        nameLabel: 'Fork name',
        namePlaceholder: 'e.g. Quarterly results – regional',
        createSubmit: 'Create fork',
        cancel: 'Cancel',
        created: 'Fork created. You can edit it as a draft.',
        createErrors: {
          default: 'The fork could not be created. Try again.',
          ACCESS_DENIED: 'You do not have permission to create forks.',
          TEMPLATE_SOURCE_NOT_FORKABLE: 'This template version cannot be forked.',
          RESOURCE_NOT_FOUND: 'This template version no longer exists.',
          IDEMPOTENCY_OPERATION_IN_PROGRESS: 'This fork is still being created. Try again shortly.',
        },
        ...templateForkRejectionCopy.en.list,
        draftRevision: 'Revision {{revision}}',
        draftSource: 'Forked from {{name}} · v{{version}}',
        draftUpdated: 'Updated {{date}}',
        continueDraft: 'Continue editing',
        editor: {
          back: 'Back to templates',
          title: 'Fork draft',
          loadError: 'This draft could not be loaded.',
          notFound: 'This draft was not found, or you do not have access to it.',
          statusEditing: 'Draft · revision {{revision}}',
          statusPublished: 'Published · revision {{revision}}',
          statusAbandoned: 'Abandoned · revision {{revision}}',
          unsaved: 'Unsaved changes',
          readOnlyNotice: 'This draft is no longer open for editing.',
          viewOnlyNotice: 'You can view this draft but your role does not allow editing it.',
          save: 'Save draft',
          saved: 'Draft saved as revision {{revision}}.',
          saveErrors: {
            default: 'The draft could not be saved.',
            ACCESS_DENIED: 'You do not have permission to edit this draft.',
            TEMPLATE_DRAFT_INVALID:
              'The draft is not valid. Fix the problems below and save again.',
            TEMPLATE_DRAFT_STATE_CONFLICT: 'This draft is no longer open for editing.',
            TEMPLATE_REVISION_REJECTED:
              'This revision was rejected and nothing has changed. Edit the source first, then save to create a new revision.',
          },
          invalidJson: 'The data (JSON) must be a valid JSON object.',
          conflictTitle: 'This draft changed elsewhere',
          conflictDescription:
            'Someone saved a newer revision, so your changes were not saved. Reload the latest revision to continue; your unsaved edits will be replaced.',
          conflictReload: 'Reload latest revision',
          problemsTitle: 'Problems to fix',
          validationTitle: 'Validation errors',
          warningsTitle: 'Warnings',
          ...templateForkRejectionCopy.en.editor,
          sourceHeading: 'Template source',
          sourceNotice:
            'Fields and repeaters stay as in the source version. Only HTML, CSS and sample data are edited here.',
          previewHeading: 'Preview',
          previewTitle: 'Draft preview',
          previewBadge: 'Default theme',
          previewDisclaimer:
            'Rendered with the default theme. The Project Design Kit theme is not applied, so this is not a production preview.',
          previewRevision: 'Preview of saved revision {{revision}}',
          previewUnsavedNote: 'Unsaved edits are not shown until you save.',
          previewRefresh: 'Refresh preview',
          previewLoading: 'Preparing preview…',
          previewExpired: 'This preview link has expired. Refresh the preview to load it again.',
          previewUnavailable: 'No preview is available for this revision.',
          previewError: 'The preview could not be created.',
          previewIssuesTitle: 'Preview could not be rendered',
          approvalHeading: 'Review and publish',
          approvalNone: 'This revision has not been approved.',
          approvalCurrent: 'Revision {{revision}} approved on {{date}}.',
          approvalFingerprint: 'Fingerprint {{fingerprint}}…',
          approvalNeedsSave: 'Save your changes before approving.',
          approvalNeedsValid: 'Fix the validation errors before approving.',
          approvalNeedsPublisher: 'Only a Template Publisher can approve and publish.',
          reviewState: {
            PENDING: 'Pending review',
            APPROVED: 'Approved',
            REJECTED: 'Rejected',
          },
          diagnosticsCount_one: '{{count}} item',
          diagnosticsCount_other: '{{count}} items',
          reject: 'Reject revision',
          rejectTitle: 'Reject this exact revision?',
          rejectDescription:
            'Rejecting revision {{revision}} (fingerprint {{fingerprint}}…) means it cannot be approved or published. Saving an edit creates a new revision that returns to pending review.',
          rejectReasonLabel: 'Reason (optional)',
          rejectConfirm: 'Reject revision',
          rejected: 'Revision {{revision}} rejected.',
          rejectionCurrent: 'Revision {{revision}} rejected on {{date}}.',
          rejectionReason: 'Reason: {{reason}}',
          rejectionNext: 'Edit and save the draft to create a new revision for review.',
          rejectErrors: {
            default: 'The revision could not be rejected.',
            ACCESS_DENIED: 'You do not have permission to review templates.',
            TEMPLATE_DRAFT_CONFLICT:
              'The draft changed. Reload the latest revision before rejecting.',
            TEMPLATE_APPROVAL_STALE:
              'The draft changed since you opened it. Reload and review again.',
            TEMPLATE_APPROVAL_CONFLICT: 'Someone else already rejected this revision.',
            TEMPLATE_DRAFT_STATE_CONFLICT: 'This draft is no longer open for review.',
          },
          approve: 'Approve revision',
          approveTitle: 'Approve this exact revision?',
          approveDescription:
            'Approving revision {{revision}} (fingerprint {{fingerprint}}…) allows it to be published. Any later edit invalidates this approval.',
          approveConfirm: 'Approve',
          approved: 'Revision {{revision}} approved.',
          approveErrors: {
            default: 'The revision could not be approved.',
            ACCESS_DENIED: 'You do not have permission to approve templates.',
            TEMPLATE_DRAFT_CONFLICT:
              'The draft changed. Reload the latest revision before approving.',
            TEMPLATE_APPROVAL_STALE:
              'The draft changed since you opened it. Reload and review again.',
            TEMPLATE_DRAFT_INVALID: 'The draft is not valid and cannot be approved.',
            TEMPLATE_APPROVAL_CONFLICT: 'This revision already has a conflicting approval.',
            TEMPLATE_REVISION_REJECTED:
              'This revision was rejected. Edit it to create a new revision for review.',
          },
          publish: 'Publish as new version',
          publishTitle: 'Publish this approved revision?',
          publishDescription:
            'This creates a new active template version from approved revision {{revision}}. The source template and existing slides are not changed.',
          publishConfirm: 'Publish',
          published: 'Published as version {{version}}.',
          publishErrors: {
            default: 'The draft could not be published.',
            ACCESS_DENIED: 'You do not have permission to publish templates.',
            TEMPLATE_DRAFT_CONFLICT:
              'The draft changed. Reload the latest revision and review again.',
            TEMPLATE_APPROVAL_REQUIRED:
              'This revision must be approved before it can be published.',
            TEMPLATE_APPROVAL_STALE: 'The approval no longer matches the draft. Approve it again.',
            TEMPLATE_PUBLISH_AUTHORIZATION_CHANGED:
              'Your publishing permission changed. Reload and try again.',
            TEMPLATE_DRAFT_STATE_CONFLICT: 'This draft is no longer open for publishing.',
            TEMPLATE_REVISION_REJECTED:
              'This revision was rejected. Edit it to create a new revision for review.',
          },
          publishedHeading: 'Published',
          publishedVersion: 'Published as version {{version}}',
          viewPublished: 'View published template',
          provenanceHeading: 'Provenance',
          provenanceSource: 'Forked from {{templateId}} · version {{version}}',
          provenanceCreated: 'Created {{date}}',
          provenancePublished: 'Published versions',
          provenanceNone: 'Not published yet.',
          provenanceVersion: 'Version {{version}}',
        },
      },
      templateStudio: {
        title: 'Create Dynamic HTML template',
        editTitle: 'Edit template',
        editNotice:
          'Saving updates this template for everyone — any presentation slide already using it will reflect the change.',
        editSaveSuccess: 'Template updated.',
        back: 'Back to templates',
        detailsHeading: 'Details',
        nameLabel: 'Name',
        namePlaceholder: 'e.g. Applications grid',
        categoryLabel: 'Category',
        categoryPlaceholder: 'e.g. story',
        markupHeading: 'Template markup',
        htmlLabel: 'HTML',
        cssLabel: 'CSS',
        jsonLabel: 'Data (JSON)',
        structureHeading: 'Editable structure',
        fieldsLabel: 'Fields',
        elementsLabel: 'Elements',
        fieldsHelp:
          'Every field an author can edit without touching HTML/CSS/JSON. Use one [] for a repeated path, e.g. cards[].title.',
        compiledHelp:
          'This template is code-backed, so its elements are shown as template metadata.',
        fieldIndex: 'Field {{index}}',
        fieldPathLabel: 'Field path',
        fieldPathPlaceholder: 'e.g. headline or cards[].title',
        fieldLabelLabel: 'Field label',
        fieldLabelPlaceholder: 'e.g. Headline',
        fieldTypeLabel: 'Field type',
        fieldType: { text: 'Text', longText: 'Long text', asset: 'Image', link: 'Link' },
        addField: 'Add field',
        removeField: 'Remove field',
        chooseAsset: 'Choose asset',
        replaceAsset: 'Replace',
        removeAsset: 'Remove',
        noAssetSelected: 'No image or icon selected',
        extractTextFields: 'Extract text from HTML',
        extractTextFieldsHelp:
          'Pull the title, meta description and other plain text out of the HTML into editable fields and the data JSON.',
        extractTextFieldsNone:
          'No new text was found to extract — everything visible is already a field.',
        extractTextFieldsAdded: 'Added {{count}} field(s) from the HTML text.',
        fieldValueLabel: 'Current value (from data JSON)',
        fieldValueEmpty: '(empty)',
        repeatersLabel: 'Repeatable groups',
        repeatersHelp:
          'Declare which repeated array (e.g. "cards") an author can Add/Delete/Duplicate/Reorder items in, and its min/max item count.',
        repeaterIndex: 'Repeater {{index}}',
        repeaterLabelLabel: 'Repeater label',
        repeaterLabelPlaceholder: 'e.g. Cards',
        repeaterPathLabel: 'Repeater path',
        repeaterPathPlaceholder: 'e.g. cards',
        repeaterMinLabel: 'Min items',
        repeaterMaxLabel: 'Max items',
        addRepeater: 'Add repeater',
        removeRepeater: 'Remove repeater',
        warningsTitle: 'Some fields may need attention',
        previewTitle: 'Preview',
        openLargePreview: 'Open large preview',
        exitLargePreview: 'Exit large preview',
        previewEmpty: 'Fix the errors below to see a preview.',
        errorsTitle: 'This template cannot be saved yet',
        save: 'Save template',
        saveSuccess: 'Template saved. It is ready to use in Add Slide.',
        saveFailed: 'Could not save this template',
        themeTokensLabel: 'Theme tokens',
        themeTokensHelp:
          'Use these Presentation theme variables in your CSS so the template automatically adapts to each Presentation, and to any per-slide override an author sets later.',
        copyToken: 'Copy',
        copiedToken: 'Copied to clipboard',
      },
      assets: {
        title: 'Assets',
        description: 'Manage images, video, audio and other reusable media for your Company.',
        projectEyebrow: 'Project assets',
        upload: 'Upload assets',
        uploading: 'Uploading…',
        uploadingProgress: 'Uploading {{done}} of {{total}}…',
        batchUploaded_one: '{{count}} file uploaded. Processing continues in the background.',
        batchUploaded_other: '{{count}} files uploaded. Processing continues in the background.',
        batchFailed_one: '{{count}} file failed: {{names}}.',
        batchFailed_other: '{{count}} files failed: {{names}}.',
        selectAllOnPage: 'Select all on page',
        selectAsset: 'Select {{title}}',
        selectedCount: '{{count}} selected',
        clearSelection: 'Clear',
        deleteSelected: 'Delete selected',
        bulkDeleteTitle_one: 'Delete {{count}} asset?',
        bulkDeleteTitle_other: 'Delete {{count}} assets?',
        bulkDeleteDescription:
          'Selected assets are removed from the library and hidden from pickers. Assets kept by published releases cannot be deleted and stay selected.',
        bulkDeleteConfirm_one: 'Delete {{count}} asset',
        bulkDeleteConfirm_other: 'Delete {{count}} assets',
        bulkDeleting: 'Deleting…',
        bulkDeletePartial_one: '{{count}} asset could not be deleted and is still selected.',
        bulkDeletePartial_other: '{{count}} assets could not be deleted and are still selected.',
        processing: 'Upload complete. Processing…',
        uploadFailed: 'Upload failed',
        search: 'Search assets',
        searchPlaceholder: 'Search assets…',
        view: 'Asset view',
        grid: 'Grid',
        list: 'List',
        companyScope: 'Company',
        empty: 'No assets yet',
        filteredEmpty: 'No assets match these filters',
        emptyDescription: 'Upload images, video, audio and other media to use in presentations.',
        errorTitle: 'Assets could not be loaded',
        results_one: '{{count}} asset',
        results_other: '{{count}} assets',
        pages: 'Asset pages',
        usages_one: '{{count}} use',
        usages_other: '{{count}} uses',
        back: 'Back to assets',
        detailEyebrow: 'Asset detail',
        replace: 'Replace',
        download: 'Download original',
        name: 'Asset name',
        descriptionField: 'Description',
        altText: 'Alt text',
        tags: 'Tags (comma separated)',
        versions: 'Versions',
        version: 'Version {{version}}',
        usedIn: 'Used in',
        noUsages: 'No presentation draft, published release or theme references this asset yet.',
        processingFailed: 'Processing failed. Upload the file again.',
        processingUnavailable: 'Processing status could not be checked.',
        processingTimedOut:
          'Processing is taking longer than expected. Check again or dismiss this message.',
        checkAgain: 'Check again',
        dismiss: 'Dismiss',
        delete: 'Delete asset',
        deleteTitle: 'Delete this asset?',
        deleteDescription:
          'The asset is removed from the library and hidden from pickers. It can no longer be added to new content.',
        deleteInUse_one:
          'This asset is used in {{count}} place. Deleting it hides it from the library and pickers. Published releases and other retained references keep what they already use.',
        deleteInUse_other:
          'This asset is used in {{count}} places. Deleting it hides it from the library and pickers. Published releases and other retained references keep what they already use.',
        deleteConfirm: 'Delete asset',
        deleteBlocked: 'Published content keeps this asset, so it cannot be deleted.',
        deleteForbidden: 'You do not have permission to delete this asset.',
        deleteUnavailable: 'This asset is no longer available.',
        deleteFailed: 'The asset could not be deleted. Try again.',
        details: 'Details',
        unit: { B: 'B', KB: 'KB', MB: 'MB' },
        editDetails: 'Edit details',
        factType: 'Type',
        factDimensions: 'Dimensions',
        factOriginalFile: 'Original file',
        factOriginalSize: 'Original size',
        factDelivery: 'Used in presentations as',
        factScope: 'Scope',
        factCreated: 'Added',
        factUpdated: 'Updated',
        createdBy: '{{name}} · {{date}}',
        deliveryOriginal: 'The original file (no optimized copy)',
        deliveryWebpLossless: 'Lossless WebP · {{size}}',
        deliveryWebpLossy: 'High-quality WebP · {{size}}',
        deliverySaved: '{{percent}}% smaller than the original',
        currentVersion: 'Current',
        versionOriginalSize: 'Original {{size}}',
        versionUploaded: 'Uploaded {{date}}',
        downloadVersion: 'Download original of version {{version}}',
        downloadShort: 'Download',
        usedInSlide: '{{presentation}} · slide {{number}}',
        untitledPresentation: 'Untitled presentation',
        usageVersion: 'version {{version}}',
        usageKind: {
          DRAFT: 'Draft',
          RELEASE: 'Published release',
          THEME: 'Theme',
          KNOWLEDGE: 'Knowledge',
        },
        status: {
          PENDING_UPLOAD: 'Uploading',
          UPLOADED: 'Processing',
          VALIDATING: 'Processing',
          PROCESSING: 'Processing',
          READY: 'Ready',
          FAILED: 'Failed',
          QUARANTINED: 'Blocked',
          DELETED: 'Deleted',
        },
        kind: {
          IMAGE: 'Image',
          VIDEO: 'Video',
          AUDIO: 'Audio',
          LOGO: 'Logo',
          ICON: 'Icon',
          LOTTIE: 'Animation',
          MODEL_3D: '3D model',
          DOCUMENT: 'Document',
        },
        ...assetFoldersCopy.en,
        ...assetUploadCopy.en,
      },
      settings: { title: 'Company settings', navigationLabel: 'Company settings' },
      aiProvider: aiProviderCopy.en,
      security: {
        account: 'Account',
        title: 'Security',
        description: 'Manage strong authentication, recovery options and signed-in devices.',
        loading: 'Loading account security',
        loadingSessions: 'Loading signed-in devices',
        errorTitle: 'Security settings could not be loaded',
        errorDescription: 'Sign in again or try later.',
        sessionsErrorTitle: 'Sessions could not be loaded',
        passkeys: 'Passkeys',
        passkeysDescription: 'Use biometrics or a device PIN for phishing-resistant sign-in.',
        addPasskey: 'Add passkey',
        noPasskeys: 'No passkeys added.',
        thisDevice: 'This device',
        passkeyError: 'The passkey could not be added.',
        authenticator: 'Authenticator app',
        authenticatorDescription: 'Use a time-based code from your authenticator app.',
        addAuthenticator: 'Add authenticator',
        noAuthenticator: 'No authenticator app added.',
        authenticatorApp: 'Authenticator app',
        recoveryCodes: 'Recovery codes',
        recoveryRemaining: '{{count}} unused codes remaining',
        regenerate: 'Generate new codes',
        saveCodes: 'Save these codes now. Existing recovery codes are no longer valid.',
        savedCodes: 'I saved these codes',
        sessions: 'Sessions',
        signedInDevices: 'Signed-in devices',
        sessionsDescription: 'Review devices that are currently signed in.',
        signOutOthers: 'Sign out other sessions',
        currentSession: 'This device',
        unknownDevice: 'Unknown device',
        current: 'Current',
        signOut: 'Sign out',
        signOutSessionFor: 'Sign out session: {{device}}',
        lastSeen: 'Last active {{date}}',
        added: 'Added {{date}}',
        remove: 'Remove',
        removePasskeyFor: 'Remove passkey: {{name}}',
        removeAuthenticatorAdded: 'Remove authenticator added {{date}}',
      },
      team: {
        membersTitle: 'Members',
        membersDescription: 'Manage company roles and access to projects and presentations.',
        invitationsTitle: 'Invitations',
        invitationsDescription: 'Track pending invitations and manage their lifecycle.',
        inviteMembers: 'Invite members',
        newInvitation: 'New invitation',
        newInvitationDescription: 'Invite one person and choose the access they should receive.',
        searchMembers: 'Search members',
        searchMembersPlaceholder: 'Search name or email…',
        searchInvitations: 'Search invitations',
        searchInvitationsPlaceholder: 'Search invited email…',
        member: 'Member',
        email: 'Email',
        emailRequired: 'Enter an email address.',
        role: 'Role',
        access: 'Access',
        status: 'Status',
        actions: 'Actions',
        expires: 'Expires',
        allRoles: 'All roles',
        allAccess: 'All access',
        allStatuses: 'All statuses',
        roles: { OWNER: 'Owner', ADMIN: 'Admin', EDITOR: 'Editor', VIEWER: 'Viewer' },
        scopes: { ALL: 'All company resources', RESTRICTED: 'Restricted' },
        statuses: { ACTIVE: 'Active', SUSPENDED: 'Suspended' },
        invitationStatuses: {
          PENDING: 'Pending',
          ACCEPTED: 'Accepted',
          EXPIRED: 'Expired',
          REVOKED: 'Revoked',
        },
        emailDelivery: 'Invitation email',
        // "Accepted by provider" is not "delivered": the provider took the message,
        // and nothing here confirms it reached an inbox.
        deliveryStatuses: {
          QUEUED: 'Queued',
          ACCEPTED: 'Accepted by email provider',
          RETRYING: 'Retrying',
          FAILED: 'Failed',
          SUPPRESSED: 'Not sent',
          UNKNOWN: 'Unknown',
        },
        deliveryDescriptions: {
          QUEUED: 'Waiting to be sent.',
          ACCEPTED: 'Handed to the email provider. Inbox delivery is not confirmed.',
          RETRYING: 'The last attempt failed. Another attempt is scheduled.',
          FAILED: 'Every attempt failed. Resend the invitation to try again.',
          SUPPRESSED: 'Email sending is turned off, so no email was sent.',
          UNKNOWN: 'The email state was not recorded for this invitation.',
        },
        manageAccess: 'Manage access',
        loadingMembers: 'Loading members',
        loadingMember: 'Loading member access',
        loadingInvitations: 'Loading invitations',
        noMembers: 'No members yet',
        noMembersMatch: 'No members match these filters',
        noMembersDescription: 'Invite a teammate or change the current filters.',
        noInvitations: 'No invitations yet',
        noInvitationsDescription: 'Invite a teammate when you are ready to collaborate.',
        memberResults_one: '{{count}} member',
        memberResults_other: '{{count}} members',
        invitationResults_one: '{{count}} invitation',
        invitationResults_other: '{{count}} invitations',
        memberPages: 'Member pages',
        invitationPages: 'Invitation pages',
        previous: 'Previous',
        next: 'Next',
        page: 'Page {{page}}',
        resend: 'Resend',
        revoke: 'Revoke',
        resendFor: 'Resend invitation to {{email}}',
        revokeFor: 'Revoke invitation for {{email}}',
        revokeConfirmationTitle: 'Revoke invitation?',
        revokeConfirmationDescription:
          'The invitation for {{email}} will no longer be valid. Existing members are not affected.',
        invitationActionFailed: 'The invitation action failed. Check the current status and try again.',
        sendInvitation: 'Send invitation',
        cancel: 'Cancel',
        privilegedAllAccess: 'Owners and Admins always have access to all company resources.',
        ownerRoleOwnerOnly: 'Only an Owner can grant the Owner role.',
        staleAccessTitle: 'Access changed elsewhere',
        staleAccessDescription:
          'Another admin changed this member’s access while you were editing. Saving now would overwrite their change. Reload to discard your edits and see the current access.',
        staleAccessReload: 'Reload current access',
        ownerRoleLocked: 'This member is an Owner. Only an Owner can change their role or access.',
        searchProjects: 'Search projects',
        searchProjectsPlaceholder: 'Search project name…',
        projectsSelected_one: '{{count}} project selected',
        projectsSelected_other: '{{count}} projects selected',
        loadingProjects: 'Loading projects',
        projectsError: 'Projects could not be loaded. Existing project access is kept.',
        noProjectsFound: 'No projects found.',
        presentationAccess: 'Presentation access',
        presentationAccessDescription:
          'Direct access to individual presentations. It does not grant the Project or its other presentations.',
        presentationGrantsList: 'Presentations this person can access directly',
        presentationUnavailable: 'unavailable',
        presentationGrantLevel: 'Access level for {{name}}',
        removePresentationGrant: 'Remove access to {{name}}',
        noPresentationGrants: 'No direct presentation access.',
        searchPresentations: 'Search presentations',
        searchPresentationsPlaceholder: 'Search presentations',
        loadingPresentations: 'Loading presentations',
        presentationsError: 'Presentations could not be loaded.',
        noPresentationsFound: 'No presentations found.',
        presentationPages: 'Presentation pages',
        projectPages: 'Project pages',
        grantLevels: { EDITOR: 'Can edit', VIEWER: 'View only' },
        projectAccess: 'Project access',
        projectAccessDescription: 'Select the projects this person can access.',
        invitationError: 'The invitation could not be created. Check the details and try again.',
        memberAccessTitle: 'Access for {{name}}',
        externalIdentityTitle: 'External identity',
        externalIdentityDescription:
          'Link an Auth0 identity to this selected Slider account. Recent strong authentication and Company security access are required.',
        externalIdentityStart: 'Continue with Auth0',
        externalIdentityConfirmLabel: 'Confirm external identity for {{name}}',
        externalIdentityConfirm:
          'Link the Auth0 identity you just authenticated to {{name}}? This grants that identity the same Slider access as this account.',
        externalIdentityConfirmAction: 'Confirm link',
        externalIdentitySuccess: 'The external identity was linked.',
        externalIdentityError:
          'The identity link could not be completed. Restart the flow and try again.',
        saveAccess: 'Save access',
        updateSuccess: 'Member access was updated.',
        updateError: 'Member access could not be updated.',
        removeMember: 'Remove member',
        removeConfirmation: 'Remove this member from the company?',
        confirmRemove: 'Remove',
        errorTitle: 'Team data could not be loaded',
        errorUnavailable: 'This company is unavailable or you do not have permission.',
        errorGeneric: 'Something went wrong. Try again.',
        retry: 'Try again',
      },
      projects: {
        title: 'Projects',
        description: 'Organize presentations, themes, assets and knowledge by initiative.',
        newProject: 'New project',
        openProject: 'Open {{name}}',
        cardActions: 'Actions for {{name}}',
        delete: 'Delete',
        deleteTitle: 'Delete project?',
        deleteDescription:
          'This removes {{name}} and its presentations from active views. Published history and analytics may be retained according to policy.',
        deleteConfirm: 'Delete project',
        deleteError: 'We couldn’t delete this project. Try again.',
        noDescription: 'No description yet.',
        loading: 'Loading projects',
        searchLabel: 'Search projects',
        searchPlaceholder: 'Search projects…',
        statusLabel: 'Project status',
        allStatuses: 'All statuses',
        active: 'Active',
        archived: 'Archived',
        sortLabel: 'Sort projects',
        sortUpdated: 'Recently updated',
        sortNameAscending: 'Name A–Z',
        sortNameDescending: 'Name Z–A',
        clearFilters: 'Clear filters',
        emptyTitle: 'No projects yet',
        emptyDescription:
          'Create a project to organize presentations, themes, assets and knowledge.',
        filteredEmptyTitle: 'No projects match these filters',
        filteredEmptyDescription: 'Try a different search or clear the current filters.',
        presentationCount_one: '{{count}} presentation',
        presentationCount_other: '{{count}} presentations',
        updated: 'Updated {{time}}',
        results_one: '{{count}} project',
        results_other: '{{count}} projects',
        paginationLabel: 'Project pages',
        previousPage: 'Previous',
        nextPage: 'Next',
        pageLabel: 'Page {{page}}',
        updating: 'Updating projects',
        errorTitle: 'Projects could not be loaded',
        errorAuthentication: 'Sign in again to continue to this workspace.',
        errorUnavailable: 'This workspace is unavailable or you no longer have access.',
        errorGeneric: 'Something went wrong while loading projects. Try again.',
        retry: 'Try again',
      },
      shares: {
        playback: viewerPlaybackCopy.en.settings,
        title: 'Share links',
        description: 'Create links with separate access rules, personalization and analytics.',
        newShareLink: 'New share link',
        newTitle: 'New share link',
        editTitle: 'Edit share link',
        backToShares: 'Back to share links',
        linkDetails: 'Link details',
        whoCanView: 'Who can view?',
        summary: 'Summary',
        name: 'Name',
        link: 'Link',
        access: 'Access',
        status: 'Status',
        views: 'Views',
        lastViewed: 'Last viewed',
        created: 'Created',
        createdBy: 'Created by',
        revokedAt: 'Revoked',
        actions: 'Actions',
        usage: 'Usage',
        never: 'Never',
        none: 'None',
        unnamed: 'Untitled link',
        open: 'Open',
        edit: 'Edit',
        copyLink: 'Copy link',
        linkCopied: 'Share link copied.',
        linkCopyFailed: 'The link could not be copied.',
        expiresAt: 'Expires',
        password: 'Password',
        newPassword: 'New password',
        passwordUnchanged: 'Leave empty to keep the current password',
        allowedEmails: 'Allowed email addresses',
        searchRecipients: 'Search company members',
        noRecipientsFound: 'No members match that search.',
        removeRecipient: 'Remove {{name}}',
        personalization: 'Personalization',
        personalizationCount: 'Overrides',
        personalizationDescription: 'Override presentation variables that were marked shareable.',
        personalizationPreviewNote:
          'A detailed social preview is public only for public links and never includes recipient-specific values.',
        overrideValue: 'Override value',
        presentationDefault: 'Presentation default: {{value}}',
        liveRelease: 'Live release',
        releaseNumber: 'Release {{number}}',
        notPublished: 'Not published',
        socialPreview: 'Social preview',
        publicPreview: 'Public preview',
        protectedPreview: 'Protected preview',
        createShareLink: 'Create share link',
        saveChanges: 'Save changes',
        created_success: 'Share link created.',
        updated: 'Share link updated.',
        revoke: 'Revoke link',
        revokeTitle: 'Revoke this share link?',
        revokeDescription:
          'Anyone using this link will lose access immediately. Existing analytics will be kept.',
        revokeConfirm: 'Revoke link',
        revokeFailed: 'Could not confirm revocation. Check the link status and try again.',
        revoked: 'Share link revoked.',
        cancel: 'Cancel',
        retry: 'Try again',
        publishFirst: 'Publish this presentation before creating a viewer link.',
        publishFirstDescription:
          'A share link always opens the latest published release, so it would show nothing yet.',
        empty: 'No share links yet',
        emptyDescription: 'Create a link to share this presentation with an audience.',
        loadError: 'Share links could not be loaded',
        loadErrorDescription: 'Try again, or return to this presentation later.',
        loadErrorForbidden: 'You do not have access to this presentation.',
        notFound: 'Share link not found',
        notFoundDescription: 'The link may have been removed.',
        saveError: 'The share link could not be saved',
        pages: 'Share link pages',
        page: 'Page {{page}}',
        previous: 'Previous',
        next: 'Next',
        statuses: { ACTIVE: 'Active', EXPIRED: 'Expired', REVOKED: 'Revoked' },
        accessTypes: {
          PUBLIC: {
            label: 'Public',
            description: 'Anyone with the link can view the presentation.',
          },
          PASSWORD: {
            label: 'Password protected',
            description: 'Anyone with the link and password can view.',
          },
          AUTHENTICATED: {
            label: 'Signed-in users',
            description: 'Only authenticated Slider users can view.',
          },
          COMPANY_ONLY: {
            label: 'Company only',
            description: 'Only members of this company can view.',
          },
          SPECIFIC_USERS: {
            label: 'Specific users',
            description: 'Only the Slider users you select can view.',
          },
          SPECIFIC_EMAILS: {
            label: 'Specific emails',
            description: 'Only verified recipients at the addresses you list can view.',
          },
        },
        failure: {
          SHARE_LINK_CONFLICT: 'This link changed since it was loaded. Reload and try again.',
          SHARE_LINK_POLICY_INVALID: 'The selected access policy is not valid.',
          PRESENTATION_VARIABLE_INVALID:
            'A personalization value does not match its variable type.',
          ACCESS_DENIED: 'You do not have permission to manage share links here.',
          default: 'The share link could not be saved. Try again.',
        },
      },
      knowledge: {
        title: 'Project knowledge',
        description:
          'Reference material AI authoring may draw on. Sources stay inside this project.',
        addSource: 'Add a knowledge source',
        addSourceAction: 'Add source',
        sourceKind: 'Kind',
        sourceTitle: 'Title',
        sourceText: 'Text',
        sourceUrl: 'URL',
        textHint: 'Facts, figures and approved claims. Do not paste secrets or credentials.',
        instructionHint:
          'Standing guidance for this project, such as tone, terminology or claims to avoid.',
        urlHint:
          'Only public HTTP and HTTPS pages can be fetched. Internal and private addresses are refused.',
        source: 'Source',
        type: 'Kind',
        status: 'Status',
        chunks: 'Passages',
        updated: 'Updated',
        actions: 'Actions',
        reingest: 'Re-ingest source',
        delete: 'Delete source',
        created: 'Source added. Ingestion has started.',
        deleted: 'Source deleted.',
        reingestRequested: 'Re-ingestion started.',
        emptyTitle: 'No knowledge sources yet',
        emptyDescription:
          'Add a document, a page or written guidance so AI authoring has something to work from.',
        unavailableTitle: 'Knowledge could not be loaded',
        confidentialTitle: 'This project is classified confidential',
        confidentialBody:
          'Its knowledge is never sent to an AI provider that is not approved for confidential data.',
        pages: 'Knowledge pages',
        page: 'Page {{page}}',
        next: 'Next',
        previous: 'Previous',
        types: {
          FILE: 'File',
          URL: 'URL',
          TEXT: 'Text',
          INSTRUCTION: 'Instruction',
          STRUCTURED_DATA: 'Data',
          PRESENTATION_REFERENCE: 'Presentation',
        },
        statuses: {
          PENDING: 'Queued',
          PROCESSING: 'Processing',
          READY: 'Ready',
          FAILED: 'Failed',
        },
        errors: {
          KNOWLEDGE_SOURCE_URL_NOT_ALLOWED: 'That URL cannot be fetched from this deployment.',
          KNOWLEDGE_INGESTION_FAILED: 'The source could not be read.',
          KNOWLEDGE_SOURCE_NOT_READY: 'The underlying file is still being processed.',
          KNOWLEDGE_SOURCE_NOT_FOUND: 'That knowledge source no longer exists.',
          KNOWLEDGE_SOURCE_CONFLICT:
            'The source changed since it was loaded. Reload and try again.',
          ACCESS_DENIED: 'You do not have permission to manage knowledge in this project.',
          PROJECT_NOT_FOUND: 'That project does not exist, or you cannot reach it.',
          default: 'The knowledge source could not be saved. Try again.',
        },
      },
      ai: {
        title: 'AI authoring',
        blocked: {
          NOT_CONFIGURED: 'No AI provider is configured for this deployment.',
          DATA_POLICY:
            'The configured AI provider is not approved for this project data classification.',
          NONE: '',
        },
        noKnowledge:
          'This project has no ready knowledge yet, so generated copy will not be sourced.',
        generatePlan: 'Generate a plan',
        planPrompt: 'What should this presentation do?',
        planAudience: 'Audience',
        planSlideCount: 'Approximate slides',
        planning: 'Planning…',
        planReady: 'Plan ready',
        planSources: 'Sources used',
        planNoSources: 'No knowledge source supported this plan; treat the copy as a draft.',
        reviewProposals: 'Review AI proposals',
        errors: {
          AI_DATA_POLICY_BLOCKED: 'This project cannot be sent to the configured AI provider.',
          AI_PROVIDER_NOT_CONFIGURED: 'No AI provider is configured for this deployment.',
          AI_PROVIDER_UNAVAILABLE: 'The AI provider could not be reached. Try again shortly.',
          AI_PROVIDER_RATE_LIMITED: 'The AI provider is rate limiting requests. Try again shortly.',
          AI_OUTPUT_INVALID: 'The AI response did not match the required structure.',
          AI_CONTEXT_TOO_LARGE: 'There is too much material for one request. Narrow the request.',
          default: 'The AI request did not complete.',
        },
      },
      aiActions: {
        eyebrow: 'AI Actions',
        title: 'AI Actions',
        description:
          'Presentations AI assistants have prepared across your projects. Nothing is built until you approve it.',
        review: 'Review',
        reviewLabel: 'Review {{title}}',
        history: 'History',
        historyLabel: 'View history of {{title}}',
        approveLabel: 'Approve and build {{title}}',
        rejectLabel: 'Reject {{title}}',
        listLabel: 'AI actions',
        resultsLabel: 'Results in this view',
        decision: {
          approveTitle: 'Approve and build this presentation?',
          approveDescription:
            'Approving builds the whole presentation as a draft in this project. Nothing is published or shared. The decision applies to the version shown below.',
          proposal: 'Proposal',
          project: 'Project',
          slides: 'Slides',
          approved: '"{{title}}" was approved and built as a draft.',
          rejectTitle: 'Reject this proposal?',
          rejectDescription:
            '"{{title}}" will be closed and no presentation will be created. The assistant can prepare a new proposal.',
          rejected: '"{{title}}" was rejected.',
        },
        shortcut: 'AI Actions',
        empty: {
          title: 'No AI actions to review',
          description:
            'When a connected AI assistant prepares a presentation in a project you can review, it will appear here.',
        },
      },
      aiProposals: {
        title: 'AI proposals',
        eyebrow: 'AI proposals',
        description:
          'Presentations an AI assistant has prepared for this project. Nothing is built until you approve it.',
        filterLabel: 'Proposal filter',
        filterPending: 'Pending review',
        filterAll: 'All proposals',
        statsLabel: 'Proposal totals by status',
        stats: {
          total: 'Total proposals',
          pending: 'Pending review',
          built: 'Built',
          rejected: 'Rejected',
        },
        aiPrepared: 'AI prepared',
        mostRecent: 'Most recent',
        newestFirst: 'Newest first',
        viewDetails: 'View details',
        sourcesDescription: 'References used to prepare this proposal.',
        retry: 'Try again',
        back: 'Back to proposals',
        pages: 'Proposal pages',
        page: 'Page {{page}}',
        next: 'Next',
        previous: 'Previous',
        slideCount_one: '{{count}} slide',
        slideCount_other: '{{count}} slides',
        preparedBy: 'Prepared by {{name}}',
        empty: {
          title: 'No proposals waiting',
          description:
            'Ask your connected AI assistant to prepare a presentation for this project. It will appear here for your review.',
        },
        status: {
          PENDING_REVIEW: 'Pending review',
          BUILDING: 'Building',
          BUILT: 'Built',
          REJECTED: 'Rejected',
          BUILD_FAILED: 'Build failed',
        },
        review: {
          purpose: 'Purpose',
          goal: 'Goal',
          audience: 'Audience',
          descriptionLabel: 'Description',
          outline: 'Narrative outline',
          slides_one: '{{count}} proposed slide',
          slides_other: '{{count}} proposed slides',
          warnings: 'Check before approving',
          sources: 'Sources',
          basedOn: 'Based on: {{source}}',
          imageReference: 'Image from the asset library',
          assets_one: 'Uses {{count}} image from the asset library',
          assets_other: 'Uses {{count}} images from the asset library',
        },
        decision: {
          label: 'Decision',
          hint: 'Approving builds this whole presentation as a draft. Nothing is published or shared.',
          retryHint: 'The approved build did not finish. Retrying will not create a second copy.',
          approve: 'Approve & Build',
          building: 'Building…',
          retry: 'Retry build',
          reject: 'Reject',
          reasonLabel: 'Reason (optional)',
          confirmReject: 'Reject proposal',
          rejecting: 'Rejecting…',
          cancel: 'Cancel',
        },
        built: {
          title: 'Draft presentation built',
          body: 'This proposal was approved and built as a draft. Nothing has been published.',
          open: 'Open the presentation',
        },
        rejected: {
          title: 'Rejected',
          body: 'This proposal was rejected on {{date}}. No presentation was created.',
          reason: 'Reason: {{reason}}',
          next: 'Ask your AI assistant for a new proposal whenever you are ready.',
        },
        building: {
          title: 'Building the presentation',
          body: 'This usually takes a few seconds. This page updates when it finishes.',
        },
        failed: {
          title: 'The build did not finish',
          body: 'Nothing was published. You can retry safely, or ask your AI assistant for a new proposal.',
        },
        errors: {
          listTitle: 'Proposals unavailable',
          list: 'The proposals could not be loaded.',
          loadTitle: 'Proposal unavailable',
          load: 'This proposal does not exist, or you cannot review it.',
          decisionTitle: 'The decision was not applied',
          AI_PROPOSAL_STALE:
            'This proposal changed since you opened it. The latest version is shown; review it again.',
          AI_PROPOSAL_ALREADY_DECIDED:
            'Someone has already made a final decision on this proposal.',
          AI_PROPOSAL_BUILD_IN_PROGRESS: 'This proposal is already being built.',
          AI_PROPOSAL_BUILD_FAILED:
            'The presentation could not be built because something it needs has changed. Nothing was published.',
          ACCESS_DENIED: 'You do not have permission to decide on proposals in this project.',
          default: 'The decision could not be applied. Try again.',
        },
      },
      analytics: {
        title: 'Analytics',
        description:
          'Engagement for the published releases of this presentation, by release and share link.',
        filtersLabel: 'Analytics filters',
        kpiLabel: 'Headline metrics',
        range: 'Date range',
        ranges: { '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days' },
        release: 'Release',
        allReleases: 'All releases',
        releaseLabel: 'Release {{number}}',
        share: 'Share link',
        allShares: 'All share links',
        date: 'Date',
        slide: 'Slide',
        reach: 'Reach',
        dropOff: 'Drop-off',
        activeTime: 'Active time',
        interactions: 'Interactions',
        media: 'Media completed',
        narration: 'Narration completed',
        device: 'Device',
        shareOfSessions: 'Share of sessions',
        completionOf: '{{completed}} of {{started}}',
        units: { hour: 'h', minute: 'm', second: 's' },
        kpi: {
          sessions: 'Sessions',
          uniqueViewers: 'Unique viewers',
          uniqueViewersHint:
            'Distinct browsers or signed-in viewers, not guaranteed unique people.',
          completionRate: 'Completion rate',
          completionRateHint: 'Sessions that reached the final slide.',
          avgActiveTime: 'Average active time',
          activeTimeHint: 'Excludes time in a hidden tab or while idle.',
          avgSlidesViewed: 'Average slides viewed',
        },
        timelineTitle: 'Sessions over time',
        timelineDescription: 'Daily sessions for the selected filters.',
        funnelTitle: 'Slide funnel',
        funnelDescription: 'Sessions that reached each slide of the selected release.',
        slideTableCaption: 'Engagement by slide',
        shareBreakdownTitle: 'Share link comparison',
        shareBreakdownDescription: 'Sessions per share link across the selected range.',
        deviceTitle: 'Devices',
        deviceDescription: 'Sessions by device category.',
        devices: { DESKTOP: 'Desktop', MOBILE: 'Mobile', TABLET: 'Tablet', UNKNOWN: 'Unknown' },
        notPublishedTitle: 'This presentation has not been published',
        notPublishedBody: 'Analytics starts once a release is published and shared.',
        noSharesTitle: 'No share links yet',
        noSharesAction: 'Create a share link',
        lowSampleTitle: 'Low sample size',
        lowSampleBody:
          'Fewer than 20 sessions in this range. Percentages here move a lot with each new viewer.',
        emptyTitle: 'No sessions in this range',
        emptyBody:
          'Try a wider date range, or check that the share link has been distributed. New views can take a few minutes to appear.',
        emptyAction: 'Open share links',
        unavailableTitle: 'Analytics could not be loaded',
        unavailableBody: 'You may not have access to this presentation, or the request failed.',
      },
      assetPicker: {
        title: 'Choose asset',
        description: 'Select from Project assets, the Company library, or recently updated files.',
        searchLabel: 'Search assets',
        searchPlaceholder: 'Search assets…',
        filterLabel: 'Filter assets',
        resultsLabel: 'Assets',
        all: 'All',
        projectAssets: 'Project assets',
        companyLibrary: 'Company library',
        companyBadge: 'Company',
        sortLabel: 'Sort',
        sortRecent: 'Recently updated',
        sortTitle: 'Name',
        viewLabel: 'Asset view',
        viewGrid: 'Grid view',
        viewList: 'List view',
        updated: 'Updated {{date}}',
        usedIn: 'Used in {{count}} slide',
        usedIn_other: 'Used in {{count}} slides',
        noResults: 'No asset matches these filters.',
        insert: 'Insert asset',
        kinds: {
          IMAGE: 'Image',
          VIDEO: 'Video',
          AUDIO: 'Audio',
          LOGO: 'Logo',
          ICON: 'Icon',
          DOCUMENT: 'Document',
        },
      },
      templatePicker: {
        addTitle: 'Add slide',
        switchTitle: 'Change template',
        description: 'Browse structured slide templates to start faster and stay consistent.',
        searchLabel: 'Search templates',
        searchPlaceholder: 'Search templates…',
        categoryLabel: 'Filter by category',
        allCategories: 'All',
        resultsLabel: 'Templates',
        noResults: 'No template matches that search.',
        selectPrompt: 'Select a template to preview it.',
        previewUnavailable: 'This template cannot be previewed.',
        addAction: 'Add slide',
        addingAction: 'Adding slide…',
        switchAction: 'Use template',
        switchingAction: 'Changing template…',
        cuesErrorTitle: 'Presenter cues are incompatible',
        cuesErrorDescription:
          'This template does not support the slide’s presenter cues. Edit those cues or choose a compatible template.',
        affectedCues: 'Affected cues: {{cues}}',
        motionErrorTitle: 'Motion is incompatible',
        motionErrorDescription:
          'This template does not declare some elements the slide’s motion animates. Nothing was changed. You can remove the slide’s motion and switch, or choose a compatible template.',
        removeMotionAndSwitch: 'Remove motion and switch',
        conflictErrorTitle: 'Slide changed',
        conflictErrorDescription: 'Reload the slide and try changing its template again.',
        otherErrorTitle: 'Template change failed',
        otherErrorDescription: 'The template could not be applied. Try again.',
      },
      viewer: {
        movie: viewerPlaybackCopy.en,
        loadingTitle: 'Loading presentation',
        slides: 'Slides',
        slideCount: '{{count}} slide',
        slideCount_other: '{{count}} slides',
        slideLabel: 'Slide {{number}}: {{title}}',
        slideAnnouncement: 'Slide {{number}} of {{total}}: {{title}}',
        position: '{{position}} / {{total}}',
        openSlideList: 'Open slide list',
        closeSlideList: 'Close slide list',
        previousSlide: 'Previous slide',
        nextSlide: 'Next slide',
        enterFullscreen: 'Enter fullscreen',
        exitFullscreen: 'Exit fullscreen',
        playNarration: 'Play narration',
        pauseNarration: 'Pause narration',
        mute: 'Mute narration',
        unmute: 'Unmute narration',
      },
      viewerGate: {
        signIn: 'Sign in',
        viewPresentation: 'View presentation',
        passwordLabel: 'Password',
        showPassword: 'Show password',
        hidePassword: 'Hide password',
        passwordIncorrect: 'That password is incorrect.',
        passwordRateLimited: 'Too many attempts. Try again in a few minutes.',
        PASSWORD_REQUIRED: {
          title: 'This presentation is password protected',
          description: 'Enter the password to continue.',
        },
        AUTHENTICATION_REQUIRED: {
          title: 'Sign in to view this presentation',
          description: 'This link is shared with specific Slider accounts.',
        },
        COMPANY_REQUIRED: {
          title: 'This presentation is only available to members of the owning company',
          description: 'Switch to an account with access, or ask the sender for a different link.',
        },
        RECIPIENT_REQUIRED: {
          title: 'This presentation is not shared with your account',
          description: 'Ask the sender to share it with the address you signed in with.',
        },
        UNAVAILABLE: {
          title: 'This presentation is no longer available',
          description: 'The link may have expired or been revoked.',
        },
        NOT_PUBLISHED: {
          title: 'This presentation isn’t available yet',
          description: 'The presentation has not been published.',
        },
      },
      notFound: {
        title: 'Page not found',
        description: 'The page you requested does not exist.',
        action: 'Return home',
      },
    },
  },
  fa: {
    translation: {
      brand: { name: 'اسلایدر', tagline: 'ایده‌ها را زیبا ارائه کنید.' },
      styleGallery: styleGalleryCopy.fa,
      bulkReview: bulkReviewCopy.fa,
      common: {
        cancel: 'انصراف',
        close: 'بستن',
        openMenu: 'باز کردن منوی ناوبری',
        theme: 'ظاهر',
      },
      marketing: {
        nav: {
          primary: 'ناوبری اصلی',
          features: 'قابلیت‌ها',
          templates: 'قالب‌ها',
          security: 'امنیت',
          about: 'درباره ما',
        },
        requestAccess: 'درخواست دسترسی زودهنگام',
        logIn: 'ورود',
        signedInAs: 'وارد شده:',
        workspace: 'فضای کاری',
        language: 'زبان',
        languageAction: 'تغییر زبان',
        appearanceLabel: 'ظاهر',
        appearanceAction: 'ظاهر: {{mode}}. برای تغییر فعال کنید.',
        appearance: { system: 'سیستم', light: 'روشن', dark: 'تیره' },
        footer: {
          product: 'محصول',
          company: 'شرکت',
          legal: 'حقوقی',
          privacy: 'حریم خصوصی',
          terms: 'شرایط استفاده',
          copyright: '© {{year}} اسلایدر. همه حقوق محفوظ است.',
        },
        cta: {
          eyebrow: 'هر وقت آماده‌اید',
          title: 'ارائه بعدی خود را مثل یک تجربه وب بسازید.',
          description:
            'به دسترسی زودهنگام بپیوندید و در ساخت روشی ساختارمندتر برای تولید، اشتراک و یادگیری از ارائه‌ها همراه شوید.',
        },
        home: {
          metaTitle: 'ارائه‌های تعاملی وب‌محور',
          metaDescription:
            'ارائه‌های واکنش‌گرا و تعاملی را با قالب‌های حرفه‌ای، اشتراک امن، تحلیل و گردش‌کار هوش مصنوعی بسازید.',
          eyebrow: 'ارائه‌های وب‌محور',
          title: 'ارائه‌های تعاملی بسازید که حس وب را دارند.',
          description:
            'با قالب‌های حرفه‌ای شروع کنید، از هوش مصنوعی کمک بگیرید، تعامل معنادار اضافه کنید، امن به اشتراک بگذارید و رفتار مخاطب را بفهمید.',
          seeHow: 'نحوه کار را ببینید',
          previewLabel: 'پیش‌نمایش ارائه تعاملی اسلایدر',
          thumbnails: 'باز کردن بندانگشتی اسلایدها',
          mockEyebrow: 'عرضه محصول',
          mockTitle: 'از نشانه به روایت برسید.',
          mockDescription: 'روایتی واکنش‌گرا برای هر نمایشگر.',
          capabilityLabel: 'قابلیت‌های محصول',
          capability1: 'واکنش‌گرا از ابتدا',
          capability2: 'تعاملی',
          capability3: 'قابل اندازه‌گیری',
          capability4: 'با کمک هوش مصنوعی',
          featuresEyebrow: 'یک گردش‌کار پیوسته',
          featuresTitle: 'از ایده نخست تا بینش مخاطب.',
          featuresDescription:
            'اسلایدر ساختار، کیفیت طراحی، انتشار و سنجش را در یک محصول متمرکز نگه می‌دارد.',
          feature1Title: 'قالب‌های طراحی‌شده',
          feature1Description:
            'با ساختارهای واکنش‌گرایی شروع کنید که برای هدف‌های واقعی ارتباطی ساخته شده‌اند.',
          feature2Title: 'تجربه‌های تعاملی وب',
          feature2Description: 'اسلایدهای منفعل را به اقدام و کاوش متمرکز و دسترس‌پذیر تبدیل کنید.',
          feature3Title: 'واکنش‌گرا در همه‌جا',
          feature3Description: 'ارائه‌ها با دسکتاپ، تبلت و موبایل به‌درستی سازگار می‌شوند.',
          feature4Title: 'ارائه‌های روایت‌شده',
          feature4Description: 'متن روشن را با پخش دسترس‌پذیر و تجربه آگاه از رونوشت همراه کنید.',
          feature5Title: 'تحلیل بیننده',
          feature5Description: 'تکمیل، زمان فعال، ریزش و الگوهای تعامل را درک کنید.',
          feature6Title: 'هوش مصنوعی مبتنی بر محتوای شما',
          feature6Description:
            'پیش‌نویس ساختاریافته را از دانش تأییدشده پروژه بسازید و هر تغییر را بازبینی کنید.',
          templatesEyebrow: 'سامانه قالب',
          templatesTitle: 'کیفیت طراحی بدون ساخت دوباره سامانه.',
          exploreTemplates: 'مشاهده قالب‌ها',
          template1Art: 'عرضه',
          template1Title: 'عرضه محصول',
          template1Type: 'روایت · محصول',
          template2Art: 'گزارش',
          template2Title: 'گزارش عملکرد',
          template2Type: 'تحلیل · مدیران',
          template3Art: 'داستان',
          template3Title: 'داستان مشتری',
          template3Type: 'مطالعه موردی · فروش',
          template4Art: 'بریف',
          template4Title: 'بریف خلاق',
          template4Type: 'راهبرد · طراحی',
          responsiveBadge: 'واکنش‌گرا',
          interactiveBadge: 'تعاملی',
          viewerEyebrow: 'نمایشگر ساخته‌شده برای وب',
          viewerTitle: 'یک پیوند به اشتراک بگذارید، نه انبوهی فایل.',
          viewerDescription:
            'مخاطب یک تجربه ارائه تمیز با کنترل‌های هدفمند و بدون رابط مدیریت دریافت می‌کند.',
          viewerPoint1: 'ناوبری سازگار با صفحه‌کلید، لمس و موبایل',
          viewerPoint2: 'روایت اختیاری و پخش دسترس‌پذیر',
          viewerPoint3: 'آخرین انتشار موفق همیشه زنده می‌ماند',
          analyticsEyebrow: 'تحلیل کاربردی',
          analyticsTitle: 'بفهمید توجه کجا به اقدام تبدیل می‌شود.',
          analyticsDescription:
            'با نشانه‌های واقعی مخاطب روایت را بهتر کنید، نه با حدس از روی دانلودها.',
          analytics: {
            views: 'بازدید یکتا',
            completion: 'تکمیل',
            activeTime: 'میانگین زمان فعال',
            chartLabel: 'نمودار نمونه تکمیل اسلایدها',
          },
          aiEyebrow: 'گردش‌کار مسئولانه هوش مصنوعی',
          aiTitle: 'کمکی که قابل بازبینی می‌ماند.',
          aiStep1Title: 'درخواست',
          aiStep1Description: 'هدف را توضیح دهید و دانش تأییدشده پروژه را انتخاب کنید.',
          aiStep2Title: 'پیش‌نویس ساختاریافته',
          aiStep2Description: 'محتوایی متناسب با یک قالب واکنش‌گرای شناخته‌شده دریافت کنید.',
          aiStep3Title: 'بازبینی و انتشار',
          aiStep3Description: 'نتیجه را ویرایش، اعتبارسنجی و آگاهانه منتشر کنید.',
          securityEyebrow: 'دسترسی هدفمند',
          securityTitle: 'آزادانه منتشر کنید یا دقیق محدود کنید.',
          securityDescription:
            'دسترسی شرکت، پروژه و ارائه، MFA قوی، پیوندهای امن و انتشار حسابرسی‌شده در خود گردش‌کار طراحی شده‌اند.',
          learnSecurity: 'بررسی امنیت',
        },
        features: {
          metaTitle: 'قابلیت‌ها',
          title: 'هرآنچه برای ساخت، اشتراک و سنجش ارائه‌های تعاملی نیاز دارید.',
          description:
            'گردش‌کار ساختاریافته، قالب‌های باکیفیت و نمایشگر اختصاصی وب از پیش‌نویس تا بینش مخاطب در کنار هم کار می‌کنند.',
          group1Title: 'ساخت',
          group1Description: 'روایتی روشن را در ویرایشگر متمرکز و مبتنی بر طرح‌واره بسازید.',
          group1Point1: 'محتوای ساختاریافته و پیش‌نمایش زنده',
          group1Point2: 'ویرایش مشارکتی امن در برابر تعارض',
          group2Title: 'قالب‌ها و تم‌ها',
          group2Description:
            'از قالب‌های واکنش‌گرای توسعه‌یافته و تم‌های کنترل‌شده برند استفاده کنید.',
          group2Point1: 'نسخه‌های تغییرناپذیر قالب و تم',
          group2Point2: 'طراحی یکپارچه در شرکت',
          group3Title: 'تعامل و رسانه',
          group3Description: 'نقاط تعاملی، رسانه و تجربه‌های جاسازی‌شده هدفمند اضافه کنید.',
          group3Point1: 'الگوهای تعامل دسترس‌پذیر',
          group3Point2: 'تحویل نسخه‌بندی‌شده دارایی خصوصی',
          group4Title: 'روایت صوتی',
          group4Description: 'از متن‌های بازبینی‌شده، صدای ارائه بسازید.',
          group4Point1: 'چرخه عمر روایت برای هر اسلاید',
          group4Point2: 'کنترل پخش و رونوشت',
          group5Title: 'اشتراک و دسترسی',
          group5Description: 'انتشارهای تغییرناپذیر بسازید و بینندگان را انتخاب کنید.',
          group5Point1: 'دسترسی عمومی، رمزی یا دعوتی',
          group5Point2: 'یک پیوند به آخرین انتشار سالم',
          group6Title: 'تحلیل',
          group6Description: 'تعامل واقعی با ارائه را اندازه بگیرید.',
          group6Point1: 'بازدید، تکمیل و زمان فعال',
          group6Point2: 'ریزش اسلاید و تعامل‌ها',
          group7Title: 'هوش مصنوعی و دانش',
          group7Description: 'تولید را به محتوای تأییدشده پروژه متصل کنید.',
          group7Point1: 'خروجی ساختاریافته و اعتبارسنجی‌شده',
          group7Point2: 'سیاست فراهم‌کننده بر پایه طبقه‌بندی داده',
          group8Title: 'همکاری',
          group8Description: 'با دسترسی صریح بازبینی، نظر و انتشار کنید.',
          group8Point1: 'نقش‌های محدودشده شرکت و پروژه',
          group8Point2: 'وضعیت بازبینی و اعلان‌ها',
        },
        templates: {
          metaTitle: 'قالب‌های ارائه',
          title: 'قالب‌های واکنش‌گرا برای ارتباط، نه فقط تزئین.',
          description:
            'سامانه‌های نمونه برای عرضه، گزارش، پیشنهاد و داستان را ببینید. این نمونه‌ها کیفیت قالب‌محور اسلایدر را نشان می‌دهند.',
          galleryLabel: 'نمونه قالب‌های عمومی',
          responsive: 'واکنش‌گرا',
          interactive: 'تعاملی',
          chart: 'نمودار',
          item1Art: 'عرضه',
          item1Title: 'عرضه محصول',
          item1Description: 'مسئله، راه‌حل و مسیر پذیرش را چارچوب‌بندی کنید.',
          item2Art: 'شاخص‌ها',
          item2Title: 'مرور عملکرد',
          item2Description: 'شاخص‌های اصلی را به روایتی روشن برای مدیران تبدیل کنید.',
          item3Art: 'پیشنهاد',
          item3Title: 'پیشنهاد فروش',
          item3Description: 'زمینه مشتری را به توصیه‌ای متمرکز پیوند دهید.',
          item4Art: 'پژوهش',
          item4Title: 'گزارش پژوهش',
          item4Description: 'شواهد، الگوها و تصمیم‌های بعدی را توضیح دهید.',
          item5Art: 'داستان',
          item5Title: 'داستان مشتری',
          item5Description: 'یک نتیجه را با جزئیات پشتیبان معتبر ارائه کنید.',
          item6Art: 'بریف',
          item6Title: 'بریف راهبردی',
          item6Description: 'تیم را حول اولویت‌ها، مسئولیت و زمان‌بندی هم‌راستا کنید.',
        },
        security: {
          metaTitle: 'امنیت',
          title: 'امنیت و دسترسی متناسب با شیوه واقعی انتشار تیم‌ها.',
          description:
            'اسلایدر همکاری داخلی را از نمایش عمومی جدا می‌کند و در هر مرز منبع، دسترسی را در سرور اعمال می‌کند.',
          item1Title: 'احراز هویت و MFA',
          item1Description: 'نشست‌های امن HttpOnly با کلید عبور، TOTP و گزینه‌های بازیابی آگاهانه.',
          item2Title: 'جداسازی مستأجر',
          item2Description: 'داده شرکت با مجوزدهی برنامه و امنیت سطح سطر PostgreSQL محافظت می‌شود.',
          item3Title: 'دسترسی محدودشده',
          item3Description: 'مجوز شرکت، پروژه و ارائه صریح است و بیننده فقط خواندنی می‌ماند.',
          item4Title: 'پیوندهای اشتراک امن',
          item4Description: 'دسترسی عمومی، رمزی و دعوتی از عضویت داخلی جداست.',
          item5Title: 'ذخیره‌سازی خصوصی',
          item5Description: 'منابع خصوصی و مشتق‌های انتشار تغییرناپذیر سیاست تحویل جدا دارند.',
          item6Title: 'رویدادهای حسابرسی و امنیت',
          item6Description: 'تغییرهای مهم دسترسی و انتشار با شناسه درخواست ثبت پایدار می‌شوند.',
          item7Title: 'سیاست فراهم‌کننده هوش مصنوعی',
          item7Description:
            'طبقه‌بندی داده و مجازبودن فراهم‌کننده پیش از خروج محتوای تأییدشده بررسی می‌شود.',
          noCertificationClaim:
            'این صفحه معماری و سیاست فعلی محصول را شرح می‌دهد. اسلایدر ادعای گواهی دریافت‌نشده ندارد.',
        },
        about: {
          metaTitle: 'درباره ما',
          title: 'ارائه‌ها باید مانند محصولات دقیق وب کار کنند.',
          description:
            'اسلایدر بستری ساختاریافته برای ساخت، انتشار و سنجش ارائه‌های تعاملی است، بدون بازسازی یک ابزار طراحی.',
          whyTitle: 'چرا اسلایدر وجود دارد',
          whyDescription:
            'گردش‌کار سنتی ارائه، محتوا، طراحی، اشتراک و بازخورد را میان فایل‌ها و ابزارهای جدا تقسیم می‌کند.',
          whyDescription2:
            'اسلایدر این مراحل را در یک چرخه عمر وب‌محور جمع می‌کند و با قالب‌های هدفمند کیفیت حرفه‌ای را نگه می‌دارد.',
          principle1Title: 'وب‌محور',
          principle1Description: 'خروجی واکنش‌گرا و تحویل مبتنی بر پیوند، حالت پیش‌فرض است.',
          principle2Title: 'ساختاریافته',
          principle2Description:
            'طرح‌واره‌های روشن، کیفیت را تکرارپذیر و همکاری را امن‌تر می‌کنند.',
          principle3Title: 'با کیفیت طراحی',
          principle3Description:
            'قالب‌ها سامانه‌های هدفمند را رمزگذاری می‌کنند، نه بداهه‌سازی هر اسلاید را.',
        },
        earlyAccess: {
          metaTitle: 'درخواست دسترسی زودهنگام',
          eyebrow: 'دسترسی زودهنگام',
          title: 'در ساخت یک گردش‌کار بهتر برای ارائه همراه شوید.',
          description:
            'بگویید تیم شما چگونه ارائه می‌سازد و به اشتراک می‌گذارد. هر درخواست را بررسی می‌کنیم و در صورت فراهم‌شدن دسترسی تماس می‌گیریم.',
          back: 'بازگشت به خانه',
          benefit1: 'کاوش یک ویرایشگر ساختاریافته وب‌محور',
          benefit2: 'انتشار تجربه‌های تعاملی و واکنش‌گرا',
          benefit3: 'ارسال مستقیم بازخورد به تیم محصول',
          name: 'نام',
          email: 'ایمیل کاری',
          company: 'شرکت',
          role: 'نقش',
          useCase: 'از اسلایدر برای چه کاری استفاده می‌کنید؟',
          useCasePlaceholder: 'مثلاً: عرضه تعاملی محصول برای تیم‌های فروش توزیع‌شده.',
          useCaseHint: 'کمی بیشتر توضیح دهید (حداقل ۱۰ نویسه).',
          required: 'این فیلد الزامی است.',
          invalidEmail: 'یک ایمیل کاری معتبر وارد کنید.',
          consent: 'به‌روزرسانی محصول و اخبار دسترسی زودهنگام',
          consentDescription:
            'اختیاری. اسلایدر می‌تواند درباره محصول ایمیل بزند. این رضایت از تأیید دریافت درخواست جداست.',
          privacyNote:
            'ارسال این فرم حساب کاربری نمی‌سازد. این اطلاعات فقط برای بررسی و پاسخ به درخواست استفاده می‌شود.',
          submitError: 'درخواست شما ارسال نشد. دوباره تلاش کنید.',
          rateLimited: 'درخواست‌های زیادی دریافت شده است. کمی صبر کنید و دوباره تلاش کنید.',
          successEyebrow: 'دسترسی زودهنگام',
          successTitle: 'درخواست دریافت شد',
          successBody:
            'سپاس. درخواست را بررسی می‌کنیم و اگر دسترسی فراهم شود با {{email}} تماس می‌گیریم.',
          backHome: 'بازگشت به خانه',
          roles: {
            MARKETING: 'بازاریابی',
            DESIGN: 'طراحی',
            PRODUCT: 'محصول',
            SALES: 'فروش',
            LEADERSHIP: 'مدیریت',
            ENGINEERING: 'مهندسی',
            OTHER: 'سایر',
          },
        },
        privacy: {
          eyebrow: 'حقوقی',
          title: 'حریم خصوصی',
          description: 'شیوه مدیریت اطلاعات ارسالی در وب‌سایت عمومی و محصول اسلایدر.',
          updated: 'آخرین به‌روزرسانی: ۶ شهریور ۱۴۰۵',
          section1Title: 'اطلاعاتی که جمع‌آوری می‌کنیم',
          section1Body:
            'اطلاعاتی را که خودتان ارسال می‌کنید، مانند جزئیات تماس دسترسی زودهنگام، و اطلاعات فنی محدود لازم برای اجرا و حفاظت از سرویس جمع‌آوری می‌کنیم.',
          section2Title: 'نحوه استفاده از اطلاعات',
          section2Body:
            'از اطلاعات برای پاسخ به درخواست‌ها، اجرای اسلایدر، امنیت حساب‌ها، بهبود پایداری و انجام تعهدات قانونی استفاده می‌کنیم.',
          section3Title: 'فراهم‌کنندگان خدمات',
          section3Body:
            'زیرساخت، ایمیل و فراهم‌کنندگان هوش مصنوعی تأییدشده فقط اطلاعات لازم برای هدف و سیاست مستند خود را پردازش می‌کنند.',
          section4Title: 'نگهداری و امنیت',
          section4Body:
            'اطلاعات را متناسب با نیاز محصول و قانون نگه می‌داریم و از کنترل دسترسی، رمزنگاری و سوابق حسابرسی مناسب استفاده می‌کنیم.',
          section5Title: 'انتخاب‌ها و تماس',
          section5Body:
            'پیگیری بازاریابی اختیاری و از پیام‌های خدماتی جداست. برای درخواست دسترسی، اصلاح یا حذف با گرداننده اسلایدر تماس بگیرید.',
        },
        terms: {
          eyebrow: 'حقوقی',
          title: 'شرایط استفاده',
          description: 'شرایط پایه استفاده از وب‌سایت عمومی و محصول دعوت‌محور اسلایدر.',
          updated: 'آخرین به‌روزرسانی: ۶ شهریور ۱۴۰۵',
          section1Title: 'استفاده از اسلایدر',
          section1Body:
            'اسلایدر فعلاً فقط با دعوت در دسترس است. باید قانونی از سرویس استفاده کنید و سیاست‌های دسترسی شرکت دعوت‌کننده را رعایت کنید.',
          section2Title: 'حساب‌ها و دسترسی',
          section2Body:
            'روش‌های احراز هویت را امن نگه دارید و دسترسی را بیرون از محدوده اعطاشده به اشتراک نگذارید.',
          section3Title: 'محتوای شما',
          section3Body:
            'مسئول محتوای ارسالی خود و داشتن حقوق و مجوزهای لازم برای استفاده از آن هستید.',
          section4Title: 'دسترس‌پذیری و تغییرات',
          section4Body:
            'قابلیت‌های دسترسی زودهنگام ممکن است با توسعه محصول تغییر کنند. برای پایداری تجربه‌ها تلاش می‌کنیم اما دسترسی بدون وقفه را تضمین نمی‌کنیم.',
          section5Title: 'استفاده مسئولانه',
          section5Body:
            'برای دورزدن کنترل دسترسی، اختلال در سرویس یا انتشار محتوای غیرقانونی و آسیب‌زا از اسلایدر استفاده نکنید.',
        },
      },
      invitation: {
        eyebrow: 'دعوت به شرکت',
        loadingTitle: 'در حال بررسی دعوت‌نامه',
        loadingDescription: 'لحظه‌ای صبر کنید تا دعوت‌نامه بررسی شود.',
        invalidTitle: 'دعوت‌نامه در دسترس نیست',
        invalidDescription:
          'این پیوند دعوت معتبر نیست. از مدیر خود بخواهید دعوت‌نامه‌ی تازه‌ای بفرستد.',
        expiredTitle: 'دعوت‌نامه منقضی شده است',
        expiredDescription:
          'این دعوت‌نامه منقضی شده است. از مدیر خود بخواهید دعوت‌نامه‌ی تازه‌ای بفرستد.',
        revokedTitle: 'دعوت‌نامه دیگر معتبر نیست',
        revokedDescription:
          'این دعوت‌نامه لغو شده است. اگر فکر می‌کنید اشتباهی رخ داده با مدیر خود تماس بگیرید.',
        alreadyAcceptedTitle: 'دعوت‌نامه قبلاً استفاده شده است',
        alreadyAcceptedDescription: 'این دعوت‌نامه پیش‌تر پذیرفته شده است. برای ادامه وارد شوید.',
        errorTitle: 'بررسی دعوت‌نامه ممکن نشد',
        errorDescription: 'مشکلی در سمت ما رخ داد. چند لحظه بعد دوباره تلاش کنید.',
        retry: 'تلاش دوباره',
        summary:
          'شما به‌عنوان {{role}} به {{company}} دعوت شده‌اید. این دعوت‌نامه برای {{email}} است.',
        role: { OWNER: 'مالک', ADMIN: 'مدیر', EDITOR: 'ویرایشگر', VIEWER: 'بیننده' },
        signInTitle: 'برای پیوستن به {{company}} وارد شوید',
        signInToAccept: 'ورود و پذیرش دعوت‌نامه',
        acceptTitle: 'پیوستن به {{company}}',
        accept: 'پذیرش دعوت‌نامه',
        setupTitle: 'برای پیوستن به {{company}} حساب خود را بسازید',
        displayName: 'نام شما',
        displayNamePlaceholder: 'نام و نام خانوادگی خود را وارد کنید',
        createAccount: 'ساخت حساب و پیوستن',
        emailFixed: 'حساب شما با همان نشانی ایمیلی ساخته می‌شود که دعوت‌نامه به آن ارسال شده است.',
        mismatchTitle: 'این دعوت‌نامه برای حساب دیگری است',
        mismatchDescription:
          'با حساب دیگری وارد شده‌اید. این دعوت‌نامه به {{email}} ارسال شده است. برای ادامه با حساب دعوت‌شده، خارج شوید.',
        switchAccount: 'خروج و تغییر حساب',
        errorRateLimited: 'تعداد تلاش‌ها زیاد است. کمی صبر کنید و دوباره تلاش کنید.',
        errorValidation: 'نام و گذرواژه را بررسی کنید و دوباره تلاش کنید.',
        errorGeneric: 'پذیرش دعوت‌نامه ممکن نشد. دوباره تلاش کنید.',
      },
      auth: {
        eyebrow: 'خوش آمدید',
        title: 'ورود به اسلایدر',
        description: 'برای ادامه به فضای کاری شرکت، ایمیل کاری و رمز عبور خود را وارد کنید.',
        email: 'ایمیل کاری',
        emailPlaceholder: 'name@company.com',
        password: 'رمز عبور',
        passwordPlaceholder: 'رمز عبور خود را وارد کنید',
        showPassword: 'نمایش رمز عبور',
        hidePassword: 'پنهان کردن رمز عبور',
        forgotPassword: 'رمز عبور را فراموش کرده‌اید؟',
        signIn: 'ورود',
        needAccess: 'دسترسی ندارید؟',
        requestEarlyAccess: 'درخواست دسترسی زودهنگام',
        verifyEyebrow: 'یک مرحله دیگر',
        verifyTitle: 'هویت خود را تأیید کنید',
        verifyDescription: 'برای تکمیل ورود، یکی از روش‌های تأیید موجود را استفاده کنید.',
        usePasskey: 'ادامه با کلید عبور',
        verificationCode: 'کد تأیید',
        recoveryCode: 'کد بازیابی',
        recoveryCodePlaceholder: 'کد بازیابی خود را وارد کنید',
        verify: 'تأیید و ورود',
        otherMethods: 'روش‌های دیگر تأیید',
        backToSignIn: 'بازگشت به ورود',
        errorInvalidCredentials: 'ایمیل یا رمز عبور نادرست است.',
        errorInvalidCode: 'کد تأیید نادرست است یا قبلاً استفاده شده است.',
        errorCodeRequired: 'برای ادامه کد تأیید یا بازیابی را وارد کنید.',
        errorCodeIncomplete: 'هر {{digits}} رقم کد تأیید را کامل وارد کنید.',
        errorRateLimited: 'تعداد تلاش‌ها بیش از حد است. کمی صبر کنید و دوباره تلاش کنید.',
        errorChallengeExpired: 'این تلاش ورود منقضی شده است. دوباره شروع کنید.',
        errorPasskey: 'کلید عبور تأیید نشد. دوباره تلاش کنید یا روش دیگری را انتخاب کنید.',
        errorGeneric: 'مشکلی پیش آمد. دوباره تلاش کنید.',
        method: {
          PASSKEY: 'استفاده از کلید عبور',
          TOTP: 'استفاده از برنامه تأییدکننده',
          EMAIL_OTP: 'ارسال کد ایمیلی',
          RECOVERY_CODE: 'استفاده از کد بازیابی',
        },
        resetEyebrow: 'بازیابی حساب',
        forgotTitle: 'بازنشانی رمز عبور',
        forgotDescription: 'ایمیل کاری خود را وارد کنید تا پیوند امن بازنشانی برایتان ارسال شود.',
        sendResetLink: 'ارسال پیوند بازنشانی',
        resetRequested:
          'اگر حسابی با این ایمیل وجود داشته باشد، دستورالعمل بازنشانی رمز ارسال شده است.',
        resetTitle: 'رمز عبور جدید انتخاب کنید',
        resetDescription: 'برای حساب اسلایدر خود یک رمز عبور جدید بسازید.',
        newPassword: 'رمز عبور جدید',
        newPasswordPlaceholder: 'رمزی با حداقل ۱۵ نویسه بسازید',
        confirmPassword: 'تکرار رمز عبور جدید',
        confirmPasswordPlaceholder: 'رمز عبور جدید را دوباره وارد کنید',
        passwordHint: 'حداقل از ۱۵ نویسه استفاده کنید.',
        updatePassword: 'به‌روزرسانی رمز عبور',
        errorPasswordMismatch: 'رمزهای عبور یکسان نیستند.',
        errorResetLink: 'این پیوند نامعتبر، منقضی یا قبلاً استفاده شده است.',
        requestNewLink: 'درخواست پیوند جدید',
        passwordChangedTitle: 'رمز عبور به‌روزرسانی شد',
        passwordChangedDescription: 'رمز عبور شما تغییر کرد. اکنون می‌توانید وارد شوید.',
        enrollmentEyebrow: 'محافظت از حساب',
        enrollmentTitle: 'راه‌اندازی احراز هویت قوی',
        enrollmentDescription:
          'نقش شما پیش از ورود به اسلایدر به کلید عبور یا برنامه تأییدکننده نیاز دارد.',
        enrollPasskey: 'راه‌اندازی کلید عبور',
        enrollAuthenticator: 'راه‌اندازی برنامه تأییدکننده',
        passkeyDeviceName: 'این دستگاه',
        scanQrCode: 'این کد QR را با برنامه تأییدکننده اسکن کنید و سپس کد ۶ رقمی آن را وارد کنید.',
        qrCodeAlt: 'کد QR راه‌اندازی برنامه تأییدکننده',
        manualSecret: 'کلید راه‌اندازی دستی',
        finishEnrollment: 'تأیید و پایان راه‌اندازی',
        strongMfaHint: 'MFA قوی یعنی کلید عبور یا برنامه تأییدکننده. کد ایمیلی یک روش جایگزین است.',
        recoveryCodesTitle: 'کدهای بازیابی خود را ذخیره کنید',
        recoveryCodesDescription:
          'اگر دسترسی به برنامه تأییدکننده را از دست دادید، هر کد یک‌بار قابل استفاده است. آن‌ها را در جای امن نگه دارید؛ دوباره نمایش داده نمی‌شوند.',
        copyRecoveryCodes: 'کپی کدهای بازیابی',
        savedRecoveryCodes: 'کدها را ذخیره کردم',
        errorSessionExpired: 'نشست راه‌اندازی شما منقضی شد. برای ادامه دوباره وارد شوید.',
      },
      manage: {
        workspace: 'فضای کاری',
        overview: 'نمای کلی',
        projects: 'پروژه‌ها',
        presentations: 'ارائه‌ها',
        templates: 'قالب‌ها',
        templateCategories: 'دسته‌ها',
        designLibrary: 'کتابخانه طراحی',
        aiActions: 'اقدامات هوش مصنوعی',
        assets: 'دارایی‌ها',
        analytics: 'تحلیل‌ها',
        settings: 'تنظیمات',
        pageTitle: 'نمای کلی شرکت',
        pageDescription: 'زیرساخت فضای کاری برای ماژول‌های محصول آماده است.',
        recentWork: 'کارهای اخیر',
        emptyTitle: 'هنوز ارائه‌ای وجود ندارد',
        emptyDescription: 'ابتدا یک پروژه بسازید تا تیم بتواند ارائه‌ای را شروع کند.',
        createProject: 'ساخت پروژه',
        profile: 'باز کردن منوی حساب',
        navigationLabel: 'مدیریت',
        secondaryNavigationLabel: 'شرکت',
        collapseSidebar: 'جمع کردن نوار کناری',
        expandSidebar: 'باز کردن نوار کناری',
        switchCompany: 'تغییر شرکت',
        companies: 'شرکت‌ها',
        companySettings: 'تنظیمات شرکت',
        search: 'جستجو',
        commandPlaceholder: 'جستجوی صفحه‌ها و فرمان‌ها…',
        commandTitle: 'جستجو در اسلایدر',
        commandDescription: 'به یک صفحه بروید یا فرمان در دسترس را اجرا کنید.',
        commandEmpty: 'صفحه یا فرمانی پیدا نشد.',
        notifications: 'اعلان‌ها',
        noNotifications: 'همه‌چیز بررسی شده است. اعلان‌های جدید اینجا نمایش داده می‌شوند.',
        account: 'حساب کاربری',
        security: 'امنیت',
        sessions: 'نشست‌ها',
        appearance: 'ظاهر',
        systemTheme: 'سیستم',
        lightTheme: 'روشن',
        darkTheme: 'تیره',
        language: 'زبان',
        signOut: 'خروج',
        signingOut: 'در حال خروج…',
      },
      companyOverview: {
        eyebrow: 'نمای کلی شرکت',
        description: 'پروژه‌ها، ارائه‌ها و فعالیت اخیر تیم شما.',
        summary: 'خلاصه شرکت',
        projects: 'پروژه‌ها',
        presentations: 'ارائه‌ها',
        published: 'منتشرشده',
        views: 'بازدید · ۳۰ روز',
        members: 'اعضا',
        newProject: 'پروژه جدید',
        newPresentation: 'ارائه جدید',
        recentPresentations: 'ارائه‌های اخیر',
        viewAll: 'مشاهده همه',
        noPresentations: 'هنوز ارائه‌ای نیست.',
        recentActivity: 'فعالیت اخیر',
        activityLine: '{{actor}} · {{action}}',
        system: 'سیستم',
        firstProjectTitle: 'اولین پروژه را بسازید',
        firstProjectDescription:
          'پروژه‌ها تم، دانش، دارایی و ارائه‌های یک ابتکار را کنار هم نگه می‌دارند.',
        errorTitle: 'نمای کلی شرکت بارگیری نشد',
        errorDescription: 'دوباره تلاش کنید یا بعداً به این فضا برگردید.',
        retry: 'تلاش دوباره',
        ...companyOverviewCopy.fa,
      },
      presentations: {
        shareLinkProposalReview: shareLinkProposalReviewCopy.fa,
        copyReview: copyReviewCopy.fa,
        copyToProject: copyToProjectCopy.fa,
        duplicate: duplicateCopy.fa,
        gallery: presentationsGalleryCopy.fa,
        guided: guidedCreateCopy.fa,
        eyebrow: 'ارائه‌ها',
        title: 'ارائه‌ها',
        description: 'داستان‌های تعاملی را بسازید، بازبینی و منتشر کنید.',
        new: 'ارائه جدید',
        search: 'جستجوی ارائه‌ها',
        searchPlaceholder: 'جستجوی ارائه‌ها…',
        empty: 'هنوز ارائه‌ای نیست',
        filteredEmpty: 'ارائه‌ای با این جستجو پیدا نشد',
        emptyDescription: 'هر وقت آماده بودید در یک پروژه قابل ویرایش ارائه بسازید.',
        errorTitle: 'ارائه‌ها بارگیری نشدند',
        retry: 'تلاش دوباره',
        results: '{{count}} ارائه',
        pages: 'صفحه‌های ارائه',
        previous: 'قبلی',
        next: 'بعدی',
        page: 'صفحه {{page}}',
        back: 'بازگشت به ارائه‌ها',
        open: 'باز کردن {{name}}',
        cardActions: 'اقدام‌های {{name}}',
        moreActions: 'اقدام‌های بیشتر',
        delete: {
          action: 'حذف ارائه',
          title: 'ارائه حذف شود؟',
          description:
            'این کار «{{name}}» را از نماهای فعال حذف می‌کند. تاریخچهٔ انتشار و تحلیل‌ها ممکن است بر اساس سیاست نگهداری شوند.',
          publishedWarning: 'این ارائه منتشر شده است. پیوندهای اشتراک آن بلافاصله از کار می‌افتند.',
          typeToConfirm: 'برای تأیید، «{{name}}» را تایپ کنید',
          confirm: 'حذف ارائه',
          deleting: 'در حال حذف…',
          success: 'ارائه حذف شد.',
          error: 'ارائه حذف نشد. دوباره تلاش کنید.',
          errorConflict:
            'این ارائه تغییر کرده یا پیش‌تر حذف شده است. آن را بررسی و دوباره تلاش کنید.',
          errorDenied: 'اجازهٔ حذف این ارائه را ندارید.',
        },
        newTitle: 'ساخت ارائه',
        newDescription: 'یک ارائه ساختاریافته در این پروژه شروع کنید.',
        fields: {
          name: 'نام ارائه',
          description: 'توضیحات',
          descriptionPlaceholder: 'این ارائه درباره چیست؟',
        },
        create: 'ساخت ارائه',
        createError: 'ارائه ساخته نشد.',
        createSuccess: 'ارائه ساخته شد.',
        status: {
          DRAFT: 'پیش‌نویس',
          IN_REVIEW: 'در حال بازبینی',
          APPROVED: 'تأییدشده',
          PUBLISHED: 'منتشرشده',
        },
      },
      presentationAuthoring: {
        unavailable: 'این ارائه در دسترس نیست یا به آن دسترسی ندارید.',
        appearance: 'ظاهر',
        customizeAppearance: 'سفارشی‌سازی ظاهر',
        customizeAppearanceHint:
          'رنگ‌ها، فونت‌ها و سطوح این ارائه را تنظیم کنید. اسلایدها و نسخه‌های منتشرشده محتوای خود را حفظ می‌کنند.',
        noDescription: 'یک ارائه ساختاریافته و وب‌محور.',
        editSlides: 'ویرایش اسلایدها',
        addSlide: 'افزودن اسلاید',
        open: 'باز کردن ارائه',
        publish: 'انتشار',
        preview: 'پیش‌نمایش',
        previewPresentation: 'پیش‌نمایش ارائه',
        share: 'اشتراک‌گذاری',
        notAvailableYet: 'هنوز در دسترس نیست',
        draftPreview: 'پیش‌نمایش پیش‌نویس',
        themeFallback: {
          title: 'پیش‌نمایش از مقادیر جایگزین تم استفاده می‌کند. انتشار مسدود است.',
        },
        closePreview: 'بستن پیش‌نمایش',
        previewViewport: 'اندازه پیش‌نمایش',
        viewport: {
          wide: 'پیش‌نمایش عریض',
          compact: 'پیش‌نمایش فشرده',
          mobile: 'پیش‌نمایش موبایل',
        },
        navigation: 'پیمایش ارائه',
        panels: 'پنل‌های اسلاید',
        visibilityOptions: { auto: 'خودکار', shown: 'نمایش', hidden: 'پنهان' },
        overview: 'نمای کلی',
        slides: 'اسلایدها',
        selectSlideHint: 'برای پیش‌نمایش، یک اسلاید را انتخاب کنید.',
        searchSlides: 'جستجوی اسلایدها',
        noMatchingSlides: 'اسلایدی با این جستجو پیدا نشد.',
        selectedSlide: 'اسلاید در حال پیش‌نمایش',
        review: 'بازبینی',
        analytics: 'تحلیل',
        shares: 'اشتراک‌ها',
        access: 'دسترسی',
        settings: 'تنظیمات',
        readiness: 'آمادگی انتشار',
        workflow: 'گردش‌کار',
        live: 'نسخه زنده',
        liveRelease: 'نسخه {{number}}',
        notPublished: 'منتشر نشده',
        slideCount: 'تعداد اسلاید',
        updated: 'به‌روزرسانی',
        chooseTemplate: 'انتخاب قالب',
        chooseTemplateDescription:
          'یک اسلاید ساختاریافته با قالب واکنش‌گرای قابل اعتماد اضافه کنید.',
        changeTemplate: 'تغییر قالب',
        chooseAsset: 'انتخاب دارایی',
        chooseAssetDescription: 'یک دارایی آماده پروژه یا شرکت انتخاب کنید.',
        replaceImage: 'جایگزینی تصویر',
        removeImage: 'حذف تصویر',
        noImageSelected: 'تصویری انتخاب نشده است',
        link: {
          missingSlide: 'اسلاید پیوندشده دیگر وجود ندارد. مقصد دیگری انتخاب کنید.',
          nextSlide: 'اسلاید بعدی',
          none: 'بدون پیوند',
          slideOption: 'اسلاید {{number}}: {{title}}',
          urlInvalid: 'نشانی کامل با https:// وارد کنید.',
          urlLabel: 'نشانی وب‌سایت',
          website: 'وب‌سایت (https)',
        },
        backOverview: 'بازگشت به نمای کلی ارائه',
        history: 'تاریخچه ویرایش',
        undo: 'بازگرداندن',
        redo: 'انجام دوباره',
        undoShortcut: 'بازگرداندن (Ctrl/Cmd+Z)',
        redoShortcut: 'انجام دوباره (Ctrl+Y یا Ctrl/Cmd+Shift+Z)',
        viewOnly: 'فقط مشاهده',
        slideNavigation: 'پیمایش اسلایدها',
        inspector: 'بازرس',
        assetField: 'دارایی',
        content: 'محتوا',
        design: 'طراحی',
        narration: 'یادداشت و روایت',
        presenterNotes: 'یادداشت ارائه‌دهنده',
        presenterScript: 'متن خواندنی ارائه‌دهنده',
        presenterScriptCopy: {
          fromNarration: 'کپی از متن روایت',
          fromNotes: 'کپی از یادداشت ارائه‌دهنده',
          confirmTitle: 'جایگزینی متن خواندنی',
          confirmBody:
            'متن خواندنی از قبل مقدار دارد. با متن کپی‌شده جایگزین شود؟ پس از این، دو متن به هم وابسته نیستند.',
          replace: 'جایگزین کن',
          cancel: 'انصراف',
        },
        narrationScript: 'متن روایت',
        narrationControls: {
          ...narrationFeedbackCopy.fa,
          title: 'روایت تولیدشده',
          description:
            'پروفایل صدا، سبک اجرا و سرعت را انتخاب کنید. تولید از متن ذخیره‌شده استفاده می‌کند.',
          voice: 'پروفایل صدا',
          style: 'سبک اجرا',
          speed: 'سرعت',
          generate: 'تولید صوت',
          regenerate: 'تولید دوباره صوت',
          processing: 'روایت در پس‌زمینه در حال تولید است…',
          scriptRequired: 'پیش از تولید صوت، متن روایت را وارد و ذخیره کنید.',
          transcript: 'متن روایت',
          audioUnsupported: 'مرورگر شما امکان پخش این روایت را ندارد.',
          staleTitle: 'روایت قدیمی شده است',
          staleDescription: 'متن یا تنظیمات صدا تغییر کرده است. پیش از انتشار دوباره تولید کنید.',
          failedTitle: 'تولید ناموفق بود',
          failedDescription:
            'ارائه‌دهنده نتوانست روایت را تولید کند. می‌توانید با خیال راحت دوباره تلاش کنید.',
          styles: { NEUTRAL: 'خنثی', WARM: 'گرم', ENERGETIC: 'پرانرژی', CALM: 'آرام' },
          status: {
            NONE: 'شروع نشده',
            DRAFT_SCRIPT: 'متن پیش‌نویس',
            PROCESSING: 'در حال تولید',
            READY: 'آماده',
            STALE: 'قدیمی',
            FAILED: 'ناموفق',
          },
          errors: {
            NARRATION_SCRIPT_EMPTY: 'ابتدا متن روایت را وارد و ذخیره کنید.',
            VOICE_PROFILE_UNAVAILABLE: 'این پروفایل صدا در دسترس نیست.',
            TTS_PROVIDER_UNAVAILABLE:
              'روایت برای این شرکت پیکربندی نشده یا غیرفعال است. از مالک یا مدیر شرکت بخواهید Gemini را در «ارائه‌دهندگان هوش مصنوعی و صدا» متصل کند.',
            NARRATION_DAILY_LIMIT_REACHED:
              'سقف روزانهٔ روایت این شرکت پر شده است. فردا دوباره تلاش کنید یا از مالک یا مدیر شرکت بخواهید سقف را افزایش دهد.',
            NARRATION_GENERATION_FAILED: 'تولید روایت ناموفق بود. دوباره تلاش کنید.',
            default: 'روایت قابل تولید نبود.',
          },
        },
        batchNarration: {
          open: 'روایت کل ارائه',
          singleSlide: 'بازگشت به این اسلاید',
          title: 'پیش‌نمایش روایت ارائه',
          description:
            'وضعیت روایت اسلایدها را مرور و یک انتخاب را پیش‌نمایش کنید. ویرایش متن موقت است و تا زمان پیش‌نمایش ذخیره نمی‌شود.',
          loadError: 'بارگیری وضعیت روایت ناموفق بود',
          empty: 'این ارائه اسلایدی برای بررسی ندارد.',
          pageBoundary:
            'صفحهٔ {{page}} از {{totalPages}} نمایش داده می‌شود ({{count}} اسلاید در کل). انتخاب‌ها هنگام جابه‌جایی بین صفحه‌ها حفظ می‌شوند.',
          selectPage: 'انتخاب هر {{count}} اسلاید بارگیری‌شده',
          selectSlide: 'انتخاب اسلاید {{number}}',
          selectedCount: '{{count}} انتخاب‌شده',
          selectionLimit:
            'پیش‌نمایش حداکثر {{count}} اسلاید را پشتیبانی می‌کند. برای ادامه یک یا چند اسلاید را از انتخاب خارج کنید.',
          scriptLimit:
            'پیش‌نمایش حداکثر {{count}} ویرایش متن و {{bytes}} کیبی‌بایت دادهٔ سریال‌شدهٔ جایگزینی متن را پشتیبانی می‌کند. برای ادامه ویرایش‌ها را پاک کنید یا اسلایدهای ویرایش‌شده را از انتخاب خارج کنید.',
          baseVoice: 'صدای پایه',
          slideVoice: 'صدای این اسلاید',
          useBaseVoice: 'استفاده از صدای پایه',
          scriptOverride: 'جایگزینی موقت متن',
          slide: 'اسلاید {{number}}',
          blocked: 'مشکل قبلی: {{reason}}',
          previous: 'صفحهٔ قبل',
          next: 'صفحهٔ بعد',
          page: 'صفحهٔ {{page}} از {{totalPages}}',
          preview: 'پیش‌نمایش اسلایدهای انتخاب‌شده',
          previewError: 'ساخت پیش‌نمایش ناموفق بود',
          previewErrorDescription: 'انتخاب و تنظیمات صدا را بررسی و دوباره تلاش کنید.',
          previewResult: 'خلاصهٔ پیش‌نمایش',
          expectedJobs: '{{count}} کار لازم خواهد بود',
          approximateDuration: 'مدت تقریبی روایت: {{duration}}',
          durationMinutesSeconds: '{{minutes}} دقیقه و {{seconds}} ثانیه',
          durationSeconds: '{{seconds}} ثانیه',
          costUnavailable: 'هزینهٔ تخمینی: در دسترس نیست',
          dispatchDisabled: 'تولید در دسترس نیست',
          dispatchExplanation:
            'تأیید انسانی برای عملیات روایت پولی پیاده‌سازی نشده است. این پیش‌نمایش صوت تولید یا کاری را صف نمی‌کند.',
          generateDisabled: 'تولید روایت (در دسترس نیست)',
          actions: {
            ALREADY_PROCESSING: 'در حال انجام',
            GENERATE_REQUIRED: 'نیازمند تولید',
            REUSE_READY: 'صدای آماده قابل استفاده است',
            SKIP_NO_SCRIPT: 'بدون متن',
          },
          reasons: {
            CLASSIFICATION_POLICY_BLOCKED: 'مسدودشده بر اساس سیاست محتوا',
            PROVIDER_NOT_CONFIGURED: 'ارائه‌دهندهٔ صدا پیکربندی نشده',
            SCRIPT_LIMIT_EXCEEDED: 'متن از محدودیت بیشتر است',
            VOICE_PROFILE_UNAVAILABLE: 'صدا در دسترس نیست',
          },
        },
        fieldLabels: {
          title: 'عنوان', subtitle: 'زیرعنوان', logo: 'لوگو',
          description: 'توضیحات', headline: 'تیتر اصلی', eyebrow: 'روتیتر',
          caption: 'زیرنویس', body: 'متن', image: 'تصویر',
          text: 'متن', name: 'نام', label: 'برچسب', value: 'مقدار', icon: 'آیکون',
        },
        noFields: 'این قالب فیلد قابل ویرایشی ندارد.',
        textScale: {
          heading: 'اندازه متن',
          option: '{{percent}}٪',
          hint: 'متن این اسلاید را بزرگ‌تر یا کوچک‌تر کنید. اگر متن جا نشود، همچنان به‌طور خودکار کوچک می‌شود.',
          dynamicHtml:
            'اسلایدهای HTML پویا اندازه متن خود را کنترل می‌کنند، بنابراین ممکن است این گزینه اعمال نشود.',
          scaled: 'برای جا شدن در اسلاید به {{percent}}٪ کاهش یافت.',
          overflow:
            'این متن حتی در کوچک‌ترین اندازه در اسلاید جا نمی‌شود. آن را کوتاه کنید یا میان دو اسلاید تقسیم کنید.',
        },
        theme: {
          heading: 'تم',
          usePresentationTheme: 'استفاده از تم ارائه',
          useCustomTheme: 'سفارشی برای این اسلاید',
          resetToPresentationTheme: 'بازگشت به تم ارائه',
          colors: {
            primary: 'اصلی',
            background: 'پس‌زمینه',
            surface: 'سطح',
            text: 'متن',
            textMuted: 'کم‌رنگ',
            border: 'حاشیه',
          },
          invalidColor: 'یک رنگ معتبر CSS وارد کنید (مثلاً #6933ff).',
        },
        repeaterItemOf: '{{label}} {{index}} از {{count}}',
        dragItem: 'کشیدن {{label}} {{index}} برای جابه‌جایی',
        addRepeaterItem: 'افزودن {{label}}',
        duplicateItem: 'تکرار',
        deleteItem: 'حذف',
        moveItemUp: 'انتقال {{label}} {{index}} به بالا',
        moveItemDown: 'انتقال {{label}} {{index}} به پایین',
        repeaterMinReached: 'حداقل {{count}} {{label}} لازم است',
        repeaterMaxReached: 'حداکثر {{count}} {{label}} مجاز است',
        listField: {
          unsupported: '{{label}} را نمی‌توان اینجا ویرایش کرد.',
          fields: {
            title: 'عنوان',
            body: 'متن',
            description: 'توضیح',
            label: 'برچسب',
            text: 'متن',
            name: 'نام',
            value: 'مقدار',
            subtitle: 'زیرعنوان',
          },
        },
        deleteSlide: 'حذف اسلاید',
        dragSlideNamed: 'کشیدن اسلاید {{title}} برای جابه‌جایی',
        moveSlideUpNamed: 'انتقال اسلاید {{title}} به بالا',
        moveSlideDownNamed: 'انتقال اسلاید {{title}} به پایین',
        deleteSlideNamed: 'حذف اسلاید: {{title}}',
        deleteSlideTitle: 'این اسلاید حذف شود؟',
        deleteSlideDescription: 'اسلاید «{{title}}» از این ارائه حذف شود؟ این کار قابل بازگشت نیست. اسلایدهای دیگر تغییری نمی‌کنند.',
        deletingSlide: 'در حال حذف…',
        deleteSlideSaveError: 'اسلاید فعلی تغییرات ذخیره‌نشده دارد. پیش از حذف، آن‌ها را ذخیره یا رفع کنید.',
        deleteSlideUnavailable: 'حذف این اسلاید ممکن نیست. مجوز خود را بررسی کنید یا ارائه را بازخوانی کنید.',
        deleteSlideConflict: 'اسلاید یا ارائه در جای دیگری تغییر کرده است. پیش از تلاش دوباره، بازخوانی کنید.',
        deleteSlideError: 'حذف این اسلاید ممکن نشد. تغییری در سایر اسلایدها اعمال نشد.',
        conflict: 'این اسلاید جای دیگری تغییر کرده است. پیش از ادامه دوباره بارگیری کنید.',
        saveFailureTitle: 'تغییرات ذخیره نشده‌اند',
        saveFailureDescription: 'ذخیرهٔ خودکار متوقف شده است. تغییرات در همین ویرایشگر باقی مانده‌اند. کد خطا را بررسی و سپس ذخیره را دوباره امتحان کنید.',
        saveFailureCode: 'کد خطا',
        saveFailureRequest: 'شناسهٔ درخواست',
        retrySave: 'تلاش دوباره برای ذخیره',
        save: {
          idle: 'آماده',
          saving: 'در حال ذخیره…',
          saved: 'ذخیره شد',
          conflict: 'تعارض',
          error: 'ذخیره ناموفق',
        },
      },
      publishQa: {
        title: 'انتشار ارائه',
        description: 'پیش از زنده‌کردن این پیش‌نویس، بررسی‌های نسخهٔ تغییرناپذیر را مرور کنید.',
        checking: 'در حال بررسی ارائه…',
        passedChecks: 'بررسی‌های موفق ({{count}})',
        publishedToast: 'ارائه منتشر شد.',
        technicalDetails: 'جزئیات فنی',
        requestId: 'شناسهٔ درخواست',
        operationId: 'شناسهٔ عملیات',
        viewErrors: 'مشاهدهٔ خطاها',
        slides: 'اسلاید',
        errors: 'خطا',
        warnings: 'هشدار',
        loadError: 'بررسی‌های انتشار بارگیری نشدند',
        loadErrorDescription: 'بررسی‌ها را دوباره اجرا کنید. چیزی منتشر نشده است.',
        retry: 'تلاش دوباره',
        cancel: 'انصراف',
        publish: 'انتشار نسخه',
        starting: 'در حال شروع انتشار…',
        done: 'تمام',
        openSlide: 'باز کردن اسلاید {{number}}',
        acceptWarnings: 'انتشار با این هشدارها',
        acceptWarningsDescription: 'هشدارها را بررسی کردم و می‌خواهم ادامه دهم.',
        submitError: 'انتشار شروع نشد',
        failedTitle: 'انتشار ناموفق بود',
        successTitle: 'ارائه منتشر شد',
        successDescription:
          'نسخهٔ {{number}} اکنون زنده است. تغییرات بعدی پیش‌نویس آن را تغییر نمی‌دهد.',
        severity: { ERROR: 'مسدودکننده', WARNING: 'هشدار', PASSED: 'موفق' },
        operation: {
          loading: 'آماده‌سازی انتشار',
          loadingDescription: 'عملیات ماندگار انتشار در حال ساخت است…',
          QUEUED: {
            title: 'انتشار در صف است',
            description: 'نسخهٔ تغییرناپذیر منتظر worker رندر است.',
          },
          VALIDATING: {
            title: 'اعتبارسنجی نسخه',
            description: 'قالب‌های دقیق، تم، متغیرها و دارایی‌ها بررسی می‌شوند.',
          },
          RENDERING: {
            title: 'رندر نسخه',
            description: 'پیش‌نمایش اجتماعی و تصویرهای بندانگشتی اسلایدها ساخته می‌شوند.',
          },
          FINALIZING: {
            title: 'فعال‌سازی نسخه',
            description: 'گیت‌های کیفیت تکمیل و نسخهٔ زنده به‌صورت اتمیک جایگزین می‌شود.',
          },
        },
        failure: {
          PRESENTATION_CONFLICT:
            'پیش‌نویس تغییر کرده است. بررسی‌های تازه‌شده را پیش از انتشار مرور کنید.',
          PUBLISH_VALIDATION_FAILED: 'یک یا چند بررسی انتشار باید برطرف شود.',
          PUBLISH_ASSET_NOT_READY: 'یکی از دارایی‌های ارجاع‌شده آماده نیست.',
          PUBLISH_SOCIAL_PREVIEW_FAILED: 'پیش‌نمایش اجتماعی الزامی ساخته نشد.',
          PUBLISH_RENDER_FAILED: 'یکی از تصویرهای نسخه ساخته نشد.',
          PUBLISH_RELEASE_PREPARATION_FAILED:
            'آماده‌سازی نسخه پس از تلاش‌های خودکار ناموفق بود. نسخهٔ زندهٔ قبلی بدون تغییر مانده است.',
          PRESENTATION_PUBLISH_IN_PROGRESS: 'انتشار دیگری هم‌اکنون در حال اجراست.',
          default: 'نسخه آماده نشد. نسخهٔ زندهٔ قبلی بدون تغییر مانده است.',
        },
      },
      presentationVariables: {
        title: 'متغیرهای ارائه',
        description:
          'مقادیر قابل استفادهٔ مجدد در اسلایدها و شخصی‌سازی اختیاری پیوند اشتراک را تعریف کنید.',
        add: 'افزودن متغیر',
        empty: 'هنوز متغیری برای ارائه تعریف نشده است.',
        key: 'نام / کلید',
        type: 'نوع',
        defaultValue: 'مقدار پیش‌فرض',
        shareOverride: 'اجازه جایگزینی در پیوند اشتراک',
        remove: 'حذف',
        save: 'ذخیره متغیرها',
        saved: 'متغیرها ذخیره شدند.',
        conflict: 'ارائه جای دیگری تغییر کرده است. پیش از ذخیره متغیرها دوباره بارگیری کنید.',
        error: 'متغیرها ذخیره نشدند.',
        types: {
          STRING: 'متن',
          NUMBER: 'عدد',
          BOOLEAN: 'بولی',
          DATE: 'تاریخ',
          URL: 'نشانی وب',
          ASSET: 'نسخه دارایی',
        },
      },
      collaboration: {
        comments: 'نظرها',
        presence: 'افراد حاضر',
        viewing: 'در حال مشاهده',
        editing: 'در حال ویرایش',
        empty: 'نظری روی این اسلاید نیست',
        emptyDescription: 'بدون تغییر محتوای اسلاید، یک گفت‌وگوی متمرکز شروع کنید.',
        loadError: 'نظرها بارگیری نشدند.',
        addComment: 'افزودن نظر',
        placeholder: 'نظر خود را بنویسید…',
        send: 'ارسال',
        reply: 'پاسخ',
        resolve: 'حل‌شده',
        reopen: 'بازگشایی',
        replying: 'در حال پاسخ به رشته',
        cancel: 'انصراف',
        createError: 'نظر ارسال نشد.',
        readAll: 'خواندن همه',
        new: 'جدید',
        notification: {
          COMMENT_CREATED: 'نظر جدید روی ارائه',
          COMMENT_MENTION: 'از شما نام برده شد',
          COMMENT_REPLY: 'پاسخ جدید',
          REVIEW_UPDATE: 'بازبینی به‌روز شد',
        },
      },
      projectSettings: projectSettingsCopy.fa,
      project: {
        navigation: {
          label: 'پروژه',
          project: 'پروژه',
          overview: 'نمای کلی',
          presentations: 'ارائه‌ها',
          assets: 'دارایی‌ها',
          knowledge: 'دانش',
          aiProposals: 'پیشنهادهای هوش مصنوعی',
          theme: 'تم',
          access: 'دسترسی',
          settings: 'تنظیمات',
        },
        new: {
          back: 'بازگشت به پروژه‌ها',
          eyebrow: 'پروژه‌ها',
          title: 'ساخت پروژه',
          description: 'فضایی متمرکز برای ارائه‌ها، تم‌ها، دارایی‌ها و دانش بسازید.',
          detailsHeading: 'جزئیات',
          submit: 'ساخت پروژه',
          successToast: 'پروژه ساخته شد.',
        },
        fields: {
          name: 'نام پروژه',
          namePlaceholder: 'مثلاً بازطراحی وب‌سایت',
          description: 'توضیحات',
          descriptionPlaceholder: 'این پروژه برای چیست؟',
          descriptionTooLong: 'توضیحات را زیر ۱۰۰۰ نویسه نگه دارید.',
          classification: 'طبقه‌بندی داده',
          classificationHelp:
            'طبقه‌بندی مشخص می‌کند کدام فراهم‌کنندگان هوش مصنوعی و خارجی می‌توانند این پروژه را پردازش کنند.',
          required: 'نام پروژه الزامی است.',
        },
        classification: { NON_SENSITIVE: 'غیرحساس', INTERNAL: 'داخلی', CONFIDENTIAL: 'محرمانه' },
        status: { ACTIVE: 'فعال', ARCHIVED: 'بایگانی‌شده' },
        actions: { cancel: 'انصراف', retry: 'تلاش دوباره', save: 'ذخیره تغییرات' },
        errors: {
          denied: 'اجازه ساخت پروژه در این شرکت را ندارید.',
          generic: 'مشکلی رخ داد. دوباره تلاش کنید.',
          validation: 'فیلدهای مشخص‌شده را بررسی و دوباره تلاش کنید.',
          unavailableTitle: 'پروژه در دسترس نیست',
          unavailable: 'این پروژه موجود نیست یا به آن دسترسی ندارید.',
          conflict: 'این پروژه در جای دیگری تغییر کرده است. پیش از ذخیره دوباره بارگیری کنید.',
        },
        overview: {
          eyebrow: 'نمای کلی پروژه',
          title: 'نمای کلی',
          description: 'خلاصه‌ای متمرکز از این پروژه.',
          presentations: 'ارائه‌ها',
          classification: 'طبقه‌بندی',
          status: 'وضعیت',
          emptyTitle: 'هنوز ارائه‌ای نیست',
          emptyDescription: 'اولین ارائه این پروژه را بسازید.',
          recent: 'ارائه‌های اخیر',
          themeTitle: 'تم پروژه',
          themeDescription: 'توکن‌های برند و ارائه را یکپارچه نگه دارید.',
          openTheme: 'باز کردن تم',
          knowledgeTitle: 'دانش پروژه',
          knowledgeDescription: 'گردش‌کارهای آینده هوش مصنوعی را به منابع تأییدشده متصل کنید.',
          openKnowledge: 'باز کردن دانش',
        },
        settings: {
          title: 'تنظیمات پروژه',
          description: 'هویت، طبقه‌بندی و چرخه عمر پروژه را مدیریت کنید.',
          saved: 'تنظیمات پروژه ذخیره شد.',
        },
        assets: {
          title: 'دارایی‌ها',
          description: 'تصویرها، ویدئو و صدای پروژه.',
          emptyTitle: 'هنوز دارایی پروژه‌ای نیست',
          emptyDescription: 'بارگذاری و خط لوله دارایی در مرحله دارایی افزوده می‌شود.',
        },
        knowledge: {
          title: 'دانش',
          description: 'منابع تأییدشده این پروژه.',
          emptyTitle: 'هنوز منبع دانشی نیست',
          emptyDescription: 'ورود سند و URL در مرحله دانش افزوده می‌شود.',
        },
        theme: {
          title: 'تم',
          description: 'رنگ، تایپوگرافی، شکل و حرکت ارائه‌های پروژه.',
          emptyTitle: 'هنوز نسخه تمی نیست',
          emptyDescription: 'نخستین نسخه تغییرناپذیر تم در مرحله تم ساخته می‌شود.',
        },
        access: {
          title: 'دسترسی پروژه',
          description: 'دسترسی ارث‌برده از شرکت و مجوزهای مستقیم پروژه را بررسی کنید.',
          emptyTitle: 'مجوز مستقیم پروژه‌ای نیست',
          emptyDescription: 'اعضای سراسری شرکت ممکن است از قبل دسترسی ارث‌برده داشته باشند.',
        },
      },
      projectAccess: {
        title: 'دسترسی پروژه',
        description:
          'افراد دارای دسترسی پروژه، در سقف نقش شرکتی خود به همه ارائه‌های این پروژه دسترسی دارند.',
        errorTitle: 'دسترسی پروژه بارگیری نشد',
        empty: 'عضوی در شرکت نیست',
        emptyDescription: 'پیش از تخصیص دسترسی پروژه، عضوی را دعوت کنید.',
        member: 'عضو',
        companyRole: 'نقش شرکتی',
        source: 'منبع دسترسی',
        level: 'سطح پروژه',
        levelFor: 'سطح دسترسی پروژه برای {{name}}',
        inherited: 'ارث‌برده از شرکت',
        none: 'بدون دسترسی',
        viewer: 'بیننده',
        editor: 'ویرایشگر',
        results: '{{count}} عضو',
        pages: 'صفحه‌های دسترسی پروژه',
        remove: 'حذف دسترسی پروژه برای {{name}}',
        sources: { COMPANY: 'شرکت', PROJECT: 'پروژه', NONE: 'هیچ' },
        search: 'جستجوی اعضا',
        searchPlaceholder: 'جستجو با نام یا ایمیل',
        presentationOnly: '{{count}} دسترسی مستقیم به ارائه',
      },
      presentationAccess: {
        title: 'دسترسی ارائه',
        description:
          'دسترسی مستقیم به این ارائه را مدیریت کنید. دسترسی پروژه و شرکت به‌طور خودکار ارث‌بری می‌شود.',
        search: 'جستجوی اعضا',
        searchPlaceholder: 'جستجو با نام یا ایمیل',
        errorTitle: 'دسترسی ارائه بارگیری نشد',
        saveError: 'تغییر دسترسی ذخیره نشد.',
        conflictTitle: 'دسترسی جای دیگری تغییر کرده است',
        conflictDescription:
          'مدیر دیگری پس از بارگیری فهرست این دسترسی را تغییر داده است. چیزی بازنویسی نشد؛ فهرست اکنون وضعیت فعلی را نشان می‌دهد.',
        empty: 'عضوی پیدا نشد',
        emptyDescription: 'پیش از تخصیص دسترسی ارائه، عضوی را دعوت کنید.',
        inheritedAccess: 'دسترسی ارث‌برده',
        directGrant: 'دسترسی مستقیم',
        noInherited: 'ندارد',
        notNeeded: 'نیاز نیست (سراسری شرکت)',
        redundant: 'از قبل با دسترسی ارث‌برده پوشش داده شده است',
        none: 'بدون دسترسی مستقیم',
        inherited: { COMPANY: 'شرکت · {{level}}', PROJECT: 'پروژه · {{level}}' },
        levels: { VIEWER: 'بیننده', EDITOR: 'ویرایشگر' },
        directLevelFor: 'سطح دسترسی مستقیم {{name}}',
        remove: 'حذف دسترسی مستقیم ارائه برای {{name}}',
        pages: 'صفحه‌های دسترسی ارائه',
      },
      companySettings: {
        retry: 'تلاش دوباره',
        system: 'سیستم',
        errors: {
          title: 'تنظیمات شرکت بارگیری نشد',
          description: 'دوباره تلاش کنید یا دسترسی خود را بررسی کنید.',
          conflict: 'تنظیمات شرکت جای دیگری تغییر کرده است. پیش از ذخیره دوباره بارگیری کنید.',
          save: 'تنظیمات شرکت ذخیره نشد.',
        },
        identity: {
          title: 'شرکت',
          description: 'هویت شرکت و تنظیمات فضای کاری را مدیریت کنید.',
          name: 'نام شرکت',
          slug: 'شناسه شرکت',
          slugHelp:
            'در نشانی‌های مدیریتی استفاده می‌شود. تغییر شناسه در نسخه اول پشتیبانی نمی‌شود.',
          workspaceDescription: 'توضیحات',
          save: 'ذخیره تغییرات',
          saved: 'تنظیمات شرکت ذخیره شد.',
        },
        audit: {
          ...auditLogCopy.fa,
          title: 'گزارش ممیزی',
          description: 'فعالیت‌های ماندگار مدیریتی و منابع را مرور کنید.',
          time: 'زمان',
          actor: 'عامل',
          action: 'عملیات',
          resource: 'منبع',
          request: 'شناسه درخواست',
          results: '{{count}} رویداد',
          pages: 'صفحه‌های رویداد ممیزی',
        },
        security: {
          title: 'امنیت',
          description: 'سیاست امنیتی اجباری و رویدادهای امنیتی اخیر را مرور کنید.',
          strongMfa: 'احراز هویت قوی اعضای ممتاز',
          strongMfaHelp:
            'مالک‌ها و مدیران برای عملیات ممتاز باید از کلید عبور یا برنامه تأییدکننده استفاده کنند. کد ایمیل فقط روش پشتیبان است.',
          required: 'اجباری',
          recent: 'رویدادهای امنیتی اخیر',
          event: 'رویداد',
          severity: 'شدت',
          severityLevels: {
            INFO: 'اطلاع‌رسانی', WARNING: 'هشدار', HIGH: 'بالا', CRITICAL: 'بحرانی',
          },
          pages: 'صفحه‌های رویداد امنیتی',
        },
      },
      appearanceStudio: {
        styleDirection: {
          heading: 'جهت‌گیری سبک',
          intro:
            'سبک‌هایی را ببینید که این پروژه مجاز به استفاده از آن‌هاست، همراه با میزان سازگاری‌شان. این بخش فقط راهنماست.',
          open: 'مشاهدهٔ سبک‌ها',
          close: 'پنهان کردن سبک‌ها',
          note:
            'انتخاب سبک در این بخش، این ارائه را تغییر نمی‌دهد. ظاهر ارائه فقط با «اعمال» کنترل‌های بالا تغییر می‌کند. سبک هرگز چیدمان یا محتوای اسلایدها را تغییر نمی‌دهد.',
          compatibility: {
            heading: 'سازگاری با این ارائه',
            noSelection:
              'یک سبک را انتخاب کنید تا ببینید با این ارائه چقدر سازگار است. تا وقتی کنترل‌های بالا را اعمال نکنید، چیزی تغییر نمی‌کند.',
            checking: 'در حال بررسی سازگاری…',
            status: {
              'eligible-recommendation': 'سازگار از نظر ساختاری',
              'preview-only': 'فقط مفهوم',
              incompatible: 'سازگار نیست',
              'missing-or-stale-style': 'سبک دیگر فعال نیست',
            },
            statusDetail: {
              'eligible-recommendation':
                'همهٔ اسلایدها از نسخه‌های قالبی استفاده می‌کنند که این سبک پیشنهاد می‌کند. این فقط بررسی ساختاری است.',
              'preview-only': 'هنوز پیشنهاد قالب تأییدشده‌ای برای این سبک ثبت نشده است.',
              incompatible:
                'پیشنهاد قالب این سبک در دسترس نیست یا برخی اسلایدها از نسخهٔ قالب دیگری استفاده می‌کنند.',
              'missing-or-stale-style':
                'این نسخه و اثرانگشت دقیق در فهرست فعلی پروژه نیست. سبک را دوباره انتخاب کنید.',
            },
            notApplied: 'اعمال نشده است و آمادهٔ انتشار نیست.',
            visualReadiness: 'آمادگی بصری: نامشخص و تأیید نشده',
            capability: 'الزامات قابلیت: این سبک اعلام نکرده است',
            slidesPinned: 'اسلایدهای با نسخهٔ قالب دیگر: {{count}} از {{total}}',
            designKitRestricted: 'برخی قالب‌های این سبک توسط Design Kit این پروژه محدود شده‌اند.',
            recommended: 'نسخه‌های قالب پیشنهادی',
            slidesUsing: 'اسلایدهای با این نسخه: {{count}}',
            alternatives: 'سبک‌های جایگزین',
            altStatus: {
              'eligible-recommendation': 'سازگار از نظر ساختاری',
              'preview-only': 'فقط مفهوم',
              incompatible: 'سازگار نیست',
            },
            reasonsHeading: 'دلیل',
            reasons: {
              STYLE_IDENTITY_MISSING_OR_STALE:
                'این نسخه و اثرانگشت دقیق در فهرست فعلی پروژه وجود ندارد.',
              STYLE_HAS_NO_EVIDENCED_RECOMMENDATION:
                'پیشنهاد قالب تأییدشده‌ای برای این سبک ثبت نشده است.',
              NO_ELIGIBLE_RECOMMENDED_TEMPLATE:
                'در حال حاضر هیچ قالب پیشنهادی واجد شرایط برای این سبک وجود ندارد.',
              RECOMMENDED_TEMPLATE_UNAVAILABLE_OR_RESTRICTED:
                'قالب پیشنهادی در دسترس نیست یا طبق سیاست فعلی محدود شده است.',
              EXISTING_SLIDES_USE_OTHER_TEMPLATE_VERSIONS:
                'برخی اسلایدها از نسخهٔ قالبی غیر از پیشنهاد استفاده می‌کنند.',
              VISUAL_QA_NOT_VERIFIED: 'کیفیت بصری این سبک تأیید نشده است.',
              STYLE_CAPABILITY_REQUIREMENTS_NOT_DECLARED: 'این سبک الزامات قابلیت اعلام نکرده است.',
            },
            viewOnly:
              'فقط مشاهده: می‌توانید سازگاری را بررسی کنید، اما نمی‌توانید ظاهر این ارائه را تغییر دهید.',
            unavailable:
              'بررسی سازگاری در دسترس نیست: دسترسی شما به فهرست سبک‌های این پروژه یا به این ارائه کافی نیست. این به معنای ناسازگار بودن سبک نیست.',
            forbidden: 'اجازهٔ بررسی سازگاری این ارائه را ندارید.',
            session: 'برای بررسی سازگاری دوباره وارد شوید.',
            stale: 'سبک انتخاب‌شده تغییر کرده یا دیگر فعال نیست. دوباره آن را انتخاب کنید.',
            failed: 'بررسی سازگاری انجام نشد. دوباره تلاش کنید.',
            retry: 'تلاش دوباره',
          },
        },
        title: 'ظاهر',
        description:
          'ظاهر این ارائه را انتخاب کنید. اعمال تغییرات، پیش‌نویس و انتشار بعدی را به‌روز می‌کند؛ نسخه‌های منتشرشده هرگز تغییر نمی‌کنند.',
        errorTitle: 'ظاهر بارگیری نشد',
        retry: 'تلاش دوباره',
        saveError: 'ظاهر ذخیره نشد. دوباره تلاش کنید.',
        applied: 'ظاهر اعمال شد.',
        status: { saved: 'ذخیره‌شده', unsaved: 'تغییرات ذخیره‌نشده' },
        source: {
          heading: 'منبع تم',
          inherit: 'استفاده از تم پروژه',
          inheritHint: 'از تم پروژه پیروی می‌کند، از جمله تغییرات بعدی آن.',
          custom: 'سفارشی‌سازی برای این ارائه',
          customHint:
            'فقط گزینه‌هایی که تغییر می‌دهید جایگزین می‌شوند و بقیه از تم پروژه پیروی می‌کنند.',
          inheritedSwatches: 'رنگ‌های ارث‌رسیده از تم پروژه',
        },
        presets: {
          heading: 'پیش‌تنظیم‌ها',
          names: {
            'studio-light': 'استودیو روشن',
            midnight: 'نیمه‌شب',
            editorial: 'نشریه',
            forest: 'جنگل',
            ocean: 'اقیانوس',
            sunset: 'غروب',
            mono: 'تک‌رنگ',
            'persian-calm': 'آرامش پارسی',
          },
        },
        colors: {
          heading: 'رنگ‌ها',
          primary: 'اصلی',
          primaryContrast: 'متن روی رنگ اصلی',
          secondary: 'ثانویه',
          accent: 'تأکیدی',
          background: 'پس‌زمینه',
          surface: 'سطح',
          text: 'متن',
          textMuted: 'متن کم‌رنگ',
          hexFor: 'مقدار هگز {{label}}',
          hexInvalid: 'رنگی مانند ‎#1A2B3C وارد کنید.',
          useInherited: 'استفاده از {{label}} ارث‌رسیده',
          inherited: 'ارث‌رسیده',
          customized: 'سفارشی',
        },
        fonts: {
          heading: 'قلم عنوان',
          body: 'قلم متن',
          search: 'جستجوی قلم‌ها',
          noResults: 'قلمی پیدا نشد',
          groups: {
            latinSans: 'لاتین بدون سریف',
            latinSerif: 'لاتین سریف',
            latinDisplay: 'لاتین نمایشی',
            latinMono: 'لاتین تک‌عرض',
            persianSans: 'فارسی / عربی بدون سریف',
            persianSerif: 'فارسی / عربی سریف',
            persianDisplay: 'نمایشی / خوشنویسی فارسی',
          },
          inherited: 'ارث‌رسیده ({{family}})',
          names: {
            inter: 'Inter',
            'ibm-plex-sans': 'IBM Plex Sans',
            'space-grotesk': 'Space Grotesk',
            'source-serif-4': 'Source Serif 4',
            'playfair-display': 'Playfair Display',
            vazirmatn: 'وزیرمتن',
            'noto-sans-arabic': 'نوتو سنس عربی',
            'noto-naskh-arabic': 'نوتو نسخ عربی',
            roboto: 'روبوتو',
            'open-sans': 'اوپن سنس',
            montserrat: 'مونتسرات',
            'dm-sans': 'دی‌ام سنس',
            manrope: 'منروپ',
            'plus-jakarta-sans': 'پلاس جاکارتا سنس',
            'nunito-sans': 'نونیتو سنس',
            raleway: 'ریل‌وی',
            figtree: 'فیگ‌تری',
            lora: 'لورا',
            merriweather: 'مری‌ودر',
            oswald: 'اسوالد',
            fraunces: 'فرانسس',
            'jetbrains-mono': 'جت‌برینز مونو',
            estedad: 'استعداد',
            cairo: 'قاهره',
            changa: 'چانگا',
            'el-messiri': 'المسیری',
            mada: 'مدی',
            'reem-kufi': 'ریم کوفی',
            amiri: 'امیری',
            'markazi-text': 'مرکزی',
            'noto-nastaliq-urdu': 'نوتو نستعلیق اردو',
            lalezar: 'لاله‌زار',
            katibeh: 'کتیبه',
            gulzar: 'گلزار',
          },
        },
        style: {
          heading: 'شکل و سبک',
          inherited: 'ارث‌رسیده',
          radius: 'گردی گوشه‌ها',
          neutral: 'تُن خنثی',
          surface: 'سبک سطح',
          radii: { sharp: 'تیز', soft: 'نرم', round: 'گرد' },
          neutrals: { slate: 'سنگ‌لوح', zinc: 'روی', stone: 'سنگی', warm: 'گرم', cool: 'سرد' },
          surfaces: { flat: 'تخت', raised: 'برجسته', outlined: 'قاب‌دار' },
        },
        preview: {
          heading: 'پیش‌نمایش زنده',
          slide: 'اسلاید پیش‌نمایش',
          slideOption: '{{number}}. {{title}}',
          noSlides: 'برای دیدن پیش‌نمایش ظاهر، یک اسلاید اضافه کنید.',
          note: 'پیش‌نمایش پیش‌نویس ذخیره‌نشده شما را نشان می‌دهد و از حالت روشن یا تاریک برنامه تأثیر نمی‌گیرد.',
        },
        actions: {
          group: 'اقدام‌های ظاهر',
          shuffle: 'ترکیب تصادفی',
          reset: 'بازگشت به ارث‌رسیده',
          discard: 'لغو تغییرات',
          apply: 'اعمال',
        },
        readOnly: {
          title: 'فقط مشاهده',
          description:
            'می‌توانید این ظاهر را ببینید، اما برای تغییر آن به دسترسی ویرایشگر نیاز دارید.',
        },
        conflict: {
          title: 'ظاهر جای دیگری تغییر کرده است',
          description: 'این ظاهر هنگام ویرایش شما توسط شخص دیگری به‌روز شد.',
          useLatest: 'استفاده از آخرین نسخه',
          keepMine: 'نگه داشتن تغییرات من',
        },
        support: {
          title: 'ظاهر ممکن است کامل اعمال نشود',
          unknown:
            'برخی قالب‌های این ارائه مشخص نکرده‌اند کدام گزینه‌های ظاهر را پشتیبانی می‌کنند، پس نتیجه ممکن است با پیش‌نمایش فرق کند.',
          unsupported: 'قالب اسلاید پیش‌نمایش این موارد را اعمال نمی‌کند: {{options}}.',
          options: {
            colors: 'رنگ‌ها',
            fonts: 'قلم‌ها',
            radius: 'گردی گوشه‌ها',
            surface: 'سبک سطح',
          },
        },
        contrast: {
          title: 'کنتراست کم',
          text: 'کنتراست متن با پس‌زمینه {{ratio}}:1 است. WCAG AA حداقل 4.5:1 را توصیه می‌کند.',
          textMuted:
            'کنتراست متن کم‌رنگ با پس‌زمینه {{ratio}}:1 است. WCAG AA حداقل 4.5:1 را توصیه می‌کند.',
          primary: 'کنتراست متن روی رنگ اصلی {{ratio}}:1 است. WCAG AA حداقل 4.5:1 را توصیه می‌کند.',
        },
        leave: {
          title: 'تغییرات ظاهر ذخیره‌نشده لغو شود؟',
          description: 'تغییراتی دارید که اعمال نشده‌اند. با خروج، آن‌ها از بین می‌روند.',
          stay: 'ادامه ویرایش',
          discard: 'لغو و خروج',
        },
      },
      themeEditor: {
        title: 'تم پروژه',
        description: 'نسخه‌های تغییرناپذیر تم را برای رنگ، تایپوگرافی و حرکت ارائه بسازید.',
        errorTitle: 'تم پروژه بارگیری نشد',
        conflict: 'پروژه جای دیگری تغییر کرده است. پیش از ساخت نسخه تم جدید بارگیری کنید.',
        saveError: 'نسخه تم ساخته نشد.',
        createVersion: 'ساخت نسخه تم',
        previewEyebrow: 'پیش‌نمایش زنده',
        previewTitle: 'داستانی روشن با برندی یکپارچه',
        previewBody:
          'قالب‌ها توکن‌های معنایی را مصرف می‌کنند و برای هر نمای ارائه بازچینی می‌شوند.',
        previewAction: 'دیدن داستان',
        history: 'تاریخچه نسخه‌ها',
        noVersions: 'هنوز نسخه تمی نیست. پیش‌نمایش از تم امن پیش‌فرض Slider استفاده می‌کند.',
        version: 'نسخه {{version}}',
        createdBy: 'ساخته‌شده توسط {{name}}',
        createdByLabel: 'ساخته‌شده توسط',
        swatchFor: 'انتخاب رنگ {{label}}',
        current: 'پیش‌فرض پروژه',
        colors: {
          primary: 'اصلی',
          secondary: 'ثانویه',
          accent: 'تأکیدی',
          background: 'پس‌زمینه',
          surface: 'سطح',
          text: 'متن',
          textMuted: 'متن کم‌رنگ',
        },
      },
      templateUse: templateUseCopy.fa,
      templateCatalog: {
        eyebrow: 'سریع‌تر بسازید',
        title: 'قالب‌ها',
        description: 'الگوهای ارائه قابل اعتماد و واکنش‌گرا و قابلیت‌هایشان را مرور کنید.',
        heroBadge: 'هر هفته قالب‌های جدید',
        heroCard: 'ایده‌ها را به اثرگذاری تبدیل کنید',
        taxonomy: {
          categories: {
            story: 'داستان', metrics: 'شاخص‌ها', media: 'رسانه',
            marketing: 'بازاریابی', general: 'عمومی', custom: 'سفارشی',
            comparison: 'مقایسه', timeline: 'خط زمانی',
          },
          tags: {
            cover: 'جلد', intro: 'مقدمه', story: 'داستان',
            media: 'رسانه', narrative: 'روایت', split: 'تقسیمی',
            'executive-summary': 'خلاصه مدیریتی', kpi: 'شاخص کلیدی', metrics: 'شاخص‌ها',
            brand: 'برند', hero: 'معرفی اصلی', data: 'داده‌ها',
            comparison: 'مقایسه', timeline: 'خط زمانی',
            chart: 'نمودار', visual: 'تصویری', product: 'محصول',
          },
        },
        highlights: {
          customizable: 'کاملاً قابل شخصی‌سازی',
          performance: 'طراحی‌شده برای عملکرد بهتر',
          time: 'صرفه‌جویی در زمان',
        },
        search: 'جستجوی قالب‌ها',
        searchPlaceholder: 'جستجوی قالب‌ها…',
        categoryLabel: 'فیلتر بر اساس دسته',
        allCategories: 'همه دسته‌ها',
        clearFilters: 'پاک کردن فیلترها',
        useTemplate: 'استفاده از قالب',
        preview: 'پیش‌نمایش {{name}}',
        previewUnavailable: 'پیش‌نمایش موجود نیست',
        version: 'نسخهٔ {{version}}',
        cardActions: 'اقدام‌های قالب {{name}}',
        statuses: {
          ACTIVE: 'فعال', DRAFT: 'پیش‌نویس', RETIRED: 'بازنشسته',
          REJECTED: 'ردشده', PUBLISHED: 'منتشرشده',
        },
        duplicate: 'کپی کردن',
        delete: 'حذف',
        moreTags: '{{count}} برچسب بیشتر',
        errorTitle: 'قالب‌ها بارگیری نشدند',
        empty: 'قالبی پیدا نشد',
        emptyDescription: 'جستجو یا دسته دیگری را امتحان کنید.',
        results: '{{count}} قالب',
        showing: 'نمایش {{count}} از {{total}} قالب',
        pages: 'صفحه‌های قالب',
        back: 'بازگشت به قالب‌ها',
        edit: 'ویرایش قالب',
        editAria: 'ویرایش {{name}}',
        bestFor: 'مناسب برای',
        capabilities: 'قابلیت‌ها',
        fields: 'فیلدهای ویرایشگر',
        fieldNameHeader: 'فیلد',
        fieldKeyHeader: 'کلید',
        fieldTypeHeader: 'نوع',
        required: 'الزامی',
        createDynamicHtml: 'قالب HTML پویا',
        addTemplate: 'افزودن قالب جدید',
        addTemplateManual: 'ساخت دستی',
        addTemplateFromFile: 'وارد کردن از فایل',
        capabilityNames: {
          actions: 'اقدام‌ها',
          chart: 'نمودار',
          'interactive-detail': 'تعاملی',
          media: 'رسانه',
          narration: 'روایت صوتی',
          'semantic-visibility': 'واکنش‌گرا',
        },
        fieldTypeNames: {
          asset: 'تصویر یا رسانه',
          boolean: 'کلید روشن/خاموش',
          currency: 'ارز',
          date: 'تاریخ',
          enum: 'انتخابی',
          link: 'پیوند',
          list: 'فهرست',
          number: 'عدد',
          percentage: 'درصد',
          text: 'متن',
          textarea: 'متن بلند',
        },
        previewViewport: 'اندازه پیش‌نمایش',
        parametersHeading: 'پارامترهای صفحه (JSON)',
      },
      templateCategories: {
        actions: 'اقدام‌ها',
        allTypes: 'همهٔ نوع‌ها',
        byName: 'نام (الفبا)',
        clearFilters: 'پاک‌کردن فیلترها',
        confirmDeleteDescription:
          'دستهٔ خالی «{{name}}» حذف شود؟ این کار برگشت‌پذیر نیست.',
        confirmDeleteTitle: 'حذف دسته؟',
        deleteCategory: 'حذف دستهٔ {{name}}',
        editCategory: 'ویرایش دستهٔ {{name}}',
        filterType: 'فیلتر نوع دسته',
        mostTemplates: 'بیشترین قالب',
        newest: 'جدیدترین به‌روزرسانی',
        noMatches: 'دسته‌ای پیدا نشد',
        noMatchesDescription: 'عبارت دیگری جستجو کنید یا فیلترها را پاک کنید.',
        oldest: 'قدیمی‌ترین به‌روزرسانی',
        searchLabel: 'جستجوی دسته‌ها',
        searchPlaceholder: 'جستجوی دسته‌ها…',
        sortLabel: 'مرتب‌سازی دسته‌ها',
        summaryLabel: 'آمار دسته‌ها',
        cancel: 'لغو',
        create: 'ساخت دسته',
        createDescription: 'فیلترهای قالب را با نام، ترتیب و توضیح‌های دقیق و مرتب نگه دارید.',
        createTitle: 'دسته جدید',
        creating: 'در حال ساخت…',
        delete: 'حذف',
        deleteBlocked: 'فقط دسته‌های خالی قابل حذف هستند.',
        description: 'دسته‌های رجیستری قالب را برای نویسندگان و کارت‌های قالب مدیریت کنید.',
        descriptionField: 'توضیح',
        descriptionPlaceholder: 'توضیح کوتاه داخلی',
        edit: 'ویرایش',
        empty: 'هنوز دسته‌ای نیست',
        emptyDescription: 'برای مرتب کردن قالب‌ها اولین دسته را بسازید.',
        errorDescription:
          'ممکن است نام دسته تکراری باشد، یا این دسته هنوز توسط قالب‌ها استفاده شود.',
        errorTitle: 'تغییر دسته ذخیره نشد',
        eyebrow: 'سیستم قالب',
        loadError: 'دسته‌ها بارگیری نشدند',
        name: 'نام',
        namePlaceholder: 'مثلاً روایت',
        noDescription: 'بدون توضیح',
        save: 'ذخیره',
        custom: 'سفارشی',
        systemReadOnly: 'داخلی، فقط خواندنی',
        system: 'سیستمی',
        sortOrder: 'ترتیب',
        templateCount: '{{count}} قالب',
        templates: 'قالب‌ها',
        title: 'دسته‌ها',
        totalCategories: 'دسته‌ها',
        totalTemplates: 'قالب‌های متصل',
        updated: 'به‌روزرسانی',
      },
      templateImport: {
        title: 'وارد کردن قالب از فایل',
        description:
          'یک فایل zip شامل یک فایل HTML و عکس‌های ارجاع‌شده در آن بارگذاری کنید. آن را وارد می‌کنیم و پیش‌نمایش قالب را باز می‌کنیم.',
        errors: {
          notAZip: 'لطفاً یک فایل zip انتخاب کنید.',
          TEMPLATE_IMPORT_INVALID_ARCHIVE: 'این فایل به‌عنوان بایگانی قالب قابل خواندن نبود.',
          TEMPLATE_IMPORT_NO_HTML: 'هیچ فایل HTML‌ای در بایگانی پیدا نشد.',
          TEMPLATE_IMPORT_TOO_LARGE: 'این بایگانی بزرگ‌تر از محدودیت مجاز است.',
          default: 'فایل وارد نشد. لطفاً دوباره تلاش کنید.',
        },
        status: {
          processing: 'در حال خواندن بایگانی…',
          uploading: 'در حال بارگذاری… {{progress}}٪',
        },
      },
      templateFork: {
        createAction: 'ایجاد فورک',
        createTitle: 'ایجاد فورک',
        createDescription:
          'یک پیش‌نویس مستقل و قابل ویرایش از {{name}} (نسخهٔ {{version}}) بسازید. قالب مبدأ هرگز تغییر نمی‌کند.',
        nameLabel: 'نام فورک',
        namePlaceholder: 'مثلاً نتایج فصلی – منطقه‌ای',
        createSubmit: 'ایجاد فورک',
        cancel: 'انصراف',
        created: 'فورک ایجاد شد. اکنون می‌توانید آن را به‌صورت پیش‌نویس ویرایش کنید.',
        createErrors: {
          default: 'فورک ایجاد نشد. دوباره تلاش کنید.',
          ACCESS_DENIED: 'اجازهٔ ایجاد فورک را ندارید.',
          TEMPLATE_SOURCE_NOT_FORKABLE: 'از این نسخهٔ قالب نمی‌توان فورک گرفت.',
          RESOURCE_NOT_FOUND: 'این نسخهٔ قالب دیگر وجود ندارد.',
          IDEMPOTENCY_OPERATION_IN_PROGRESS:
            'فورک هنوز در حال ایجاد است. کمی بعد دوباره تلاش کنید.',
        },
        ...templateForkRejectionCopy.fa.list,
        draftRevision: 'بازبینی {{revision}}',
        draftSource: 'فورک‌شده از {{name}} · نسخهٔ {{version}}',
        draftUpdated: 'به‌روزرسانی {{date}}',
        continueDraft: 'ادامهٔ ویرایش',
        editor: {
          back: 'بازگشت به قالب‌ها',
          title: 'پیش‌نویس فورک',
          loadError: 'بارگذاری این پیش‌نویس ممکن نشد.',
          notFound: 'این پیش‌نویس پیدا نشد یا به آن دسترسی ندارید.',
          statusEditing: 'پیش‌نویس · بازبینی {{revision}}',
          statusPublished: 'منتشرشده · بازبینی {{revision}}',
          statusAbandoned: 'رهاشده · بازبینی {{revision}}',
          unsaved: 'تغییرات ذخیره‌نشده',
          readOnlyNotice: 'این پیش‌نویس دیگر برای ویرایش باز نیست.',
          viewOnlyNotice:
            'می‌توانید این پیش‌نویس را ببینید اما نقش شما اجازهٔ ویرایش آن را نمی‌دهد.',
          save: 'ذخیرهٔ پیش‌نویس',
          saved: 'پیش‌نویس به‌صورت بازبینی {{revision}} ذخیره شد.',
          saveErrors: {
            default: 'ذخیرهٔ پیش‌نویس ممکن نشد.',
            ACCESS_DENIED: 'اجازهٔ ویرایش این پیش‌نویس را ندارید.',
            TEMPLATE_DRAFT_INVALID: 'پیش‌نویس معتبر نیست. مشکلات زیر را برطرف و دوباره ذخیره کنید.',
            TEMPLATE_DRAFT_STATE_CONFLICT: 'این پیش‌نویس دیگر برای ویرایش باز نیست.',
            TEMPLATE_REVISION_REJECTED:
              'این بازبینی رد شده و چیزی تغییر نکرده است. ابتدا منبع را ویرایش کنید، سپس برای ساخت بازبینی جدید ذخیره کنید.',
          },
          invalidJson: 'داده‌ها (JSON) باید یک شیء JSON معتبر باشند.',
          conflictTitle: 'این پیش‌نویس جای دیگری تغییر کرده است',
          conflictDescription:
            'بازبینی جدیدتری ذخیره شده است، بنابراین تغییرات شما ذخیره نشد. برای ادامه، آخرین بازبینی را بارگذاری کنید؛ ویرایش‌های ذخیره‌نشدهٔ شما جایگزین می‌شوند.',
          conflictReload: 'بارگذاری آخرین بازبینی',
          problemsTitle: 'مشکلاتی که باید برطرف شوند',
          validationTitle: 'خطاهای اعتبارسنجی',
          warningsTitle: 'هشدارها',
          ...templateForkRejectionCopy.fa.editor,
          sourceHeading: 'منبع قالب',
          sourceNotice:
            'فیلدها و بخش‌های تکرارشونده مانند نسخهٔ مبدأ باقی می‌مانند. در اینجا فقط HTML، CSS و داده‌های نمونه ویرایش می‌شوند.',
          previewHeading: 'پیش‌نمایش',
          previewTitle: 'پیش‌نمایش پیش‌نویس',
          previewBadge: 'پوستهٔ پیش‌فرض',
          previewDisclaimer:
            'با پوستهٔ پیش‌فرض نمایش داده شده است. پوستهٔ کیت طراحی پروژه اعمال نشده است، پس این پیش‌نمایش نسخهٔ نهایی نیست.',
          previewRevision: 'پیش‌نمایش بازبینی ذخیره‌شدهٔ {{revision}}',
          previewUnsavedNote: 'ویرایش‌های ذخیره‌نشده تا زمان ذخیره نمایش داده نمی‌شوند.',
          previewRefresh: 'تازه‌سازی پیش‌نمایش',
          previewLoading: 'در حال آماده‌سازی پیش‌نمایش…',
          previewExpired:
            'پیوند این پیش‌نمایش منقضی شده است. برای بارگذاری دوباره، آن را تازه‌سازی کنید.',
          previewUnavailable: 'برای این بازبینی پیش‌نمایشی در دسترس نیست.',
          previewError: 'ایجاد پیش‌نمایش ممکن نشد.',
          previewIssuesTitle: 'پیش‌نمایش نمایش داده نشد',
          approvalHeading: 'بررسی و انتشار',
          approvalNone: 'این بازبینی تأیید نشده است.',
          approvalCurrent: 'بازبینی {{revision}} در {{date}} تأیید شد.',
          approvalFingerprint: 'اثر انگشت {{fingerprint}}…',
          approvalNeedsSave: 'پیش از تأیید، تغییرات خود را ذخیره کنید.',
          approvalNeedsValid: 'پیش از تأیید، خطاهای اعتبارسنجی را برطرف کنید.',
          approvalNeedsPublisher: 'فقط ناشر قالب می‌تواند تأیید و منتشر کند.',
          reviewState: {
            PENDING: 'در انتظار بررسی',
            APPROVED: 'تأییدشده',
            REJECTED: 'ردشده',
          },
          diagnosticsCount_one: '{{count}} مورد',
          diagnosticsCount_other: '{{count}} مورد',
          reject: 'رد بازبینی',
          rejectTitle: 'همین بازبینی دقیق رد شود؟',
          rejectDescription:
            'با رد بازبینی {{revision}} (اثر انگشت {{fingerprint}}…) دیگر نمی‌توان آن را تأیید یا منتشر کرد. ذخیرهٔ یک ویرایش، بازبینی جدیدی می‌سازد که دوباره در انتظار بررسی قرار می‌گیرد.',
          rejectReasonLabel: 'دلیل (اختیاری)',
          rejectConfirm: 'رد بازبینی',
          rejected: 'بازبینی {{revision}} رد شد.',
          rejectionCurrent: 'بازبینی {{revision}} در {{date}} رد شد.',
          rejectionReason: 'دلیل: {{reason}}',
          rejectionNext: 'پیش‌نویس را ویرایش و ذخیره کنید تا بازبینی جدیدی برای بررسی ساخته شود.',
          rejectErrors: {
            default: 'رد بازبینی ممکن نشد.',
            ACCESS_DENIED: 'اجازهٔ بررسی قالب‌ها را ندارید.',
            TEMPLATE_DRAFT_CONFLICT:
              'پیش‌نویس تغییر کرده است. پیش از رد کردن، آخرین بازبینی را بارگذاری کنید.',
            TEMPLATE_APPROVAL_STALE:
              'پیش‌نویس از زمان باز کردن شما تغییر کرده است. دوباره بارگذاری و بررسی کنید.',
            TEMPLATE_APPROVAL_CONFLICT: 'شخص دیگری پیش‌تر این بازبینی را رد کرده است.',
            TEMPLATE_DRAFT_STATE_CONFLICT: 'این پیش‌نویس دیگر برای بررسی باز نیست.',
          },
          approve: 'تأیید بازبینی',
          approveTitle: 'این بازبینی دقیقاً تأیید شود؟',
          approveDescription:
            'تأیید بازبینی {{revision}} (اثر انگشت {{fingerprint}}…) امکان انتشار آن را فراهم می‌کند. هر ویرایش بعدی این تأیید را باطل می‌کند.',
          approveConfirm: 'تأیید',
          approved: 'بازبینی {{revision}} تأیید شد.',
          approveErrors: {
            default: 'تأیید بازبینی ممکن نشد.',
            ACCESS_DENIED: 'اجازهٔ تأیید قالب‌ها را ندارید.',
            TEMPLATE_DRAFT_CONFLICT:
              'پیش‌نویس تغییر کرده است. پیش از تأیید، آخرین بازبینی را بارگذاری کنید.',
            TEMPLATE_APPROVAL_STALE:
              'پیش‌نویس از زمان باز کردن شما تغییر کرده است. دوباره بارگذاری و بررسی کنید.',
            TEMPLATE_DRAFT_INVALID: 'پیش‌نویس معتبر نیست و تأیید نمی‌شود.',
            TEMPLATE_APPROVAL_CONFLICT: 'این بازبینی از قبل تأییدی متناقض دارد.',
            TEMPLATE_REVISION_REJECTED:
              'این بازبینی رد شده است. آن را ویرایش کنید تا بازبینی جدیدی برای بررسی ساخته شود.',
          },
          publish: 'انتشار به‌عنوان نسخهٔ جدید',
          publishTitle: 'این بازبینی تأییدشده منتشر شود؟',
          publishDescription:
            'این کار یک نسخهٔ فعال جدید از بازبینی تأییدشدهٔ {{revision}} می‌سازد. قالب مبدأ و اسلایدهای موجود تغییر نمی‌کنند.',
          publishConfirm: 'انتشار',
          published: 'به‌عنوان نسخهٔ {{version}} منتشر شد.',
          publishErrors: {
            default: 'انتشار پیش‌نویس ممکن نشد.',
            ACCESS_DENIED: 'اجازهٔ انتشار قالب‌ها را ندارید.',
            TEMPLATE_DRAFT_CONFLICT:
              'پیش‌نویس تغییر کرده است. آخرین بازبینی را بارگذاری و دوباره بررسی کنید.',
            TEMPLATE_APPROVAL_REQUIRED: 'این بازبینی باید پیش از انتشار تأیید شود.',
            TEMPLATE_APPROVAL_STALE: 'تأیید دیگر با پیش‌نویس مطابقت ندارد. دوباره تأیید کنید.',
            TEMPLATE_PUBLISH_AUTHORIZATION_CHANGED:
              'مجوز انتشار شما تغییر کرده است. دوباره بارگذاری و تلاش کنید.',
            TEMPLATE_DRAFT_STATE_CONFLICT: 'این پیش‌نویس دیگر برای انتشار باز نیست.',
            TEMPLATE_REVISION_REJECTED:
              'این بازبینی رد شده است. آن را ویرایش کنید تا بازبینی جدیدی برای بررسی ساخته شود.',
          },
          publishedHeading: 'منتشرشده',
          publishedVersion: 'به‌عنوان نسخهٔ {{version}} منتشر شد',
          viewPublished: 'مشاهدهٔ قالب منتشرشده',
          provenanceHeading: 'خاستگاه',
          provenanceSource: 'فورک‌شده از {{templateId}} · نسخهٔ {{version}}',
          provenanceCreated: 'ایجاد {{date}}',
          provenancePublished: 'نسخه‌های منتشرشده',
          provenanceNone: 'هنوز منتشر نشده است.',
          provenanceVersion: 'نسخهٔ {{version}}',
        },
      },
      templateStudio: {
        title: 'ایجاد قالب HTML پویا',
        editTitle: 'ویرایش قالب',
        editNotice:
          'ذخیره‌سازی همین قالب را برای همه به‌روزرسانی می‌کند؛ هر اسلایدی که از قبل از این قالب استفاده می‌کند نیز این تغییر را نشان خواهد داد.',
        editSaveSuccess: 'قالب به‌روزرسانی شد.',
        back: 'بازگشت به قالب‌ها',
        detailsHeading: 'جزئیات',
        nameLabel: 'نام',
        namePlaceholder: 'مثلاً شبکه کاربردها',
        categoryLabel: 'دسته',
        categoryPlaceholder: 'مثلاً story',
        markupHeading: 'کد قالب',
        htmlLabel: 'HTML',
        cssLabel: 'CSS',
        jsonLabel: 'داده (JSON)',
        structureHeading: 'ساختار قابل‌ویرایش',
        fieldsLabel: 'فیلدها',
        elementsLabel: 'المان‌ها',
        fieldsHelp:
          'هر فیلدی که نویسنده بتواند بدون دست‌زدن به HTML/CSS/JSON ویرایش کند. برای مسیر تکراری از یک [] استفاده کنید، مثلاً cards[].title.',
        compiledHelp:
          'این قالب کدنویسی‌شده است، بنابراین elementها به‌صورت اطلاعات قالب نمایش داده می‌شوند.',
        fieldIndex: 'فیلد {{index}}',
        fieldPathLabel: 'مسیر فیلد',
        fieldPathPlaceholder: 'مثلاً headline یا cards[].title',
        fieldLabelLabel: 'برچسب فیلد',
        fieldLabelPlaceholder: 'مثلاً Headline',
        fieldTypeLabel: 'نوع فیلد',
        fieldType: { text: 'متن', longText: 'متن بلند', asset: 'تصویر', link: 'پیوند' },
        addField: 'افزودن فیلد',
        removeField: 'حذف فیلد',
        chooseAsset: 'انتخاب دارایی',
        replaceAsset: 'جایگزینی',
        removeAsset: 'حذف',
        noAssetSelected: 'هیچ عکس یا آیکنی انتخاب نشده است',
        extractTextFields: 'استخراج متن از HTML',
        extractTextFieldsHelp:
          'عنوان، توضیحات متا و دیگر متن‌های ساده را از HTML استخراج می‌کند و به فیلدهای قابل‌ویرایش و JSON داده اضافه می‌کند.',
        extractTextFieldsNone:
          'متن جدیدی برای استخراج پیدا نشد — همهٔ متن‌های قابل‌مشاهده از قبل فیلد شده‌اند.',
        extractTextFieldsAdded: '{{count}} فیلد از متن HTML اضافه شد.',
        fieldValueLabel: 'مقدار فعلی (از JSON داده)',
        fieldValueEmpty: '(خالی)',
        repeatersLabel: 'گروه‌های تکرارشونده',
        repeatersHelp:
          'مشخص کنید کدام آرایه تکرارشونده (مثلاً «cards») قابلیت افزودن/حذف/تکرار/جابه‌جایی داشته باشد و حداقل/حداکثر تعداد آن را تعیین کنید.',
        repeaterIndex: 'گروه تکرارشونده {{index}}',
        repeaterLabelLabel: 'برچسب گروه',
        repeaterLabelPlaceholder: 'مثلاً Cards',
        repeaterPathLabel: 'مسیر گروه',
        repeaterPathPlaceholder: 'مثلاً cards',
        repeaterMinLabel: 'حداقل تعداد',
        repeaterMaxLabel: 'حداکثر تعداد',
        addRepeater: 'افزودن گروه تکرارشونده',
        removeRepeater: 'حذف گروه',
        warningsTitle: 'برخی فیلدها نیاز به بررسی دارند',
        previewTitle: 'پیش‌نمایش',
        openLargePreview: 'باز کردن پیش‌نمایش بزرگ',
        exitLargePreview: 'خروج از پیش‌نمایش بزرگ',
        previewEmpty: 'برای مشاهدهٔ پیش‌نمایش، خطاهای زیر را برطرف کنید.',
        errorsTitle: 'این قالب هنوز قابل ذخیره نیست',
        save: 'ذخیرهٔ قالب',
        saveSuccess: 'قالب ذخیره شد و برای استفاده در «افزودن اسلاید» آماده است.',
        saveFailed: 'ذخیرهٔ این قالب ممکن نشد',
        themeTokensLabel: 'متغیرهای تم',
        themeTokensHelp:
          'از این متغیرهای تم ارائه در CSS خود استفاده کنید تا قالب به‌طور خودکار با هر ارائه و هر بازنویسی سطح‌اسلاید سازگار شود.',
        copyToken: 'کپی',
        copiedToken: 'در کلیپ‌بورد کپی شد',
      },
      assets: {
        title: 'دارایی‌ها',
        description: 'تصویر، ویدئو، صدا و دیگر رسانه‌های قابل استفادهٔ مجدد شرکت را مدیریت کنید.',
        projectEyebrow: 'دارایی‌های پروژه',
        upload: 'بارگذاری دارایی‌ها',
        uploading: 'در حال بارگذاری…',
        uploadingProgress: 'در حال بارگذاری {{done}} از {{total}}…',
        batchUploaded_one: '{{count}} فایل بارگذاری شد. پردازش در پس‌زمینه ادامه دارد.',
        batchUploaded_other: '{{count}} فایل بارگذاری شد. پردازش در پس‌زمینه ادامه دارد.',
        batchFailed_one: '{{count}} فایل ناموفق بود: {{names}}.',
        batchFailed_other: '{{count}} فایل ناموفق بود: {{names}}.',
        selectAllOnPage: 'انتخاب همهٔ موارد صفحه',
        selectAsset: 'انتخاب {{title}}',
        selectedCount: '{{count}} مورد انتخاب شد',
        clearSelection: 'پاک کردن',
        deleteSelected: 'حذف انتخاب‌شده‌ها',
        bulkDeleteTitle_one: '{{count}} دارایی حذف شود؟',
        bulkDeleteTitle_other: '{{count}} دارایی حذف شود؟',
        bulkDeleteDescription:
          'دارایی‌های انتخاب‌شده از کتابخانه و انتخابگرها حذف می‌شوند. دارایی‌هایی که انتشارهای منتشرشده نگه می‌دارند حذف نمی‌شوند و انتخاب‌شده می‌مانند.',
        bulkDeleteConfirm_one: 'حذف {{count}} دارایی',
        bulkDeleteConfirm_other: 'حذف {{count}} دارایی',
        bulkDeleting: 'در حال حذف…',
        bulkDeletePartial_one: '{{count}} دارایی حذف نشد و همچنان انتخاب‌شده است.',
        bulkDeletePartial_other: '{{count}} دارایی حذف نشد و همچنان انتخاب‌شده‌اند.',
        processing: 'بارگذاری کامل شد. در حال پردازش…',
        uploadFailed: 'بارگذاری ناموفق بود',
        search: 'جستجوی دارایی‌ها',
        searchPlaceholder: 'جستجوی دارایی‌ها…',
        view: 'نمای دارایی‌ها',
        grid: 'شبکه',
        list: 'فهرست',
        companyScope: 'شرکت',
        empty: 'هنوز دارایی‌ای نیست',
        filteredEmpty: 'دارایی‌ای با این فیلترها پیدا نشد',
        emptyDescription:
          'تصویر، ویدئو، صدا و دیگر رسانه‌ها را برای استفاده در ارائه‌ها بارگذاری کنید.',
        errorTitle: 'دارایی‌ها بارگیری نشدند',
        results: '{{count}} دارایی',
        pages: 'صفحه‌های دارایی',
        usages: '{{count}} استفاده',
        back: 'بازگشت به دارایی‌ها',
        detailEyebrow: 'جزئیات دارایی',
        replace: 'جایگزینی',
        download: 'دریافت فایل اصلی',
        name: 'نام دارایی',
        descriptionField: 'توضیحات',
        altText: 'متن جایگزین',
        tags: 'برچسب‌ها (با ویرگول جدا کنید)',
        versions: 'نسخه‌ها',
        version: 'نسخه {{version}}',
        usedIn: 'استفاده‌شده در',
        noUsages: 'هیچ پیش‌نویس ارائه، انتشار منتشرشده یا پوسته‌ای به این دارایی ارجاع نمی‌دهد.',
        processingFailed: 'پردازش ناموفق بود. فایل را دوباره بارگذاری کنید.',
        processingUnavailable: 'وضعیت پردازش بررسی نشد.',
        processingTimedOut:
          'پردازش بیش از حد انتظار طول می‌کشد. دوباره بررسی کنید یا این پیام را ببندید.',
        checkAgain: 'بررسی دوباره',
        dismiss: 'بستن پیام',
        delete: 'حذف دارایی',
        deleteTitle: 'این دارایی حذف شود؟',
        deleteDescription:
          'دارایی از کتابخانه حذف و از انتخاب‌گرها پنهان می‌شود. دیگر نمی‌توان آن را به محتوای جدید افزود.',
        deleteInUse:
          'این دارایی در {{count}} جا استفاده شده است. حذف آن را از کتابخانه و انتخاب‌گرها پنهان می‌کند. نسخه‌های منتشرشده و سایر ارجاع‌های نگهداری‌شده همان چیزی را که قبلاً استفاده می‌کردند حفظ می‌کنند.',
        deleteConfirm: 'حذف دارایی',
        deleteBlocked: 'محتوای منتشرشده این دارایی را نگه داشته و حذف آن ممکن نیست.',
        deleteForbidden: 'اجازهٔ حذف این دارایی را ندارید.',
        deleteUnavailable: 'این دارایی دیگر در دسترس نیست.',
        deleteFailed: 'حذف دارایی انجام نشد. دوباره تلاش کنید.',
        details: 'جزئیات',
        unit: { B: 'بایت', KB: 'کیلوبایت', MB: 'مگابایت' },
        editDetails: 'ویرایش جزئیات',
        factType: 'نوع',
        factDimensions: 'ابعاد',
        factOriginalFile: 'فایل اصلی',
        factOriginalSize: 'اندازهٔ فایل اصلی',
        factDelivery: 'نسخهٔ مورد استفاده در ارائه‌ها',
        factScope: 'دامنه',
        factCreated: 'افزوده‌شده',
        factUpdated: 'به‌روزرسانی',
        createdBy: '{{name}} · {{date}}',
        deliveryOriginal: 'فایل اصلی (بدون نسخهٔ بهینه‌شده)',
        deliveryWebpLossless: 'WebP بدون افت کیفیت · {{size}}',
        deliveryWebpLossy: 'WebP با کیفیت بالا · {{size}}',
        deliverySaved: '{{percent}}٪ کوچک‌تر از فایل اصلی',
        currentVersion: 'فعلی',
        versionOriginalSize: 'فایل اصلی {{size}}',
        versionUploaded: 'بارگذاری در {{date}}',
        downloadVersion: 'دریافت فایل اصلی نسخهٔ {{version}}',
        downloadShort: 'دریافت',
        usedInSlide: '{{presentation}} · اسلاید {{number}}',
        untitledPresentation: 'ارائهٔ بدون عنوان',
        usageVersion: 'نسخهٔ {{version}}',
        usageKind: {
          DRAFT: 'پیش‌نویس',
          RELEASE: 'انتشار منتشرشده',
          THEME: 'پوسته',
          KNOWLEDGE: 'دانش',
        },
        status: {
          PENDING_UPLOAD: 'در حال بارگذاری',
          UPLOADED: 'در حال پردازش',
          VALIDATING: 'در حال پردازش',
          PROCESSING: 'در حال پردازش',
          READY: 'آماده',
          FAILED: 'ناموفق',
          QUARANTINED: 'مسدودشده',
          DELETED: 'حذف‌شده',
        },
        kind: {
          IMAGE: 'تصویر',
          VIDEO: 'ویدئو',
          AUDIO: 'صدا',
          LOGO: 'لوگو',
          ICON: 'نماد',
          LOTTIE: 'انیمیشن',
          MODEL_3D: 'مدل سه‌بعدی',
          DOCUMENT: 'سند',
        },
        ...assetFoldersCopy.fa,
        ...assetUploadCopy.fa,
      },
      settings: { title: 'تنظیمات شرکت', navigationLabel: 'تنظیمات شرکت' },
      aiProvider: aiProviderCopy.fa,
      security: {
        account: 'حساب',
        title: 'امنیت',
        description: 'احراز هویت قوی، گزینه‌های بازیابی و دستگاه‌های واردشده را مدیریت کنید.',
        loading: 'در حال بارگیری امنیت حساب',
        loadingSessions: 'در حال بارگیری دستگاه‌های واردشده',
        errorTitle: 'تنظیمات امنیت بارگیری نشد',
        errorDescription: 'دوباره وارد شوید یا بعداً تلاش کنید.',
        sessionsErrorTitle: 'نشست‌ها بارگیری نشدند',
        passkeys: 'کلیدهای عبور',
        passkeysDescription:
          'برای ورود مقاوم در برابر فیشینگ از زیست‌سنجی یا PIN دستگاه استفاده کنید.',
        addPasskey: 'افزودن کلید عبور',
        noPasskeys: 'کلید عبوری افزوده نشده است.',
        thisDevice: 'این دستگاه',
        passkeyError: 'کلید عبور افزوده نشد.',
        authenticator: 'برنامه تأییدکننده',
        authenticatorDescription: 'از کد زمان‌دار برنامه تأییدکننده استفاده کنید.',
        addAuthenticator: 'افزودن تأییدکننده',
        noAuthenticator: 'برنامه تأییدکننده‌ای افزوده نشده است.',
        authenticatorApp: 'برنامه تأییدکننده',
        recoveryCodes: 'کدهای بازیابی',
        recoveryRemaining: '{{count}} کد استفاده‌نشده باقی مانده',
        regenerate: 'ساخت کدهای جدید',
        saveCodes: 'این کدها را اکنون ذخیره کنید. کدهای قبلی دیگر معتبر نیستند.',
        savedCodes: 'کدها را ذخیره کردم',
        sessions: 'نشست‌ها',
        signedInDevices: 'دستگاه‌های واردشده',
        sessionsDescription: 'دستگاه‌هایی را که اکنون وارد حساب هستند بررسی کنید.',
        signOutOthers: 'خروج نشست‌های دیگر',
        currentSession: 'این دستگاه',
        unknownDevice: 'دستگاه ناشناس',
        current: 'فعلی',
        signOut: 'خروج',
        signOutSessionFor: 'خروج نشست دستگاه: {{device}}',
        lastSeen: 'آخرین فعالیت {{date}}',
        added: 'افزوده‌شده در {{date}}',
        remove: 'حذف',
        removePasskeyFor: 'حذف کلید عبور: {{name}}',
        removeAuthenticatorAdded: 'حذف تأییدکننده افزوده‌شده در {{date}}',
      },
      team: {
        membersTitle: 'اعضا',
        membersDescription: 'نقش‌ها و دسترسی اعضا به پروژه‌ها و ارائه‌ها را مدیریت کنید.',
        invitationsTitle: 'دعوت‌نامه‌ها',
        invitationsDescription: 'دعوت‌نامه‌های در انتظار و چرخهٔ عمر آن‌ها را مدیریت کنید.',
        inviteMembers: 'دعوت اعضا',
        newInvitation: 'دعوت‌نامه جدید',
        newInvitationDescription: 'یک نفر را دعوت کنید و سطح دسترسی او را تعیین کنید.',
        searchMembers: 'جستجوی اعضا',
        searchMembersPlaceholder: 'جستجوی نام یا ایمیل…',
        searchInvitations: 'جستجوی دعوت‌نامه‌ها',
        searchInvitationsPlaceholder: 'جستجوی ایمیل دعوت‌شده…',
        member: 'عضو',
        email: 'ایمیل',
        emailRequired: 'نشانی ایمیل را وارد کنید.',
        role: 'نقش',
        access: 'دسترسی',
        status: 'وضعیت',
        actions: 'عملیات',
        expires: 'انقضا',
        allRoles: 'همه نقش‌ها',
        allAccess: 'همه دسترسی‌ها',
        allStatuses: 'همه وضعیت‌ها',
        roles: { OWNER: 'مالک', ADMIN: 'مدیر', EDITOR: 'ویرایشگر', VIEWER: 'بیننده' },
        scopes: { ALL: 'همه منابع شرکت', RESTRICTED: 'محدود' },
        statuses: { ACTIVE: 'فعال', SUSPENDED: 'تعلیق‌شده' },
        invitationStatuses: {
          PENDING: 'در انتظار',
          ACCEPTED: 'پذیرفته‌شده',
          EXPIRED: 'منقضی‌شده',
          REVOKED: 'لغوشده',
        },
        emailDelivery: 'ایمیل دعوت‌نامه',
        deliveryStatuses: {
          QUEUED: 'در صف ارسال',
          ACCEPTED: 'پذیرفته‌شده توسط سرویس ایمیل',
          RETRYING: 'در حال تلاش مجدد',
          FAILED: 'ناموفق',
          SUPPRESSED: 'ارسال نشد',
          UNKNOWN: 'نامشخص',
        },
        deliveryDescriptions: {
          QUEUED: 'در انتظار ارسال.',
          ACCEPTED: 'به سرویس ایمیل تحویل داده شد. رسیدن به صندوق ورودی تأیید نشده است.',
          RETRYING: 'آخرین تلاش ناموفق بود. تلاش بعدی زمان‌بندی شده است.',
          FAILED: 'همهٔ تلاش‌ها ناموفق بود. برای تلاش دوباره، دعوت‌نامه را دوباره ارسال کنید.',
          SUPPRESSED: 'ارسال ایمیل غیرفعال است و ایمیلی ارسال نشد.',
          UNKNOWN: 'وضعیت ایمیل برای این دعوت‌نامه ثبت نشده است.',
        },
        manageAccess: 'مدیریت دسترسی',
        loadingMembers: 'در حال بارگیری اعضا',
        loadingMember: 'در حال بارگیری دسترسی عضو',
        loadingInvitations: 'در حال بارگیری دعوت‌نامه‌ها',
        noMembers: 'هنوز عضوی وجود ندارد',
        noMembersMatch: 'عضوی با این فیلترها پیدا نشد',
        noMembersDescription: 'یک هم‌تیمی دعوت کنید یا فیلترها را تغییر دهید.',
        noInvitations: 'هنوز دعوت‌نامه‌ای وجود ندارد',
        noInvitationsDescription: 'هر زمان آمادهٔ همکاری بودید، یک هم‌تیمی دعوت کنید.',
        memberResults: '{{count}} عضو',
        invitationResults: '{{count}} دعوت‌نامه',
        memberPages: 'صفحه‌های اعضا',
        invitationPages: 'صفحه‌های دعوت‌نامه‌ها',
        previous: 'قبلی',
        next: 'بعدی',
        page: 'صفحه {{page}}',
        resend: 'ارسال دوباره',
        revoke: 'لغو',
        resendFor: 'ارسال دوباره دعوت‌نامه به {{email}}',
        revokeFor: 'لغو دعوت‌نامه برای {{email}}',
        revokeConfirmationTitle: 'دعوت‌نامه لغو شود؟',
        revokeConfirmationDescription:
          'دعوت‌نامهٔ {{email}} دیگر معتبر نخواهد بود. اعضای فعلی حذف نمی‌شوند.',
        invitationActionFailed: 'عملیات دعوت‌نامه انجام نشد. وضعیت فعلی را بررسی و دوباره تلاش کنید.',
        sendInvitation: 'ارسال دعوت‌نامه',
        cancel: 'انصراف',
        privilegedAllAccess: 'مالک و مدیر همیشه به همه منابع شرکت دسترسی دارند.',
        ownerRoleOwnerOnly: 'فقط مالک می‌تواند نقش مالک را اعطا کند.',
        staleAccessTitle: 'دسترسی در جای دیگری تغییر کرد',
        staleAccessDescription:
          'مدیر دیگری هنگام ویرایش شما دسترسی این عضو را تغییر داد. ذخیره کردن اکنون تغییر او را بازنویسی می‌کند. برای کنار گذاشتن ویرایش‌های خود و دیدن دسترسی فعلی، بارگذاری مجدد کنید.',
        staleAccessReload: 'بارگذاری دسترسی فعلی',
        ownerRoleLocked: 'این عضو مالک است. فقط مالک می‌تواند نقش یا دسترسی او را تغییر دهد.',
        searchProjects: 'جستجوی پروژه‌ها',
        searchProjectsPlaceholder: 'جستجوی نام پروژه…',
        projectsSelected_one: '{{count}} پروژه انتخاب شده',
        projectsSelected_other: '{{count}} پروژه انتخاب شده',
        loadingProjects: 'در حال بارگذاری پروژه‌ها',
        projectsError: 'پروژه‌ها بارگذاری نشدند. دسترسی‌های موجود حفظ می‌شوند.',
        noProjectsFound: 'پروژه‌ای پیدا نشد.',
        presentationAccess: 'دسترسی ارائه',
        presentationAccessDescription:
          'دسترسی مستقیم به ارائه‌های مشخص. پروژه یا ارائه‌های دیگر آن را شامل نمی‌شود.',
        presentationGrantsList: 'ارائه‌هایی که این فرد مستقیم به آن‌ها دسترسی دارد',
        presentationUnavailable: 'در دسترس نیست',
        presentationGrantLevel: 'سطح دسترسی به {{name}}',
        removePresentationGrant: 'حذف دسترسی به {{name}}',
        noPresentationGrants: 'دسترسی مستقیمی به ارائه وجود ندارد.',
        searchPresentations: 'جستجوی ارائه‌ها',
        searchPresentationsPlaceholder: 'جستجوی ارائه‌ها',
        loadingPresentations: 'در حال بارگیری ارائه‌ها',
        presentationsError: 'ارائه‌ها بارگیری نشدند.',
        noPresentationsFound: 'ارائه‌ای پیدا نشد.',
        presentationPages: 'صفحه‌های ارائه',
        projectPages: 'صفحه‌های پروژه',
        grantLevels: { EDITOR: 'امکان ویرایش', VIEWER: 'فقط مشاهده' },
        projectAccess: 'دسترسی پروژه',
        projectAccessDescription: 'پروژه‌هایی را که این فرد می‌تواند ببیند انتخاب کنید.',
        invitationError: 'دعوت‌نامه ایجاد نشد. اطلاعات را بررسی و دوباره تلاش کنید.',
        memberAccessTitle: 'دسترسی {{name}}',
        externalIdentityTitle: 'هویت خارجی',
        externalIdentityDescription:
          'یک هویت Auth0 را به این حساب انتخاب‌شدهٔ Slider پیوند دهید. احراز هویت قوی اخیر و دسترسی امنیتی شرکت لازم است.',
        externalIdentityStart: 'ادامه با Auth0',
        externalIdentityConfirmLabel: 'تأیید هویت خارجی برای {{name}}',
        externalIdentityConfirm:
          'هویتی را که همین حالا در Auth0 تأیید کردید به {{name}} پیوند دهید؟ این هویت همان دسترسی Slider این حساب را خواهد داشت.',
        externalIdentityConfirmAction: 'تأیید پیوند',
        externalIdentitySuccess: 'هویت خارجی پیوند داده شد.',
        externalIdentityError: 'پیوند هویت کامل نشد. فرایند را دوباره آغاز کنید.',
        saveAccess: 'ذخیره دسترسی',
        updateSuccess: 'دسترسی عضو به‌روزرسانی شد.',
        updateError: 'دسترسی عضو به‌روزرسانی نشد.',
        removeMember: 'حذف عضو',
        removeConfirmation: 'این عضو از شرکت حذف شود؟',
        confirmRemove: 'حذف',
        errorTitle: 'اطلاعات تیم بارگیری نشد',
        errorUnavailable: 'این شرکت در دسترس نیست یا مجوز کافی ندارید.',
        errorGeneric: 'مشکلی پیش آمد. دوباره تلاش کنید.',
        retry: 'تلاش دوباره',
      },
      projects: {
        title: 'پروژه‌ها',
        description: 'ارائه‌ها، پوسته‌ها، دارایی‌ها و دانش را بر اساس هر هدف سازمان‌دهی کنید.',
        newProject: 'پروژه جدید',
        openProject: 'باز کردن {{name}}',
        cardActions: 'اقدام‌های {{name}}',
        delete: 'حذف',
        deleteTitle: 'پروژه حذف شود؟',
        deleteDescription:
          'این کار {{name}} و ارائه‌های آن را از نماهای فعال حذف می‌کند. تاریخچهٔ انتشار و تحلیل‌ها ممکن است بر اساس سیاست نگهداری شوند.',
        deleteConfirm: 'حذف پروژه',
        deleteError: 'پروژه حذف نشد. دوباره تلاش کنید.',
        noDescription: 'هنوز توضیحی ندارد.',
        loading: 'در حال بارگیری پروژه‌ها',
        searchLabel: 'جستجوی پروژه‌ها',
        searchPlaceholder: 'جستجوی پروژه‌ها…',
        statusLabel: 'وضعیت پروژه',
        allStatuses: 'همه وضعیت‌ها',
        active: 'فعال',
        archived: 'بایگانی‌شده',
        sortLabel: 'مرتب‌سازی پروژه‌ها',
        sortUpdated: 'آخرین به‌روزرسانی',
        sortNameAscending: 'نام الف تا ی',
        sortNameDescending: 'نام ی تا الف',
        clearFilters: 'پاک کردن فیلترها',
        emptyTitle: 'هنوز پروژه‌ای وجود ندارد',
        emptyDescription: 'برای سازمان‌دهی ارائه‌ها، پوسته‌ها، دارایی‌ها و دانش یک پروژه بسازید.',
        filteredEmptyTitle: 'پروژه‌ای با این فیلترها پیدا نشد',
        filteredEmptyDescription: 'عبارت دیگری جستجو کنید یا فیلترهای فعلی را پاک کنید.',
        presentationCount: '{{count}} ارائه',
        updated: 'به‌روزرسانی {{time}}',
        results: '{{count}} پروژه',
        paginationLabel: 'صفحه‌های پروژه',
        previousPage: 'قبلی',
        nextPage: 'بعدی',
        pageLabel: 'صفحه {{page}}',
        updating: 'در حال به‌روزرسانی پروژه‌ها',
        errorTitle: 'پروژه‌ها بارگیری نشدند',
        errorAuthentication: 'برای ادامه در این فضای کاری دوباره وارد شوید.',
        errorUnavailable: 'این فضای کاری در دسترس نیست یا دیگر به آن دسترسی ندارید.',
        errorGeneric: 'هنگام بارگیری پروژه‌ها مشکلی پیش آمد. دوباره تلاش کنید.',
        retry: 'تلاش دوباره',
      },
      shares: {
        playback: viewerPlaybackCopy.fa.settings,
        title: 'پیوندهای اشتراک',
        description: 'پیوندهایی با قواعد دسترسی، شخصی‌سازی و تحلیل جداگانه بسازید.',
        newShareLink: 'پیوند اشتراک تازه',
        newTitle: 'پیوند اشتراک تازه',
        editTitle: 'ویرایش پیوند اشتراک',
        backToShares: 'بازگشت به پیوندهای اشتراک',
        linkDetails: 'جزئیات پیوند',
        whoCanView: 'چه کسی می‌تواند ببیند؟',
        summary: 'خلاصه',
        name: 'نام',
        link: 'پیوند',
        access: 'دسترسی',
        status: 'وضعیت',
        views: 'بازدید',
        lastViewed: 'آخرین بازدید',
        created: 'ایجاد',
        createdBy: 'ایجادکننده',
        revokedAt: 'باطل‌شده',
        actions: 'اقدام‌ها',
        usage: 'استفاده',
        never: 'هرگز',
        none: 'هیچ',
        unnamed: 'پیوند بدون نام',
        open: 'باز کردن',
        edit: 'ویرایش',
        copyLink: 'کپی پیوند',
        linkCopied: 'پیوند اشتراک کپی شد.',
        linkCopyFailed: 'پیوند کپی نشد.',
        expiresAt: 'انقضا',
        password: 'گذرواژه',
        newPassword: 'گذرواژهٔ تازه',
        passwordUnchanged: 'برای حفظ گذرواژهٔ فعلی خالی بگذارید',
        allowedEmails: 'نشانی‌های مجاز',
        searchRecipients: 'جست‌وجوی اعضای شرکت',
        noRecipientsFound: 'عضوی با این جست‌وجو پیدا نشد.',
        removeRecipient: 'حذف {{name}}',
        personalization: 'شخصی‌سازی',
        personalizationCount: 'جایگزینی‌ها',
        personalizationDescription:
          'متغیرهای ارائه را که قابل اشتراک علامت خورده‌اند جایگزین کنید.',
        personalizationPreviewNote:
          'پیش‌نمایش اجتماعی کامل فقط برای پیوندهای عمومی است و هرگز مقادیر مخصوص گیرنده را شامل نمی‌شود.',
        overrideValue: 'مقدار جایگزین',
        presentationDefault: 'پیش‌فرض ارائه: {{value}}',
        liveRelease: 'نسخهٔ زنده',
        releaseNumber: 'نسخهٔ {{number}}',
        notPublished: 'منتشر نشده',
        socialPreview: 'پیش‌نمایش اجتماعی',
        publicPreview: 'پیش‌نمایش عمومی',
        protectedPreview: 'پیش‌نمایش محافظت‌شده',
        createShareLink: 'ساخت پیوند اشتراک',
        saveChanges: 'ذخیرهٔ تغییرات',
        created_success: 'پیوند اشتراک ساخته شد.',
        updated: 'پیوند اشتراک به‌روزرسانی شد.',
        revoke: 'ابطال پیوند',
        revokeTitle: 'این پیوند اشتراک باطل شود؟',
        revokeDescription:
          'هر کسی که از این پیوند استفاده می‌کند بلافاصله دسترسی خود را از دست می‌دهد. تحلیل‌های موجود حفظ می‌شود.',
        revokeConfirm: 'ابطال پیوند',
        revokeFailed: 'نتیجهٔ ابطال پیوند تأیید نشد. وضعیت پیوند را بررسی کنید و دوباره تلاش کنید.',
        revoked: 'پیوند اشتراک باطل شد.',
        cancel: 'انصراف',
        retry: 'تلاش دوباره',
        publishFirst: 'پیش از ساخت پیوند نمایش، این ارائه را منتشر کنید.',
        publishFirstDescription:
          'پیوند اشتراک همیشه آخرین نسخهٔ منتشرشده را باز می‌کند، پس اکنون چیزی نشان نمی‌دهد.',
        empty: 'هنوز پیوند اشتراکی نیست',
        emptyDescription: 'برای اشتراک این ارائه با مخاطب، پیوندی بسازید.',
        loadError: 'پیوندهای اشتراک بارگیری نشدند',
        loadErrorDescription: 'دوباره تلاش کنید یا بعداً به این ارائه بازگردید.',
        loadErrorForbidden: 'به این ارائه دسترسی ندارید.',
        notFound: 'پیوند اشتراک پیدا نشد',
        notFoundDescription: 'ممکن است این پیوند حذف شده باشد.',
        saveError: 'پیوند اشتراک ذخیره نشد',
        pages: 'صفحه‌های پیوند اشتراک',
        page: 'صفحه {{page}}',
        previous: 'قبلی',
        next: 'بعدی',
        statuses: { ACTIVE: 'فعال', EXPIRED: 'منقضی', REVOKED: 'باطل' },
        accessTypes: {
          PUBLIC: {
            label: 'عمومی',
            description: 'هر کسی که پیوند را داشته باشد می‌تواند ارائه را ببیند.',
          },
          PASSWORD: {
            label: 'محافظت‌شده با گذرواژه',
            description: 'هر کسی که پیوند و گذرواژه را داشته باشد می‌تواند ببیند.',
          },
          AUTHENTICATED: {
            label: 'کاربران واردشده',
            description: 'فقط کاربران احراز هویت‌شدهٔ Slider می‌توانند ببینند.',
          },
          COMPANY_ONLY: {
            label: 'فقط شرکت',
            description: 'فقط اعضای این شرکت می‌توانند ببینند.',
          },
          SPECIFIC_USERS: {
            label: 'کاربران مشخص',
            description: 'فقط کاربران Slider که انتخاب می‌کنید می‌توانند ببینند.',
          },
          SPECIFIC_EMAILS: {
            label: 'نشانی‌های مشخص',
            description: 'فقط گیرندگان تأییدشده در نشانی‌هایی که فهرست می‌کنید می‌توانند ببینند.',
          },
        },
        failure: {
          SHARE_LINK_CONFLICT: 'این پیوند از زمان بارگیری تغییر کرده است. صفحه را تازه کنید.',
          SHARE_LINK_POLICY_INVALID: 'سیاست دسترسی انتخاب‌شده معتبر نیست.',
          PRESENTATION_VARIABLE_INVALID: 'یکی از مقادیر شخصی‌سازی با نوع متغیر همخوان نیست.',
          ACCESS_DENIED: 'اجازهٔ مدیریت پیوندهای اشتراک را در اینجا ندارید.',
          default: 'پیوند اشتراک ذخیره نشد. دوباره تلاش کنید.',
        },
      },
      knowledge: {
        title: 'دانش پروژه',
        description: 'منابع مرجعی که نویسندگی هوش مصنوعی می‌تواند از آن‌ها استفاده کند.',
        addSource: 'افزودن منبع دانش',
        addSourceAction: 'افزودن منبع',
        sourceKind: 'نوع',
        sourceTitle: 'عنوان',
        sourceText: 'متن',
        sourceUrl: 'نشانی اینترنتی',
        textHint: 'واقعیت‌ها، اعداد و ادعاهای تأییدشده. اطلاعات محرمانه وارد نکنید.',
        instructionHint:
          'راهنمای پایدار این پروژه، مانند لحن، اصطلاحات یا ادعاهایی که باید پرهیز شود.',
        urlHint: 'تنها صفحات عمومی HTTP و HTTPS خوانده می‌شوند. نشانی‌های داخلی پذیرفته نمی‌شوند.',
        source: 'منبع',
        type: 'نوع',
        status: 'وضعیت',
        chunks: 'قطعه‌ها',
        updated: 'به‌روزرسانی',
        actions: 'کنش‌ها',
        reingest: 'پردازش دوبارهٔ منبع',
        delete: 'حذف منبع',
        created: 'منبع افزوده شد. پردازش آغاز شده است.',
        deleted: 'منبع حذف شد.',
        reingestRequested: 'پردازش دوباره آغاز شد.',
        emptyTitle: 'هنوز منبع دانشی وجود ندارد',
        emptyDescription:
          'یک سند، صفحه یا راهنمای نوشتاری بیفزایید تا هوش مصنوعی مبنایی داشته باشد.',
        unavailableTitle: 'دانش بارگذاری نشد',
        confidentialTitle: 'این پروژه محرمانه طبقه‌بندی شده است',
        confidentialBody:
          'دانش آن هرگز به ارائه‌دهندهٔ هوش مصنوعی‌ای که برای دادهٔ محرمانه تأیید نشده ارسال نمی‌شود.',
        pages: 'صفحه‌های دانش',
        page: 'صفحهٔ {{page}}',
        next: 'بعدی',
        previous: 'قبلی',
        types: {
          FILE: 'فایل',
          URL: 'نشانی',
          TEXT: 'متن',
          INSTRUCTION: 'دستورالعمل',
          STRUCTURED_DATA: 'داده',
          PRESENTATION_REFERENCE: 'ارائه',
        },
        statuses: {
          PENDING: 'در صف',
          PROCESSING: 'در حال پردازش',
          READY: 'آماده',
          FAILED: 'ناموفق',
        },
        errors: {
          KNOWLEDGE_SOURCE_URL_NOT_ALLOWED: 'این نشانی از این استقرار قابل دریافت نیست.',
          KNOWLEDGE_INGESTION_FAILED: 'منبع خوانده نشد.',
          KNOWLEDGE_SOURCE_NOT_READY: 'فایل مربوطه هنوز در حال پردازش است.',
          KNOWLEDGE_SOURCE_NOT_FOUND: 'این منبع دانش دیگر وجود ندارد.',
          KNOWLEDGE_SOURCE_CONFLICT: 'منبع پس از بارگذاری تغییر کرده است. دوباره بارگذاری کنید.',
          ACCESS_DENIED: 'اجازهٔ مدیریت دانش این پروژه را ندارید.',
          PROJECT_NOT_FOUND: 'این پروژه وجود ندارد یا به آن دسترسی ندارید.',
          default: 'منبع دانش ذخیره نشد. دوباره تلاش کنید.',
        },
      },
      ai: {
        title: 'نویسندگی هوش مصنوعی',
        blocked: {
          NOT_CONFIGURED: 'برای این استقرار ارائه‌دهندهٔ هوش مصنوعی پیکربندی نشده است.',
          DATA_POLICY: 'ارائه‌دهندهٔ پیکربندی‌شده برای طبقه‌بندی دادهٔ این پروژه تأیید نشده است.',
          NONE: '',
        },
        noKnowledge: 'این پروژه هنوز دانش آماده‌ای ندارد، بنابراین متن تولیدشده مستند نخواهد بود.',
        generatePlan: 'ساخت طرح',
        planPrompt: 'این ارائه باید چه کاری انجام دهد؟',
        planAudience: 'مخاطب',
        planSlideCount: 'تعداد تقریبی اسلاید',
        planning: 'در حال طراحی…',
        planReady: 'طرح آماده است',
        planSources: 'منابع استفاده‌شده',
        planNoSources: 'هیچ منبع دانشی این طرح را پشتیبانی نکرد؛ متن را پیش‌نویس در نظر بگیرید.',
        reviewProposals: 'بررسی پیشنهادهای هوش مصنوعی',
        errors: {
          AI_DATA_POLICY_BLOCKED: 'این پروژه به ارائه‌دهندهٔ پیکربندی‌شده ارسال نمی‌شود.',
          AI_PROVIDER_NOT_CONFIGURED: 'برای این استقرار ارائه‌دهندهٔ هوش مصنوعی پیکربندی نشده است.',
          AI_PROVIDER_UNAVAILABLE: 'ارائه‌دهنده در دسترس نبود. کمی بعد دوباره تلاش کنید.',
          AI_PROVIDER_RATE_LIMITED: 'ارائه‌دهنده درخواست‌ها را محدود کرده است. کمی بعد تلاش کنید.',
          AI_OUTPUT_INVALID: 'پاسخ هوش مصنوعی با ساختار لازم مطابقت نداشت.',
          AI_CONTEXT_TOO_LARGE: 'حجم مطالب برای یک درخواست زیاد است. درخواست را محدودتر کنید.',
          default: 'درخواست هوش مصنوعی کامل نشد.',
        },
      },
      aiActions: {
        eyebrow: 'اقدامات هوش مصنوعی',
        title: 'اقدامات هوش مصنوعی',
        description:
          'ارائه‌هایی که دستیارهای هوش مصنوعی در پروژه‌های شما آماده کرده‌اند. تا زمانی که تأیید نکنید چیزی ساخته نمی‌شود.',
        review: 'بررسی',
        reviewLabel: 'بررسی {{title}}',
        history: 'تاریخچه',
        historyLabel: 'مشاهدهٔ تاریخچهٔ {{title}}',
        approveLabel: 'تأیید و ساخت {{title}}',
        rejectLabel: 'رد کردن {{title}}',
        listLabel: 'اقدامات هوش مصنوعی',
        resultsLabel: 'نتایج این نما',
        decision: {
          approveTitle: 'این ارائه تأیید و ساخته شود؟',
          approveDescription:
            'با تأیید، کل ارائه به‌صورت پیش‌نویس در این پروژه ساخته می‌شود. چیزی منتشر یا به اشتراک گذاشته نمی‌شود. این تصمیم برای نسخهٔ نمایش‌داده‌شده در زیر اعمال می‌شود.',
          proposal: 'پیشنهاد',
          project: 'پروژه',
          slides: 'اسلایدها',
          approved: '«{{title}}» تأیید و به‌صورت پیش‌نویس ساخته شد.',
          rejectTitle: 'این پیشنهاد رد شود؟',
          rejectDescription:
            '«{{title}}» بسته می‌شود و هیچ ارائه‌ای ساخته نمی‌شود. دستیار می‌تواند پیشنهاد جدیدی آماده کند.',
          rejected: '«{{title}}» رد شد.',
        },
        shortcut: 'اقدامات هوش مصنوعی',
        empty: {
          title: 'اقدامی برای بررسی وجود ندارد',
          description:
            'وقتی یک دستیار هوش مصنوعی متصل، ارائه‌ای را در پروژه‌ای که می‌توانید بررسی کنید آماده کند، اینجا نمایش داده می‌شود.',
        },
      },
      aiProposals: {
        title: 'پیشنهادهای هوش مصنوعی',
        eyebrow: 'پیشنهادهای هوش مصنوعی',
        description:
          'ارائه‌هایی که دستیار هوش مصنوعی برای این پروژه آماده کرده است. تا تأیید شما چیزی ساخته نمی‌شود.',
        filterLabel: 'فیلتر پیشنهادها',
        filterPending: 'در انتظار بررسی',
        filterAll: 'همهٔ پیشنهادها',
        statsLabel: 'تعداد پیشنهادها بر اساس وضعیت',
        stats: {
          total: 'کل پیشنهادها',
          pending: 'در انتظار بررسی',
          built: 'ساخته‌شده',
          rejected: 'ردشده',
        },
        aiPrepared: 'تهیه‌شده با هوش مصنوعی',
        mostRecent: 'جدیدترین',
        newestFirst: 'جدیدترین ابتدا',
        viewDetails: 'مشاهدهٔ جزئیات',
        sourcesDescription: 'منابع استفاده‌شده برای آماده‌سازی این پیشنهاد.',
        retry: 'تلاش دوباره',
        back: 'بازگشت به پیشنهادها',
        pages: 'صفحه‌های پیشنهاد',
        page: 'صفحهٔ {{page}}',
        next: 'بعدی',
        previous: 'قبلی',
        slideCount_one: '{{count}} اسلاید',
        slideCount_other: '{{count}} اسلاید',
        preparedBy: 'آماده‌شده توسط {{name}}',
        empty: {
          title: 'پیشنهادی در انتظار نیست',
          description:
            'از دستیار هوش مصنوعی متصل بخواهید برای این پروژه یک ارائه آماده کند. برای بررسی شما اینجا نمایش داده می‌شود.',
        },
        status: {
          PENDING_REVIEW: 'در انتظار بررسی',
          BUILDING: 'در حال ساخت',
          BUILT: 'ساخته‌شده',
          REJECTED: 'ردشده',
          BUILD_FAILED: 'ساخت ناموفق',
        },
        review: {
          purpose: 'هدف',
          goal: 'هدف ارائه',
          audience: 'مخاطب',
          descriptionLabel: 'توضیحات',
          outline: 'طرح روایت',
          slides_one: '{{count}} اسلاید پیشنهادی',
          slides_other: '{{count}} اسلاید پیشنهادی',
          warnings: 'پیش از تأیید بررسی کنید',
          sources: 'منابع',
          basedOn: 'بر پایهٔ: {{source}}',
          imageReference: 'تصویر از کتابخانهٔ دارایی‌ها',
          assets_one: 'از {{count}} تصویر کتابخانهٔ دارایی‌ها استفاده می‌کند',
          assets_other: 'از {{count}} تصویر کتابخانهٔ دارایی‌ها استفاده می‌کند',
        },
        decision: {
          label: 'تصمیم',
          hint: 'با تأیید، کل این ارائه به‌صورت پیش‌نویس ساخته می‌شود. چیزی منتشر یا به اشتراک گذاشته نمی‌شود.',
          retryHint: 'ساخت تأییدشده کامل نشد. تلاش دوباره نسخهٔ دوم نمی‌سازد.',
          approve: 'تأیید و ساخت',
          building: 'در حال ساخت…',
          retry: 'تلاش دوباره برای ساخت',
          reject: 'رد کردن',
          reasonLabel: 'دلیل (اختیاری)',
          confirmReject: 'رد پیشنهاد',
          rejecting: 'در حال رد کردن…',
          cancel: 'انصراف',
        },
        built: {
          title: 'ارائهٔ پیش‌نویس ساخته شد',
          body: 'این پیشنهاد تأیید و به‌صورت پیش‌نویس ساخته شد. چیزی منتشر نشده است.',
          open: 'باز کردن ارائه',
        },
        rejected: {
          title: 'ردشده',
          body: 'این پیشنهاد در {{date}} رد شد. هیچ ارائه‌ای ساخته نشد.',
          reason: 'دلیل: {{reason}}',
          next: 'هر زمان آماده بودید، از دستیار هوش مصنوعی پیشنهاد جدید بخواهید.',
        },
        building: {
          title: 'در حال ساخت ارائه',
          body: 'معمولاً چند ثانیه طول می‌کشد. با پایان کار، این صفحه به‌روز می‌شود.',
        },
        failed: {
          title: 'ساخت کامل نشد',
          body: 'چیزی منتشر نشد. می‌توانید با اطمینان دوباره تلاش کنید یا از دستیار پیشنهاد جدید بخواهید.',
        },
        errors: {
          listTitle: 'پیشنهادها در دسترس نیستند',
          list: 'پیشنهادها بارگذاری نشدند.',
          loadTitle: 'پیشنهاد در دسترس نیست',
          load: 'این پیشنهاد وجود ندارد یا اجازهٔ بررسی آن را ندارید.',
          decisionTitle: 'تصمیم اعمال نشد',
          AI_PROPOSAL_STALE:
            'این پیشنهاد از زمان باز کردن تغییر کرده است. آخرین نسخه نمایش داده شده؛ دوباره بررسی کنید.',
          AI_PROPOSAL_ALREADY_DECIDED: 'پیش‌تر دربارهٔ این پیشنهاد تصمیم نهایی گرفته شده است.',
          AI_PROPOSAL_BUILD_IN_PROGRESS: 'این پیشنهاد هم‌اکنون در حال ساخت است.',
          AI_PROPOSAL_BUILD_FAILED:
            'ارائه ساخته نشد، چون چیزی که لازم بود تغییر کرده است. چیزی منتشر نشد.',
          ACCESS_DENIED: 'اجازهٔ تصمیم‌گیری دربارهٔ پیشنهادهای این پروژه را ندارید.',
          default: 'تصمیم اعمال نشد. دوباره تلاش کنید.',
        },
      },
      analytics: {
        title: 'تحلیل‌ها',
        description: 'میزان تعامل با نسخه‌های منتشرشدهٔ این ارائه، به تفکیک نسخه و لینک اشتراک.',
        filtersLabel: 'فیلترهای تحلیل',
        kpiLabel: 'شاخص‌های کلیدی',
        range: 'بازهٔ زمانی',
        ranges: { '7d': '۷ روز گذشته', '30d': '۳۰ روز گذشته', '90d': '۹۰ روز گذشته' },
        release: 'نسخه',
        allReleases: 'همهٔ نسخه‌ها',
        releaseLabel: 'نسخهٔ {{number}}',
        share: 'لینک اشتراک',
        allShares: 'همهٔ لینک‌های اشتراک',
        date: 'تاریخ',
        slide: 'اسلاید',
        reach: 'دسترسی',
        dropOff: 'ریزش',
        activeTime: 'زمان فعال',
        interactions: 'تعامل‌ها',
        media: 'رسانهٔ کامل‌شده',
        narration: 'روایت کامل‌شده',
        device: 'دستگاه',
        shareOfSessions: 'سهم از نشست‌ها',
        completionOf: '{{completed}} از {{started}}',
        units: { hour: 'س', minute: 'د', second: 'ث' },
        kpi: {
          sessions: 'نشست‌ها',
          uniqueViewers: 'بینندگان یکتا',
          uniqueViewersHint: 'مرورگرها یا کاربران واردشدهٔ متمایز؛ لزوماً افراد یکتا نیستند.',
          completionRate: 'نرخ تکمیل',
          completionRateHint: 'نشست‌هایی که به آخرین اسلاید رسیده‌اند.',
          avgActiveTime: 'میانگین زمان فعال',
          activeTimeHint: 'زمان تب پنهان یا بی‌کاری محاسبه نمی‌شود.',
          avgSlidesViewed: 'میانگین اسلایدهای دیده‌شده',
        },
        timelineTitle: 'نشست‌ها در طول زمان',
        timelineDescription: 'نشست‌های روزانه برای فیلترهای انتخاب‌شده.',
        funnelTitle: 'قیف اسلایدها',
        funnelDescription: 'نشست‌هایی که به هر اسلاید از نسخهٔ انتخاب‌شده رسیده‌اند.',
        slideTableCaption: 'تعامل به تفکیک اسلاید',
        shareBreakdownTitle: 'مقایسهٔ لینک‌های اشتراک',
        shareBreakdownDescription: 'نشست‌ها به ازای هر لینک اشتراک در بازهٔ انتخاب‌شده.',
        deviceTitle: 'دستگاه‌ها',
        deviceDescription: 'نشست‌ها به تفکیک نوع دستگاه.',
        devices: { DESKTOP: 'رومیزی', MOBILE: 'موبایل', TABLET: 'تبلت', UNKNOWN: 'نامشخص' },
        notPublishedTitle: 'این ارائه هنوز منتشر نشده است',
        notPublishedBody: 'تحلیل‌ها پس از انتشار و اشتراک‌گذاری یک نسخه آغاز می‌شود.',
        noSharesTitle: 'هنوز لینک اشتراکی وجود ندارد',
        noSharesAction: 'ساخت لینک اشتراک',
        lowSampleTitle: 'حجم نمونه کم است',
        lowSampleBody:
          'کمتر از ۲۰ نشست در این بازه. درصدها با هر بینندهٔ تازه به‌شدت تغییر می‌کنند.',
        emptyTitle: 'در این بازه نشستی ثبت نشده است',
        emptyBody:
          'بازهٔ زمانی بزرگ‌تری را امتحان کنید یا از توزیع لینک اشتراک مطمئن شوید. نمایش بازدیدهای جدید ممکن است چند دقیقه طول بکشد.',
        emptyAction: 'باز کردن پیوندهای اشتراک',
        unavailableTitle: 'تحلیل‌ها بارگذاری نشد',
        unavailableBody: 'ممکن است به این ارائه دسترسی نداشته باشید یا درخواست ناموفق بوده باشد.',
      },
      assetPicker: {
        title: 'انتخاب فایل',
        description: 'از فایل‌های پروژه، کتابخانهٔ شرکت یا فایل‌های اخیر انتخاب کنید.',
        searchLabel: 'جستجوی فایل‌ها',
        searchPlaceholder: 'جستجوی فایل‌ها…',
        filterLabel: 'فیلتر فایل‌ها',
        resultsLabel: 'فایل‌ها',
        all: 'همه',
        projectAssets: 'فایل‌های پروژه',
        companyLibrary: 'کتابخانهٔ شرکت',
        companyBadge: 'شرکت',
        sortLabel: 'مرتب‌سازی',
        sortRecent: 'آخرین به‌روزرسانی',
        sortTitle: 'نام',
        viewLabel: 'نمای فایل‌ها',
        viewGrid: 'نمای شبکه‌ای',
        viewList: 'نمای فهرستی',
        updated: 'به‌روزرسانی {{date}}',
        usedIn: 'استفاده در {{count}} اسلاید',
        noResults: 'فایلی با این فیلترها پیدا نشد.',
        insert: 'درج فایل',
        kinds: {
          IMAGE: 'تصویر',
          VIDEO: 'ویدیو',
          AUDIO: 'صدا',
          LOGO: 'لوگو',
          ICON: 'آیکون',
          DOCUMENT: 'سند',
        },
      },
      templatePicker: {
        addTitle: 'افزودن اسلاید',
        switchTitle: 'تغییر قالب',
        description: 'قالب‌های ساختاریافته را مرور کنید تا سریع‌تر شروع کنید و یکدست بمانید.',
        searchLabel: 'جستجوی قالب‌ها',
        searchPlaceholder: 'جستجوی قالب‌ها…',
        categoryLabel: 'فیلتر بر اساس دسته',
        allCategories: 'همه',
        resultsLabel: 'قالب‌ها',
        noResults: 'قالبی با این جستجو پیدا نشد.',
        selectPrompt: 'یک قالب را انتخاب کنید تا پیش‌نمایشش را ببینید.',
        previewUnavailable: 'پیش‌نمایش این قالب ممکن نیست.',
        addAction: 'افزودن اسلاید',
        addingAction: 'در حال افزودن اسلاید…',
        switchAction: 'استفاده از قالب',
        switchingAction: 'در حال تغییر قالب…',
        cuesErrorTitle: 'نشانه‌های ارائه با قالب سازگار نیستند',
        cuesErrorDescription:
          'این قالب از نشانه‌های ارائهٔ اسلاید پشتیبانی نمی‌کند. نشانه‌ها را ویرایش کنید یا قالب سازگار دیگری برگزینید.',
        affectedCues: 'نشانه‌های ناسازگار: {{cues}}',
        motionErrorTitle: 'حرکت با قالب سازگار نیست',
        motionErrorDescription:
          'این قالب برخی عناصری را که حرکت اسلاید روی آن‌ها اجرا می‌شود تعریف نکرده است. چیزی تغییر نکرد. می‌توانید حرکت اسلاید را حذف و قالب را عوض کنید یا قالب سازگار دیگری برگزینید.',
        removeMotionAndSwitch: 'حذف حرکت و تغییر قالب',
        conflictErrorTitle: 'اسلاید تغییر کرده است',
        conflictErrorDescription: 'اسلاید را دوباره بارگیری کنید و تغییر قالب را تکرار کنید.',
        otherErrorTitle: 'تغییر قالب انجام نشد',
        otherErrorDescription: 'قالب اعمال نشد. دوباره تلاش کنید.',
      },
      viewer: {
        movie: viewerPlaybackCopy.fa,
        loadingTitle: 'در حال بارگیری ارائه',
        slides: 'اسلایدها',
        slideCount: '{{count}} اسلاید',
        slideCount_other: '{{count}} اسلاید',
        slideLabel: 'اسلاید {{number}}: {{title}}',
        slideAnnouncement: 'اسلاید {{number}} از {{total}}: {{title}}',
        position: '{{position}} / {{total}}',
        openSlideList: 'باز کردن فهرست اسلایدها',
        closeSlideList: 'بستن فهرست اسلایدها',
        previousSlide: 'اسلاید قبلی',
        nextSlide: 'اسلاید بعدی',
        enterFullscreen: 'تمام‌صفحه',
        exitFullscreen: 'خروج از تمام‌صفحه',
        playNarration: 'پخش روایت',
        pauseNarration: 'توقف روایت',
        mute: 'بی‌صدا کردن روایت',
        unmute: 'باصدا کردن روایت',
      },
      viewerGate: {
        signIn: 'ورود',
        viewPresentation: 'مشاهدهٔ ارائه',
        passwordLabel: 'گذرواژه',
        showPassword: 'نمایش گذرواژه',
        hidePassword: 'پنهان کردن گذرواژه',
        passwordIncorrect: 'گذرواژه نادرست است.',
        passwordRateLimited: 'تلاش‌های بیش از حد. چند دقیقه دیگر دوباره تلاش کنید.',
        PASSWORD_REQUIRED: {
          title: 'این ارائه با گذرواژه محافظت می‌شود',
          description: 'برای ادامه گذرواژه را وارد کنید.',
        },
        AUTHENTICATION_REQUIRED: {
          title: 'برای مشاهدهٔ این ارائه وارد شوید',
          description: 'این پیوند با حساب‌های مشخصی در Slider به اشتراک گذاشته شده است.',
        },
        COMPANY_REQUIRED: {
          title: 'این ارائه فقط برای اعضای شرکت مالک در دسترس است',
          description: 'با حسابی که دسترسی دارد وارد شوید یا از فرستنده پیوند دیگری بخواهید.',
        },
        RECIPIENT_REQUIRED: {
          title: 'این ارائه با حساب شما به اشتراک گذاشته نشده است',
          description:
            'از فرستنده بخواهید آن را با نشانی‌ای که با آن وارد شده‌اید به اشتراک بگذارد.',
        },
        UNAVAILABLE: {
          title: 'این ارائه دیگر در دسترس نیست',
          description: 'ممکن است پیوند منقضی یا باطل شده باشد.',
        },
        NOT_PUBLISHED: {
          title: 'این ارائه هنوز در دسترس نیست',
          description: 'این ارائه هنوز منتشر نشده است.',
        },
      },
      notFound: {
        title: 'صفحه پیدا نشد',
        description: 'صفحه درخواستی وجود ندارد.',
        action: 'بازگشت به خانه',
      },
    },
  },
} as const;

await i18n.use(initReactI18next).init({
  fallbackLng: 'en',
  lng: getInitialLanguage(),
  interpolation: { escapeValue: false },
  resources,
});

const applyDocumentLanguage = (language: string) => {
  const isRtl = language.startsWith('fa');
  document.documentElement.lang = isRtl ? 'fa' : 'en';
  document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
  localStorage.setItem(LOCALE_STORAGE_KEY, isRtl ? 'fa' : 'en');
};

applyDocumentLanguage(i18n.resolvedLanguage ?? i18n.language);
i18n.on('languageChanged', applyDocumentLanguage);
